import ExcelJS from "exceljs";
import {
  TradeConfirmationRow,
  StockData,
  CustomerData,
  BondResultRow,
  StockResultRow,
  PurchaseHistoryRow,
  SoldStockRow,
  ChargeRow,
  BondDistributionFile,
  BondDistributionRow,
  CashBalanceRow,
  BalanceHistoryRow,
  RefundHistoryEntry,
} from "./types";
import {
  ceilTo2Decimals,
  floorTo2Decimals,
  safeNumber,
  DEFAULT_COLUMNS,
  DEFAULT_BOND_METADATA,
  normalizeBondName,
  normalizeEmailKey,
  sanitizeEmail,
} from "./utils";

// Helper function to strip time from date string, keeping only the date portion
const stripTimeFromDate = (dateStr: string): string => {
  if (!dateStr) return "";
  // If it contains a space (date time separator), take only the date part
  if (dateStr.includes(" ")) {
    return dateStr.split(" ")[0];
  }
  return dateStr;
};

const normalizeCompactDate = (text: string): string => {
  const compactText = String(text || "").trim().replace(/\.0+$/, "");
  if (!/^\d{6,8}$/.test(compactText)) return "";

  const year = compactText.slice(0, 4);
  const rest = compactText.slice(4);
  const splitOptions =
    rest.length === 4 ? [[2, 2]] :
    rest.length === 3 ? [[2, 1], [1, 2]] :
    rest.length === 2 ? [[1, 1]] :
    [];

  for (const [monthLen, dayLen] of splitOptions) {
    const month = Number(rest.slice(0, monthLen));
    const day = Number(rest.slice(monthLen, monthLen + dayLen));
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }

  return "";
};

// Normalize date text to YYYY-MM-DD key for cross-sheet linking.
const normalizeDateKey = (dateStr: string): string => {
  if (!dateStr) return "";
  const text = stripTimeFromDate(String(dateStr).trim()).replace(/\.0+$/, "");
  const compactYmd = normalizeCompactDate(text);
  if (compactYmd) {
    return compactYmd;
  }
  const ymd = text.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
  if (ymd) {
    return `${ymd[1]}-${String(Number(ymd[2])).padStart(2, "0")}-${String(Number(ymd[3])).padStart(2, "0")}`;
  }
  const mdy = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (mdy) {
    return `${mdy[3]}-${String(Number(mdy[1])).padStart(2, "0")}-${String(Number(mdy[2])).padStart(2, "0")}`;
  }
  return text;
};

const formatNarrativeDisplayDate = (dateStr: string): string => {
  const key = normalizeDateKey(dateStr);
  const m = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(dateStr || "").trim().replace(/\.0+$/, "");
  return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
};

// Download combined TC as Excel with Mongolian + English headers
export const downloadCombinedTC = async (combinedTC: TradeConfirmationRow[]) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Combined TC");

  const columns = [
    { mn: "Зааварчилгааны дугаар", en: "Sender Reference No", key: "refNo", width: 20 },
    { mn: "Огноо", en: "Trade Date", key: "tradeDate", width: 12 },
    { mn: "Төлбөр хийгдэх өдөр", en: "Settlement Date", key: "settlementDate", width: 15 },
    { mn: "Үнэт цаасны нэр", en: "Security name", key: "name", width: 45 },
    { mn: "Үнэт цаасны симбол", en: "Security symbol", key: "symbol", width: 15 },
    { mn: "Гүйлгээний төрөл", en: "Instruction type", key: "instructionType", width: 15 },
    { mn: "Бүртгэх төрөл", en: "Portfolio class", key: "portfolioClass", width: 15 },
    { mn: "Тоо ширхэг", en: "Quantity", key: "quantity", width: 12 },
    { mn: "Нэгжийн үнэ", en: "Unit price", key: "unitPrice", width: 12 },
    { mn: "Арилжааны дүн", en: "Sec amount total", key: "secAmountTotal", width: 15 },
    { mn: "Шимтгэл", en: "Fee", key: "fee", width: 10 },
    { mn: "Шимтгэлийн дүн", en: "Fee amount", key: "feeAmount", width: 12 },
    { mn: "Нийт дүн", en: "Total Amount", key: "totalAmount", width: 15 },
    { mn: "Валют", en: "Currency", key: "currency", width: 10 },
  ];

  worksheet.columns = columns.map((col) => ({
    key: col.key,
    width: col.width,
  }));

  const mnRow = worksheet.addRow(columns.map((col) => col.mn));
  mnRow.font = { bold: true };
  mnRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };

  const enRow = worksheet.addRow(columns.map((col) => col.en));
  enRow.font = { bold: true };
  enRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD0D0D0" },
  };

  combinedTC.forEach((row) => {
    worksheet.addRow([
      row.refNo,
      row.tradeDate,
      row.settlementDate,
      row.name,
      row.symbol,
      row.instructionType,
      row.portfolioClass,
      row.quantity,
      row.unitPrice,
      row.secAmountTotal,
      row.fee,
      row.feeAmount,
      row.totalAmount,
      row.currency,
    ]);
  });

  const totalQuantity = combinedTC.reduce((sum, r) => sum + r.quantity, 0);
  const totalSecAmount = ceilTo2Decimals(combinedTC.reduce((sum, r) => sum + r.secAmountTotal, 0));
  const totalFeeAmount = ceilTo2Decimals(combinedTC.reduce((sum, r) => sum + r.feeAmount, 0));
  const totalTotalAmount = ceilTo2Decimals(combinedTC.reduce((sum, r) => sum + r.totalAmount, 0));

  const totalRow = worksheet.addRow([
    "Нийт",
    "",
    "",
    "",
    "",
    "",
    "",
    totalQuantity,
    "",
    totalSecAmount,
    "",
    totalFeeAmount,
    totalTotalAmount,
    "USD",
  ]);
  totalRow.font = { bold: true };

  const blob = new Blob([await workbook.xlsx.writeBuffer()], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Combined_TC.xlsx";
  a.click();
  window.URL.revokeObjectURL(url);
};

// Download processed offer as Excel
export const downloadProcessedOffer = async (offer: { fileName: string; data: StockData[] }) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Trade Data");

  worksheet.columns = [
    { header: "AccountAlias", key: "accountAlias", width: 12 },
    { header: "ListingExchange", key: "listingExchange", width: 15 },
    { header: "CurrencyPrimary", key: "currencyPrimary", width: 15 },
    { header: "AssetClass", key: "assetClass", width: 12 },
    { header: "Symbol", key: "symbol", width: 10 },
    { header: "Description", key: "description", width: 40 },
    { header: "OrderType", key: "orderType", width: 12 },
    { header: "Buy/Sell", key: "buySell", width: 10 },
    { header: "Quantity", key: "quantity", width: 15 },
    { header: "Price", key: "price", width: 12 },
    { header: "Proceeds", key: "proceeds", width: 15 },
    { header: "Commission", key: "commission", width: 15 },
    { header: "CommissionCurrency", key: "commissionCurrency", width: 18 },
    { header: "TradeDate", key: "tradeDate", width: 12 },
    { header: "TradeID", key: "tradeId", width: 15 },
  ];

  offer.data.forEach((row) => {
    worksheet.addRow({
      accountAlias: DEFAULT_COLUMNS.AccountAlias,
      listingExchange: DEFAULT_COLUMNS.ListingExchange,
      currencyPrimary: DEFAULT_COLUMNS.CurrencyPrimary,
      assetClass: DEFAULT_COLUMNS.AssetClass,
      symbol: row.symbol,
      description: row.name,
      orderType: DEFAULT_COLUMNS.OrderType,
      buySell: DEFAULT_COLUMNS["Buy/Sell"],
      quantity: row.quantity === 0 ? 0 : (typeof row.quantity === "number" ? -row.quantity : row.quantity),
      price: row.price,
      proceeds: row.proceeds,
      commission: row.commission,
      commissionCurrency: DEFAULT_COLUMNS.CommissionCurrency,
      tradeDate: DEFAULT_COLUMNS.TradeDate,
      tradeId: DEFAULT_COLUMNS.TradeID,
    });
  });

  const blob = new Blob([await workbook.xlsx.writeBuffer()], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Output_${offer.fileName.replace(/\.[^/.]+$/, "")}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
};

// Download accumulated purchase data
export const downloadAccumulatedPurchaseData = async (data: PurchaseHistoryRow[]) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Захиалгын хүсэлт");

  worksheet.columns = [
    { header: "ХА огноо", key: "haDate", width: 12 },
    { header: "Овог", key: "lastName", width: 15 },
    { header: "Нэр", key: "firstName", width: 15 },
    { header: "И-мейл", key: "email", width: 25 },
    { header: "Гүйлгээний код", key: "txCode", width: 15 },
    { header: "Мөнгөн дүн", key: "amount", width: 15 },
    { header: "Багц №", key: "portfolioIndex", width: 10 },
    { header: "Bond", key: "bond", width: 12 },
    { header: "Худалдан авалтаас үлдсэн дүн", key: "holdAmount", width: 15 },
  ];

  data.forEach((row) => worksheet.addRow(row));

  const blob = new Blob([await workbook.xlsx.writeBuffer()], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Accumulated_Purchase_History.xlsx";
  a.click();
  window.URL.revokeObjectURL(url);
};

// Download accumulated sold stock
export const downloadAccumulatedSoldStock = async (sheets: { name: string; data: SoldStockRow[] }[]) => {
  const workbook = new ExcelJS.Workbook();

  sheets.forEach(({ name, data }) => {
    const worksheet = workbook.addWorksheet(name);
    worksheet.columns = [
      { header: "Email", key: "email", width: 25 },
      { header: "Утас", key: "phone", width: 12 },
      { header: "Овог", key: "lastName", width: 15 },
      { header: "Нэр", key: "firstName", width: 15 },
      { header: "Регистр", key: "regNumber", width: 12 },
      { header: "ТТД", key: "ttd", width: 12 },
      { header: "Хөрөнгийн төрөл", key: "assetType", width: 15 },
      { header: "Хөрөнгийн нэр", key: "assetName", width: 25 },
      { header: "Тоо ширхэг", key: "quantity", width: 12 },
      { header: "Ханш", key: "price", width: 12 },
      { header: "USD-MNT", key: "usdMnt", width: 12 },
      { header: "Нийт үнэ", key: "totalPrice", width: 15 },
      { header: "ҮЦК шимтгэл", key: "uckFee", width: 12 },
      { header: "AvtaMata", key: "avtamataFee", width: 12 },
      { header: "Шилжүүлэх дүн", key: "transferAmount", width: 15 },
      { header: "Огноо", key: "date", width: 12 },
    ];
    data.forEach((row) => worksheet.addRow(row));
  });

  const blob = new Blob([await workbook.xlsx.writeBuffer()], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Accumulated_Sold_Stock.xlsx";
  a.click();
  window.URL.revokeObjectURL(url);
};

// Download accumulated charge
export const downloadAccumulatedCharge = async (sheets: { name: string; data: ChargeRow[] }[]) => {
  const workbook = new ExcelJS.Workbook();

  sheets.forEach(({ name, data }) => {
    const worksheet = workbook.addWorksheet(name);
    worksheet.columns = [
      { header: "№", key: "index", width: 8 },
      { header: "Овог", key: "lastName", width: 15 },
      { header: "Нэр", key: "firstName", width: 15 },
      { header: "Регистр", key: "regNumber", width: 12 },
      { header: "Утас", key: "phone", width: 12 },
      { header: "И-мейл", key: "email", width: 25 },
      { header: "Мөнгөн дүн", key: "amount", width: 15 },
      { header: "Тайлбар", key: "description", width: 20 },
      { header: "ХА огноо", key: "haDate", width: 12 },
    ];
    data.forEach((row) => worksheet.addRow(row));
  });

  const blob = new Blob([await workbook.xlsx.writeBuffer()], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Accumulated_Charge.xlsx";
  a.click();
  window.URL.revokeObjectURL(url);
};

// Purchase breakdown row for Sheet 4
export interface PurchaseBreakdownRow {
  email: string;
  phone: string;
  lastName: string;
  firstName: string;
  regNumber: string;
  bankAccount: string;
  bank: string;
  txCode: string;
  ttd: string;
  assetType: string;
  assetName: string;
  quantity: number;
  totalPrice: number;
  purchaseDate: string;
}


// Thin border style for cells
const thinBorder: Partial<ExcelJS.Borders> = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

// Helper function to style header row
const styleHeaderRow = (row: ExcelJS.Row) => {
  row.font = { bold: true };
  row.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" }, // Gray background (#d9d9d9)
  };
  row.alignment = { horizontal: "center", vertical: "middle" };
  row.eachCell((cell) => {
    cell.border = thinBorder;
  });
};

// Helper function to add border to data row
const addBorderToRow = (row: ExcelJS.Row) => {
  row.eachCell((cell) => {
    cell.border = thinBorder;
  });
};

// Helper function to set column widths
  const setColumnWidths = (sheet: ExcelJS.Worksheet, widths: number[]) => {
  widths.forEach((width, idx) => {
    sheet.getColumn(idx + 1).width = width;
  });
};

const formatDisplayDate = (dateStr: string): string => {
  if (!dateStr) return "";
  const parts = dateStr.includes("/") ? dateStr.split("/") : dateStr.split("-");
  if (parts.length < 3) return dateStr;

  // Support YYYY-MM-DD and MM/DD/YYYY style inputs.
  if (parts[0].length === 4) {
    const year = parts[0];
    const month = String(parseInt(parts[1], 10)).padStart(2, "0");
    const day = String(parseInt(parts[2], 10)).padStart(2, "0");
    return `${month}/${day}/${year}`;
  }

  const month = String(parseInt(parts[0], 10)).padStart(2, "0");
  const day = String(parseInt(parts[1], 10)).padStart(2, "0");
  const year = parts[2];
  return `${month}/${day}/${year}`;
};

const resolveBankCode = (bankName: string): string => {
  const name = (bankName || "").toLowerCase();
  if (name.includes("хаан")) return "0005";
  if (name.includes("голомт")) return "0015";
  if (name.includes("төрийн")) return "0034";
  if (name.includes("хас")) return "0032";
  if (name.includes("худалдаа")) return "0004";
  return "";
};

const normalizeDateTimeKey = (dateStr: string): string => {
  if (!dateStr) return "";
  const text = String(dateStr).trim();
  const isoLike = text.match(
    /^(\d{4})[./-](\d{1,2})[./-](\d{1,2})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (isoLike) {
    return `${isoLike[1]}-${String(Number(isoLike[2])).padStart(2, "0")}-${String(Number(isoLike[3])).padStart(2, "0")} ${String(Number(isoLike[4] || "0")).padStart(2, "0")}:${String(Number(isoLike[5] || "0")).padStart(2, "0")}:${String(Number(isoLike[6] || "0")).padStart(2, "0")}`;
  }
  const mdyLike = text.match(
    /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:[T\s](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (mdyLike) {
    return `${mdyLike[3]}-${String(Number(mdyLike[1])).padStart(2, "0")}-${String(Number(mdyLike[2])).padStart(2, "0")} ${String(Number(mdyLike[4] || "0")).padStart(2, "0")}:${String(Number(mdyLike[5] || "0")).padStart(2, "0")}:${String(Number(mdyLike[6] || "0")).padStart(2, "0")}`;
  }
  return text.replace("T", " ").replace(/\.\d+Z?$/, "");
};

const calculateIbanChecksum = (bankCode: string, accountPart: string): string => {
  const numeric = `${bankCode}${accountPart}222300`; // "MN00" => 222300
  let mod = 0;
  for (const ch of numeric) {
    mod = (mod * 10 + Number(ch)) % 97;
  }
  return String(98 - mod).padStart(2, "0");
};

const buildExpectedIban = (accountNo: string, bankName: string): string => {
  if (!accountNo) return "";
  const cleaned = accountNo.replace(/\s+/g, "");
  if (cleaned.toUpperCase().startsWith("MN")) return cleaned;

  const accountDigits = cleaned.replace(/\D/g, "").padStart(12, "0").slice(-12);
  const bankCode = resolveBankCode(bankName);
  if (!bankCode || accountDigits.length !== 12) return cleaned;

  const checksum = calculateIbanChecksum(bankCode, accountDigits);
  return `${checksum}${bankCode}${accountDigits}`;
};

const getTransferFee = (bankName: string, totalAmount: number): number => {
  if ((bankName || "").toLowerCase().includes("голомт")) return 100;
  return totalAmount >= 5_000_000 ? 400 : 200;
};

// Extended bond row with tailbar for Part 3
export interface BondRetakeRowWithTailbar extends BondResultRow {
  tailbar?: string; // Generated explanation from Part 3
}

// Generate 8-sheet Excel file
export const generate8SheetExcel = async (
  refundDate: string,
  customerMap: Record<string, CustomerData>,
  bondRows: BondResultRow[],
  stockRows: StockResultRow[],
  balanceHistoryRows: BalanceHistoryRow[], // Sheet 4: Balance History with ratio
  accumulatedSoldStockSheets: { name: string; data: SoldStockRow[] }[],
  cashBalanceRows: CashBalanceRow[],
  bondRetakeRows: BondResultRow[], // Sheet 6: filtered by ratio
  combinedTC: TradeConfirmationRow[] = [],
  bankStatementData: { date: string; income: number; txCode: string; account: string }[] = [], // Bank statement for Голомт огноо lookup
  revisedBondRetakeRows?: BondRetakeRowWithTailbar[], // Optional: revised rows with tailbar from Part 3
  bondMetaMap?: Record<string, { rate: number; paymentDates: string[]; maturityDate?: string }>, // Bond metadata with payment dates for P column calculation
  bankInfoMap?: Record<string, { portfolioIndex: number; cashTotal: number }>, // Bank info map for Sheet 8
  accumulatedChargeSheets: { name: string; data: ChargeRow[] }[] = [], // Sheet 9: Charge/deposit data
  maturedBondRows: BondResultRow[] = [], // Sheet 4: Matured bonds (days=0)
  purchaseHistoryData: PurchaseHistoryRow[] = [], // Purchase History for Тайлбар date matching
  refundHistoryMap: Record<string, RefundHistoryEntry[]> = {},
  bondDistributionFiles: BondDistributionFile[] = [],
) => {
  console.log("generate8SheetExcel called with:", {
    refundDate,
    customersCount: Object.keys(customerMap).length,
    bondRowsCount: bondRows.length,
    stockRowsCount: stockRows.length,
    balanceHistoryCount: balanceHistoryRows.length,
    cashBalanceCount: cashBalanceRows.length,
    bondRetakeCount: bondRetakeRows.length
  });

  try {
    const workbook = new ExcelJS.Workbook();
    const displayRefundDate = formatDisplayDate(refundDate);
    const carryForwardByEmailDate: Record<string, number> = {};
    const carryForwardCommentByEmailDate: Record<string, string> = {};
    const carryForwardSourceLabelByEmailDate: Record<string, string> = {};
    const carryForwardSourceAmountByEmailDate: Record<string, number> = {};
    const soldCarryByEmailDate: Record<string, number> = {};

  // Build matured bond totals per customer
  const maturedBondTotals: Record<string, number> = {};
  maturedBondRows.forEach(b => {
    const email = b.email.toLowerCase().trim();
    maturedBondTotals[email] = (maturedBondTotals[email] || 0) + safeNumber(b.transferAmount);
  });

  // Sheet 1: Summary (Нийт)
  const sheet1 = workbook.addWorksheet("1.Нийт");
  // Set column widths for Sheet 1 (15 columns including Код)
  setColumnWidths(sheet1, [5, 15, 15, 12, 28, 15, 15, 22, 15, 15, 15, 18, 22, 20, 18]);

  // Title in E1 cell
  sheet1.addRow(["", "", "", "", `НИЙТ БУЦААЛТ ${displayRefundDate}`, "", "", "", "", "", "", "", "", "", ""]);
  const headerRow1 = sheet1.addRow(["№", "Овог", "Нэр", "Регистр", "Э-мэйл", "Бонд", "Хувьцаа", "Хугацаа дууссан бонд", "Бэлэн мөнгө", "Нийт", "Нийт шилжүүлэх", "Данс", "IBAN", "Хүлээн авах банк", "Код"]);
  styleHeaderRow(headerRow1);

  let idx = 1;
  let totalBond = 0;
  let totalStock = 0;
  let totalMaturedBond = 0;
  let totalCash = 0;
  let totalTransfer = 0;

  Object.values(customerMap).forEach(c => {
    const maturedAmount = maturedBondTotals[c.email.toLowerCase().trim()] || 0;
    const total = safeNumber(c.bondTotal) + safeNumber(c.stockTotal) + maturedAmount + safeNumber(c.cashTotal);
    const transferFee = getTransferFee(c.bankName || "", total);
    const transferAmount = Math.max(0, total - transferFee);
    const iban = buildExpectedIban(c.accountNo || "", c.bankName || "");
    const dataRow = sheet1.addRow([
      idx++, c.lastName || "", c.firstName || "", c.regNumber || "", c.email || "",
      ceilTo2Decimals(c.bondTotal), ceilTo2Decimals(c.stockTotal), ceilTo2Decimals(maturedAmount), ceilTo2Decimals(c.cashTotal), ceilTo2Decimals(total), ceilTo2Decimals(transferAmount),
      c.accountNo || "", iban, c.bankName || "", c.depositCode || ""
    ]);
    addBorderToRow(dataRow);

    // Accumulate totals
    totalBond += safeNumber(c.bondTotal);
    totalStock += safeNumber(c.stockTotal);
    totalMaturedBond += maturedAmount;
    totalCash += safeNumber(c.cashTotal);
    totalTransfer += transferAmount;
  });

  // Add empty row
  sheet1.addRow([]);

  // Add totals row
  const grandTotal = totalBond + totalStock + totalMaturedBond + totalCash;
  sheet1.addRow([
    "Нийт", "", "", "", "",
    ceilTo2Decimals(totalBond), ceilTo2Decimals(totalStock), ceilTo2Decimals(totalMaturedBond), ceilTo2Decimals(totalCash), ceilTo2Decimals(grandTotal), ceilTo2Decimals(totalTransfer),
    "", "", "", ""
  ]);

  // Add four empty rows
  sheet1.addRow([]);
  sheet1.addRow([]);
  sheet1.addRow([]);
  sheet1.addRow([]);

  // Signature rows (expected placement)
  sheet1.addRow(["", "", "", "", "", "Тооцоолол бэлтгэсэн", "", "", "", "", "", "", "Э.Соронзонболд", "", ""]);
  sheet1.addRow(["", "", "", "", "", "Тооцоолол хянасан", "", "", "", "", "", "", "Б. Энхтүвшин", "", ""]);
  sheet1.addRow(["", "", "", "", "", "Тооцоолол хянасан", "", "", "", "", "", "", "Ж. Сувд", "", ""]);

  // Sheet 2: Bonds (Бонд)
  const sheet2 = workbook.addWorksheet("2.Бонд");
  // Set column widths for Bond sheet (22 columns)
  setColumnWidths(sheet2, [28, 12, 15, 15, 14, 18, 18, 15, 12, 20, 15, 10, 14, 14, 10, 15, 10, 15, 15, 15, 10, 15]);

  // Title row: ХАВСРАЛТ 1 (A1), БОНД БУЦААЛТ {date} (C1)
  const titleRow2 = sheet2.addRow(["ХАВСРАЛТ 1", "", `БОНД БУЦААЛТ ${displayRefundDate}`]);
  titleRow2.font = { bold: true, size: 12 };

  // Header row with column names
  const headerRow2 = sheet2.addRow([
    "Email", "Утасны дугаар", "Овог", "Нэр", "Регистрийн дугаар", "Данс", "Хүлээн авах банк",
    "Гүйлгээний код", "ТТД", "Хөрөнгийн нэр", "Мөнгөн дүн", "Бонд хүү",
    "Худалдан авсан огноо", "Хүү тооцоолсон огноо", "Хүү тооцох хоног", "Тооцсон хүү",
    "100%", "Нийт үнэ", "AvtaMata шимтгэл", "Арилжааны шимтгэл", "0.10", "Шилжүүлэх дүн"
  ]);
  styleHeaderRow(headerRow2);

  const sheet2RowsBySourceKey: Record<string, number[]> = {};
  const sheet2RowsBySourceRowKey: Record<string, number[]> = {};
  bondRows.forEach(b => {
    const calculatedInterest = safeNumber(b.calculatedInterest);
    const totalPrice = safeNumber(b.amount) + calculatedInterest;
    // Bond rate as percentage (e.g., 0.2 → "20.00%")
    const bondRatePercent = `${(safeNumber(b.bondRate) * 100).toFixed(2)}%`;
    const bondDataRow = sheet2.addRow([
      b.email || "", b.phone || "", b.lastName || "", b.firstName || "", b.regNumber || "",
      b.bankAccount || "", b.bank || "", b.txCode || "", b.ttd || "", b.name || "",
      ceilTo2Decimals(b.amount), bondRatePercent,
      stripTimeFromDate(b.purchaseDate || ""),
      stripTimeFromDate(b.interestEndDate || ""),
      safeNumber(b.days), ceilTo2Decimals(calculatedInterest),
      ceilTo2Decimals(calculatedInterest), ceilTo2Decimals(totalPrice),
      ceilTo2Decimals(b.avtamataFee), 0.00, ceilTo2Decimals(b.oat10), ceilTo2Decimals(b.transferAmount)
    ]);
    const sourceDateKey = normalizeDateKey((b.sourcePurchaseDate || b.purchaseDate || "").toString());
    const sourceKey = `${(b.email || "").toLowerCase().trim()}|${sourceDateKey}`;
    if (!sheet2RowsBySourceKey[sourceKey]) sheet2RowsBySourceKey[sourceKey] = [];
    sheet2RowsBySourceKey[sourceKey].push(bondDataRow.number);
    const sourceDateObj = parseFlexibleDate((b.sourcePurchaseDate || b.purchaseDate || "").toString());
    if (sourceDateObj) {
      const sourceRowKey = `${(b.email || "").toLowerCase().trim()}|${sourceDateObj.getFullYear()}-${String(
        sourceDateObj.getMonth() + 1,
      ).padStart(2, "0")}-${String(sourceDateObj.getDate()).padStart(2, "0")}|${normalizeBondName(
        (b.name || "").replace("Нео бонд ", "Нео "),
      )}`;
      if (!sheet2RowsBySourceRowKey[sourceRowKey]) sheet2RowsBySourceRowKey[sourceRowKey] = [];
      sheet2RowsBySourceRowKey[sourceRowKey].push(bondDataRow.number);
    }
    addBorderToRow(bondDataRow);
  });

  // Sheet 3: Stocks (Хувьцаа)
  const sheet3 = workbook.addWorksheet("3.Хувьцаа");
  // Set column widths for Stock sheet (18 columns)
  setColumnWidths(sheet3, [28, 14, 18, 35, 12, 15, 12, 12, 12, 15, 10, 14, 14, 10, 15, 18, 15, 15]);

  // Title row: ХАВСРАЛТ 2 (A1), ХУВЬЦАА БУЦААЛТ {date} (C1)
  const titleRow3 = sheet3.addRow(["ХАВСРАЛТ 2", "", `ХУВЬЦАА БУЦААЛТ ${displayRefundDate}`]);
  titleRow3.font = { bold: true, size: 12 };

  // Add empty row
  sheet3.addRow([]);

  // Add Combined TC section at the top if available
  if (combinedTC.length > 0) {
    // Mongolian header row for TC
    const tcHeaderMN = sheet3.addRow([
      "Зааварчилгааны дугаар", "Огноо", "Төлбөр хийгдэх өдөр", "Үнэт цаасны нэр", "Үнэт цаасны симбол",
      "Гүйлгээний төрөл", "Бүртгэх төрөл", "Тоо ширхэг", "Нэгжийн Үнэ", "Арилжааны дүн",
      "Шимтгэл", "Шимтгэлийн дүн", "Нийт дүн", "Валют"
    ]);
    styleHeaderRow(tcHeaderMN);

    // English header row for TC
    const tcHeaderEN = sheet3.addRow([
      "Sender Reference No", "Trade Date", "Settlement Date", "Security name", "Security symbol",
      "Instruction type", "Potfolio class", "Quantity", "Unit price", "Sec amount total",
      "Fee", "Fee amount", "Total Amount", "Сurrency"
    ]);
    styleHeaderRow(tcHeaderEN);

    combinedTC.forEach(tc => {
      const tcDataRow = sheet3.addRow([
        tc.refNo,
        tc.tradeDate || "",
        tc.settlementDate || "",
        tc.name || "",
        tc.symbol || "",
        tc.instructionType || "",
        tc.portfolioClass || "",
        safeNumber(tc.quantity),
        safeNumber(tc.unitPrice),
        floorTo2Decimals(tc.secAmountTotal),
        tc.fee ? `${(Number(tc.fee) * 100).toFixed(2)}%` : "",
        floorTo2Decimals(tc.feeAmount),
        floorTo2Decimals(tc.totalAmount),
        tc.currency || "USD"
      ]);
      addBorderToRow(tcDataRow);
    });

    // Add totals row for Combined TC
    const totalQuantity = combinedTC.reduce((sum, r) => sum + r.quantity, 0);
    const totalSecAmount = floorTo2Decimals(combinedTC.reduce((sum, r) => sum + floorTo2Decimals(r.secAmountTotal), 0));
    const totalFeeAmount = floorTo2Decimals(combinedTC.reduce((sum, r) => sum + floorTo2Decimals(r.feeAmount), 0));
    const totalTotalAmount = floorTo2Decimals(combinedTC.reduce((sum, r) => sum + floorTo2Decimals(r.totalAmount), 0));

    const tcTotalRow = sheet3.addRow([
      "Нийт", "", "", "", "", "", "",
      totalQuantity, "", totalSecAmount, "", totalFeeAmount, totalTotalAmount, "USD"
    ]);
    tcTotalRow.font = { bold: true };

    // USD-MNT note rows matching expected template.
    const usdRate = stockRows.length > 0 ? safeNumber(stockRows[0].usdRate) : 0;
    const usdRateText = usdRate > 0 ? usdRate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
    const tcSettlementDate = combinedTC[0]?.settlementDate ? formatDisplayDate(combinedTC[0].settlementDate) : "";
    const tcTradeDate = combinedTC[0]?.tradeDate ? formatDisplayDate(combinedTC[0].tradeDate) : "";
    sheet3.addRow([]);
    sheet3.addRow(["", "", "", "USD-MNT ханш", usdRateText, tcSettlementDate ? `${tcSettlementDate} бэлэн бус авах ханш` : "", "", "", "", "", "", "", "", "", "", "", "", ""]);
    sheet3.addRow(["", "", "", "USD-MNT ханш", usdRateText, tcTradeDate ? `${tcTradeDate} бэлэн бус авах ханш` : "", "", "XLE хувьцааг Эс Жи Капитал ҮЦК-ын төлбөрийн даалгаврын ханшаар биелүүлэв.", "", "", "", "", "", "", "", "", "", ""]);

    // Add empty rows as separator
    sheet3.addRow([]);
    sheet3.addRow([]);
  }

  // Stock refund section header
  const stockHeaderRow = sheet3.addRow([
    "Email", "Утасны дугаар", "Овог", "Нэр", "Регистрийн дугаар", "Данс", "Хүлээн авах банк",
    "Гүйлгээний код", "ТТД", "Худалдан авсан огноо", "Хөрөнгийн нэр", "Тоо ширхэг", "Ханш",
    "USD-MNT ханш", "Нийт үнэ", "ҮЦК шимтгэл (0.8%)", "AvtaMata шимтгэл", "Шилжүүлэх дүн"
  ]);
  styleHeaderRow(stockHeaderRow);

  stockRows.forEach(s => {
    const stockDataRow = sheet3.addRow([
      s.email || "", s.phone || "", s.lastName || "", s.firstName || "", s.regNumber || "",
      s.bankAccount || "", s.bank || "", s.txCode || "", s.ttd || "",
      stripTimeFromDate(s.purchaseDate || ""),
      s.name || "", safeNumber(s.quantity), safeNumber(s.price), safeNumber(s.usdRate),
      floorTo2Decimals(s.totalMNT), floorTo2Decimals(s.uckFee), floorTo2Decimals(s.avtamataFee), floorTo2Decimals(s.transferAmount)
    ]);
    addBorderToRow(stockDataRow);
  });

  // Sheet 4: Matured Bonds (ХУГАЦАА ДУУССАН БОНД)
  const sheet4 = workbook.addWorksheet("4.Хугацаа дууссан бонд");
  setColumnWidths(sheet4, [28, 12, 15, 15, 14, 18, 18, 15, 12, 20, 15, 10, 14, 14, 10, 15, 10, 15, 15, 15, 10, 15]);

  const titleRow4 = sheet4.addRow(["ХАВСРАЛТ 3", "", "", "", "", "", "", `ХУГАЦАА ДУУССАН БОНД БУЦААЛТ ${displayRefundDate}`]);
  titleRow4.font = { bold: true, size: 12 };

  sheet4.addRow([]);

  const headerRow4 = sheet4.addRow([
    "Email", "Утасны дугаар", "Овог", "Нэр", "Регистрийн дугаар", "Данс", "Хүлээн авах банк",
    "Гүйлгээний код", "ТТД", "Хөрөнгийн нэр", "Мөнгөн дүн", "Бонд хүү",
    "Худалдан авсан огноо", "Хүү тооцоолсон огноо", "Хүү тооцох хоног", "Тооцсон хүү",
    "100%", "Нийт үнэ", "AvtaMata шимтгэл", "Арилжааны шимтгэл", "0.10", "Шилжүүлэх дүн"
  ]);
  styleHeaderRow(headerRow4);

  maturedBondRows.forEach(b => {
    const bondRatePercent = `${(safeNumber(b.bondRate) * 100).toFixed(0)}%`;
    const maturedDataRow = sheet4.addRow([
      b.email || "", b.phone || "", b.lastName || "", b.firstName || "", b.regNumber || "",
      b.bankAccount || "", b.bank || "", b.txCode || "", b.ttd || "", b.name || "",
      ceilTo2Decimals(b.amount), bondRatePercent,
      stripTimeFromDate(b.purchaseDate || ""),
      stripTimeFromDate(b.interestEndDate || ""),
      safeNumber(b.days), ceilTo2Decimals(b.calculatedInterest),
      ceilTo2Decimals(b.calculatedInterest), ceilTo2Decimals(b.amount),
      b.avtamataFee || "", 0, ceilTo2Decimals(b.oat10), ceilTo2Decimals(b.transferAmount)
    ]);
    addBorderToRow(maturedDataRow);
  });

  // Add signature rows
  sheet4.addRow([]);
  sheet4.addRow([]);
  sheet4.addRow([]);
  sheet4.addRow(["", "", "", "", "", "", "Тооцоолол бэлтгэсэн", "", "", "", "", "", "", "Э.Соронзонболд"]);
  sheet4.addRow(["", "", "", "", "", "", "Тооцоолол хянасан", "", "", "", "", "", "", "Б. Энхтүвшин"]);
  sheet4.addRow(["", "", "", "", "", "", "Тооцоолол хянасан", "", "", "", "", "", "", "Ж. Сувд"]);

  // Sheet 5: Balance History (ХУДАЛДАН АВАЛТЫН ЗАДАРГАА)
  const sheet5 = workbook.addWorksheet("5.ХА задаргаа");

  // Stock symbols in fixed expected order.
  const stockSymbols = ["DIA", "VDE", "VUG", "VXF", "BITI", "TSLA", "BABA", "SGOV", "PDD", "MPNGY", "FXI", "JD", "HIMS", "VAL", "NVDA", "HOOD", "LI", "OSCR", "DVN", "CDE", "EUO", "BCI", "XLE"];

  // Set column widths for Sheet 5 (15 base columns + stock columns + Тайлбар)
  const baseWidths = [20, 15, 18, 15, 15, 15, 14, 12, 28, 18, 15, 8, 15, 15, 15];
  const stockWidths = stockSymbols.map(() => 10);
  setColumnWidths(sheet5, [...baseWidths, ...stockWidths, 25]);

  // Title row
  const haTitle = sheet5.addRow(["Хавсралт 4", "", "", "", "", "", "ХУДАЛДАН АВАЛТЫН ЗАДАРГАА"]);
  haTitle.font = { bold: true, size: 12 };

  // Empty row
  sheet5.addRow([]);

  // Header row - matching CSV format with stock columns
  const haHeader = sheet5.addRow([
    "Голомт огноо", "Дүн", "Данс", "Худалдан авалтад орсон огноо",
    "Овог", "Нэр", "Регистр", "Утас", "И-мейл", "Гүйлгээний код",
    "Мөнгөн дүн", "Багц №", "", "Bond", "Худалдан авалтаас үлдсэн дүн",
    ...stockSymbols,
    "Тайлбар"
  ]);
  styleHeaderRow(haHeader);

  // Create bank statement lookup maps
  const bankStatementByTxCode: Record<string, string> = {};
  // Map by amount -> array of dates (multiple transactions can have same amount)
  const bankStatementByAmount: Record<number, { date: string; txCode: string }[]> = {};

  // Store all bank statement entries for flexible matching
  const allBankStatementEntries: { date: string; income: number; txCode: string; account: string }[] = [];

  bankStatementData.forEach(bs => {
    // Store by txCode for direct lookup
    if (bs.txCode) {
      const txCodeClean = bs.txCode.trim().toUpperCase();
      bankStatementByTxCode[txCodeClean] = bs.date;
      // Also store without spaces
      bankStatementByTxCode[txCodeClean.replace(/\s+/g, "")] = bs.date;
    }
    // Store by amount for matching
    if (bs.income > 0) {
      if (!bankStatementByAmount[bs.income]) {
        bankStatementByAmount[bs.income] = [];
      }
      bankStatementByAmount[bs.income].push({ date: bs.date, txCode: bs.txCode || "" });
    }
    // Store all entries for flexible search
    allBankStatementEntries.push({ date: bs.date, income: bs.income, txCode: bs.txCode || "", account: bs.account || "" });
  });


  // Filter date: October 25, 2024 (inclusive)
  const cutoffDate = new Date("2024-10-25");

  // Filter balanceHistoryRows to only include purchases on or after October 25, 2024
  const filteredBalanceHistoryRows = balanceHistoryRows.filter(h => {
    if (!h.purchaseDate) return false;
    const purchaseDate = parsePurchaseDate(h.purchaseDate);
    if (!purchaseDate) return false;
    return purchaseDate >= cutoffDate;
  });


  // Helper function to parse bank statement date
  const parseBankDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const match1 = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (match1) return new Date(parseInt(match1[3]), parseInt(match1[1]) - 1, parseInt(match1[2]));
    const match2 = dateStr.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (match2) return new Date(parseInt(match2[1]), parseInt(match2[2]) - 1, parseInt(match2[3]));
    return null;
  };

  // Helper to parse purchase date (YYYY/MM/DD format)
  function parsePurchaseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const text = stripTimeFromDate(String(dateStr).trim());

    const ymd = text.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);
    if (ymd) {
      return new Date(parseInt(ymd[1], 10), parseInt(ymd[2], 10) - 1, parseInt(ymd[3], 10));
    }

    const mdy = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (mdy) {
      return new Date(parseInt(mdy[3], 10), parseInt(mdy[1], 10) - 1, parseInt(mdy[2], 10));
    }

    return null;
  }

  function parseFlexibleDate(dateStr: string): Date | null {
    if (!dateStr) return null;
    const normalized = normalizeDateKey(dateStr);
    const compactMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (compactMatch) {
      return new Date(parseInt(compactMatch[1], 10), parseInt(compactMatch[2], 10) - 1, parseInt(compactMatch[3], 10));
    }
    const m1 = dateStr.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
    if (m1) return new Date(parseInt(m1[1]), parseInt(m1[2]) - 1, parseInt(m1[3]));
    const m2 = dateStr.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (m2) return new Date(parseInt(m2[3]), parseInt(m2[1]) - 1, parseInt(m2[2]));
    return null;
  }

  function parseFlexibleDateTime(dateStr: string): Date | null {
    if (!dateStr) return null;
    const normalized = normalizeDateTimeKey(dateStr);
    const match = normalized.match(
      /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/
    );
    if (match) {
      return new Date(
        parseInt(match[1], 10),
        parseInt(match[2], 10) - 1,
        parseInt(match[3], 10),
        parseInt(match[4], 10),
        parseInt(match[5], 10),
        parseInt(match[6], 10),
      );
    }
    return parseFlexibleDate(dateStr);
  }

  function toYMD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function roundTo2(value: number): number {
    return Math.round(safeNumber(value) * 100) / 100;
  }

  // Find all bank entries for a user's AT1 code.
  const findBankEntriesForUser = (txCode: string): { id: string; date: string; income: number; account: string; parsedDate: Date }[] => {
    const at1Match = txCode.trim().toUpperCase().match(/AT1-[A-Z0-9]{4}-[A-Z0-9]{4}/i);
    if (!at1Match) return [];
    const at1Code = at1Match[0].toUpperCase();
    return allBankStatementEntries
      .flatMap((e, index) => {
        if (!e.txCode.toUpperCase().includes(at1Code)) return [];
        const parsedDate = parseBankDate(e.date);
        if (!parsedDate) return [];
        return [{
          id: `${at1Code}|${index}|${e.date}|${safeNumber(e.income)}|${e.account || ""}`,
          date: e.date,
          income: e.income,
          account: e.account || "",
          parsedDate,
        }];
      })
      .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
  };

  // Sheet 5 is rebuilt later from uploaded purchase history so that
  // purchase dates, source lines, and stock allocations follow the user's
  // upload exactly. Keep the original bank lookup helpers, but do not emit
  // the balance-history rows here.

  const selectedEmails = new Set(Object.keys(customerMap).map((email) => normalizeEmailKey(email)));
  const customerOrder = new Map<string, number>();
  Object.keys(customerMap).forEach((email, index) => {
    customerOrder.set(normalizeEmailKey(email), index);
  });
  const normalizePhoneKey = (value: string): string => String(value || "").replace(/\D+/g, "");
  const customerEmailByRegNumber = new Map<string, string>();
  const customerEmailByPhone = new Map<string, string>();
  Object.values(customerMap).forEach((customer) => {
    const emailKey = normalizeEmailKey(customer.email || "");
    const regNumberKey = String(customer.regNumber || "").trim().toUpperCase();
    const phoneKey = normalizePhoneKey(customer.phone || "");
    if (emailKey.includes("@")) {
      if (regNumberKey) customerEmailByRegNumber.set(regNumberKey, emailKey);
      if (phoneKey) customerEmailByPhone.set(phoneKey, emailKey);
    }
  });
  const resolvePurchaseHistoryEmail = (row: PurchaseHistoryRow): string => {
    const directEmail = normalizeEmailKey(String(row.email || ""));
    if (selectedEmails.has(directEmail)) return directEmail;

    const regNumberKey = String(row.regNumber || "").trim().toUpperCase();
    if (regNumberKey && customerEmailByRegNumber.has(regNumberKey)) {
      return customerEmailByRegNumber.get(regNumberKey)!;
    }

    const phoneKey = normalizePhoneKey(String(row.phone || ""));
    if (phoneKey && customerEmailByPhone.has(phoneKey)) {
      return customerEmailByPhone.get(phoneKey)!;
    }

    return directEmail;
  };

  type PreliminaryFundingEntry = {
    rowKey: string;
    dateKey: string;
    normalizedDate: string;
    parsedDate: Date;
    order: number;
    email: string;
    txCode: string;
    amount: number;
    portfolioIndex: number;
    bond: number;
    holdAmount: number;
    haDate: string;
  };
  type PreliminaryBankLine = {
    id: string;
    date: string;
    income: number;
    account: string;
    parsedDate: Date;
  };
  const preliminaryFundingEntries = purchaseHistoryData
    .map((row, order) => {
      const email = resolvePurchaseHistoryEmail(row);
      const parsedDate = parseFlexibleDateTime(row.haDate || "") || parseFlexibleDate(row.haDate || "");
      if (!email.includes("@") || !parsedDate || !selectedEmails.has(email)) return null;
      const normalizedDate = toYMD(parsedDate);
      return {
        rowKey: `${email}|${normalizedDate}|${order}`,
        dateKey: `${email}|${normalizedDate}`,
        normalizedDate,
        parsedDate,
        order,
        email,
        txCode: String(row.txCode || customerMap[email]?.depositCode || "").trim(),
        amount: safeNumber(row.amount),
        portfolioIndex: Number(row.portfolioIndex) || 0,
        bond: safeNumber(row.bond),
        holdAmount: safeNumber(row.holdAmount),
        haDate: row.haDate || "",
      } as PreliminaryFundingEntry;
    })
    .filter((row): row is PreliminaryFundingEntry => row !== null);
  const preliminaryFundingEntriesByEmail = new Map<string, PreliminaryFundingEntry[]>();
  preliminaryFundingEntries.forEach((entry) => {
    if (!preliminaryFundingEntriesByEmail.has(entry.email)) preliminaryFundingEntriesByEmail.set(entry.email, []);
    preliminaryFundingEntriesByEmail.get(entry.email)!.push(entry);
  });
  preliminaryFundingEntriesByEmail.forEach((entries) => {
    entries.sort((a, b) => {
      if (a.parsedDate.getTime() !== b.parsedDate.getTime()) {
        return a.parsedDate.getTime() - b.parsedDate.getTime();
      }
      return a.order - b.order;
    });
  });
  const PRELIMINARY_BANK_LOOKBACK_DAYS = 60;
  const findBestRecentBankSubset = (
    candidates: PreliminaryBankLine[],
    targetAmount: number,
  ): PreliminaryBankLine[] => {
    const target = roundTo2(targetAmount);
    if (target <= 0 || candidates.length === 0) return [];
    const items = candidates.slice(-8);
    let bestExact: { itemCount: number; recencyScore: number; items: PreliminaryBankLine[] } | null = null;
    let bestUnder: { total: number; itemCount: number; recencyScore: number; items: PreliminaryBankLine[] } | null = null;
    const subsetCount = 1 << items.length;
    for (let mask = 1; mask < subsetCount; mask++) {
      const selected = items.filter((_, index) => (mask & (1 << index)) !== 0);
      const total = roundTo2(selected.reduce((sum, item) => sum + safeNumber(item.income), 0));
      if (total <= 0 || total - target > 1) continue;
      const recencyScore = selected.reduce((sum, item) => sum + items.indexOf(item), 0);
      if (Math.abs(total - target) <= 1) {
        const candidate = { itemCount: selected.length, recencyScore, items: selected };
        if (
          !bestExact ||
          candidate.itemCount < bestExact.itemCount ||
          (candidate.itemCount === bestExact.itemCount && candidate.recencyScore > bestExact.recencyScore)
        ) {
          bestExact = candidate;
        }
        continue;
      }
      const candidate = { total, itemCount: selected.length, recencyScore, items: selected };
      if (
        !bestUnder ||
        candidate.total > bestUnder.total + 0.0001 ||
        (Math.abs(candidate.total - bestUnder.total) <= 0.0001 && candidate.itemCount < bestUnder.itemCount) ||
        (
          Math.abs(candidate.total - bestUnder.total) <= 0.0001 &&
          candidate.itemCount === bestUnder.itemCount &&
          candidate.recencyScore > bestUnder.recencyScore
        )
      ) {
        bestUnder = candidate;
      }
    }
    const matched = bestExact?.items || bestUnder?.items || [];
    return matched
      .slice()
      .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
  };
  const sumBankLines = (lines: Array<{ income: number }>): number =>
    roundTo2(lines.reduce((sum, line) => sum + safeNumber(line.income), 0));
  const preliminaryBankSourcesByRowKey = new Map<string, PreliminaryBankLine[]>();
  preliminaryFundingEntriesByEmail.forEach((entries, email) => {
    const txCode = entries.find((entry) => entry.txCode)?.txCode || customerMap[email]?.depositCode || "";
    const userBankEntries = findBankEntriesForUser(txCode) as PreliminaryBankLine[];
    const usedIds = new Set<string>();
    entries.forEach((entry) => {
      if (entry.amount <= 0) return;
      const candidates = userBankEntries.filter((candidate) => {
        const diffDaysToPurchase = Math.floor((entry.parsedDate.getTime() - candidate.parsedDate.getTime()) / (1000 * 60 * 60 * 24));
        return !usedIds.has(candidate.id) &&
          candidate.parsedDate.getTime() <= entry.parsedDate.getTime() &&
          diffDaysToPurchase >= 0 &&
          diffDaysToPurchase <= PRELIMINARY_BANK_LOOKBACK_DAYS;
      });
      const matched = findBestRecentBankSubset(candidates, entry.amount);
      const matchedTotal = sumBankLines(matched);
      if (matched.length === 0 || Math.abs(matchedTotal - roundTo2(entry.amount)) > 1) return;
      preliminaryBankSourcesByRowKey.set(entry.rowKey, matched);
      matched.forEach((line) => usedIds.add(line.id));
    });
  });
  const preliminaryBankAmountByRowKey = new Map<string, number>();
  preliminaryBankSourcesByRowKey.forEach((bankLines, rowKey) => {
    preliminaryBankAmountByRowKey.set(
      rowKey,
      roundTo2(bankLines.reduce((sum, line) => sum + safeNumber(line.income), 0)),
    );
  });
  const bankEntriesByAt1Code = new Map<string, PreliminaryBankLine[]>();
  allBankStatementEntries.forEach((entry, index) => {
    const at1Match = String(entry.txCode || "").trim().toUpperCase().match(/AT1-[A-Z0-9]{4}-[A-Z0-9]{4}/i);
    const parsedDate = parseBankDate(entry.date);
    if (!at1Match || !parsedDate) return;
    const at1Code = at1Match[0].toUpperCase();
    if (!bankEntriesByAt1Code.has(at1Code)) bankEntriesByAt1Code.set(at1Code, []);
    bankEntriesByAt1Code.get(at1Code)!.push({
      id: `${at1Code}|infer|${index}|${entry.date}|${safeNumber(entry.income)}|${entry.account || ""}`,
      date: entry.date,
      income: entry.income,
      account: entry.account || "",
      parsedDate,
    });
  });
  bankEntriesByAt1Code.forEach((entries) => {
    entries.sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
  });

  // Sheet 7: Sold Stocks (Зарсан ХЦ)
  const sheet6 = workbook.addWorksheet("7.Зарсан ХЦ");
  // Set column widths for Sheet 6 (17 columns)
  setColumnWidths(sheet6, [28, 14, 15, 18, 16, 14, 14, 12, 12, 12, 12, 15, 14, 14, 15, 16, 35]);

  // Title row
  const ssTitle = sheet6.addRow(["Хавсралт 5", "", "", "", "", "ХУВЬЦАА ЗАРЖ ДАХИН ЦЭНЭГЛЭСЭН ЗАДАРГАА"]);
  ssTitle.font = { bold: true, size: 12 };

  // Add empty row
  sheet6.addRow([]);

  const ssHeader = sheet6.addRow([
    "Email", "Утасны дугаар", "Овог", "Нэр", "Регистрийн дугаар", "ТТД",
    "Хөрөнгийн төрөл", "Хөрөнгийн нэр", "Тоо ширхэг", "Ханш", "USD-MNT",
    "Нийт үнэ", "ҮЦК шимтгэл", "AvtaMata шимтгэл", "Шилжүүлэх дүн",
    "Огноо", "Тайлбар"
  ]);
  styleHeaderRow(ssHeader);

  const soldPurchasesByEmail = new Map<string, { date: string; key: string; amount: number }[]>();
  const soldRechargeByEmail = new Map<string, { date: string; key: string; amount: number }[]>();
  purchaseHistoryData.forEach((ph) => {
    const email = (ph.email || "").toLowerCase().trim();
    if (!email.includes("@")) return;
    if (safeNumber(ph.bond) <= 0) return;
    const key = normalizeDateKey(ph.haDate || "");
    if (!key) return;
    if (!soldPurchasesByEmail.has(email)) soldPurchasesByEmail.set(email, []);
    soldPurchasesByEmail.get(email)!.push({
      date: formatNarrativeDisplayDate(ph.haDate || ""),
      key,
      amount: safeNumber(ph.amount),
    });
  });
  accumulatedChargeSheets.forEach((sheet) => {
    let sheetDate = sheet.name || "";
    if (/^\d{8}$/.test(sheetDate)) {
      sheetDate = `${sheetDate.substring(0, 4)}/${sheetDate.substring(4, 6)}/${sheetDate.substring(6, 8)}`;
    }
    const sheetKey = normalizeDateKey(sheetDate);
    const sheetDisplayDate = formatNarrativeDisplayDate(sheetDate);
    sheet.data.forEach((charge) => {
      const email = (charge.email || "").toLowerCase().trim();
      if (!email.includes("@")) return;
      const amount = safeNumber(charge.amount);
      if (amount <= 0 || !sheetKey) return;
      if (!soldRechargeByEmail.has(email)) soldRechargeByEmail.set(email, []);
      soldRechargeByEmail.get(email)!.push({
        date: sheetDisplayDate,
        key: sheetKey,
        amount,
      });
    });
  });
  soldPurchasesByEmail.forEach((arr) => arr.sort((a, b) => a.key.localeCompare(b.key)));
  soldRechargeByEmail.forEach((arr) => arr.sort((a, b) => a.key.localeCompare(b.key)));
  const findExactMatchedDestinationDateForSold = (
    email: string,
    fromDate: string,
    soldSumAmount: number,
  ): string => {
    const fromKey = normalizeDateKey(fromDate);
    if (!fromKey) return "";
    const target = Math.max(0, safeNumber(soldSumAmount));
    if (target <= 0) return "";

    const rechargeList = soldRechargeByEmail.get((email || "").toLowerCase().trim()) || [];
    const futureRecharge = rechargeList.filter((item) => item.key > fromKey);
    const rechargeExactish = futureRecharge
      .filter((item) => Math.abs(safeNumber(item.amount) - target) <= 1)
      .sort((a, b) => a.key.localeCompare(b.key));
    if (rechargeExactish.length > 0) return rechargeExactish[0].date;

    const list = soldPurchasesByEmail.get((email || "").toLowerCase().trim()) || [];
    const future = list.filter((item) => item.key > fromKey);
    const exactish = future
      .filter((item) => Math.abs(safeNumber(item.amount) - target) <= 1)
      .sort((a, b) => a.key.localeCompare(b.key));
    if (exactish.length > 0) return exactish[0].date;

    return "";
  };
  const findMatchedPurchaseDateForSold = (
    email: string,
    fromDate: string,
    soldSumAmount: number,
  ): string => {
    return findExactMatchedDestinationDateForSold(email, fromDate, soldSumAmount);
  };
  const findPurchaseAmountForSoldDate = (
    email: string,
    purchaseDate: string,
    soldSumAmount: number,
  ): number => {
    const purchaseKey = normalizeDateKey(purchaseDate);
    if (!purchaseKey) return 0;
    const target = roundTo2(soldSumAmount);
    const candidates = (soldPurchasesByEmail.get((email || "").toLowerCase().trim()) || [])
      .filter((item) => item.key === purchaseKey && safeNumber(item.amount) > 0);
    if (candidates.length === 0) return 0;

    const underOrEqual = candidates
      .filter((item) => roundTo2(item.amount) <= target + 0.01)
      .sort((a, b) => {
        const diffA = Math.abs(roundTo2(a.amount) - target);
        const diffB = Math.abs(roundTo2(b.amount) - target);
        if (diffA !== diffB) return diffA - diffB;
        return roundTo2(b.amount) - roundTo2(a.amount);
      });
    if (underOrEqual.length > 0) return roundTo2(underOrEqual[0].amount);

    const closest = candidates
      .slice()
      .sort((a, b) => Math.abs(roundTo2(a.amount) - target) - Math.abs(roundTo2(b.amount) - target))[0];
    return closest ? roundTo2(closest.amount) : 0;
  };
  const formatNarrativeDate = (dateStr: string): string => {
    return formatNarrativeDisplayDate(dateStr);
  };
  type SoldRenderableRow = {
    email: string;
    soldAmount: number;
    soldDate: string;
    assetName: string;
    rowValues: Array<string | number>;
    sourceIndex: number;
  };
  type CompletedRefundStockMatch = {
    matchKey: string;
    symbol: string;
    quantity: number;
    purchasedAtKey: string;
  };
  const completedRefundStocksByEmail = new Map<string, CompletedRefundStockMatch[]>();
  const activeRefundStocksByEmail = new Map<string, CompletedRefundStockMatch[]>();
  Object.entries(refundHistoryMap).forEach(([email, entries]) => {
    const emailKey = normalizeEmailKey(email);
    const completedStocks: CompletedRefundStockMatch[] = [];
    const activeStocks: CompletedRefundStockMatch[] = [];
    entries.forEach((entry, entryIndex) => {
      const status = (entry.status || "").toLowerCase();
      const targetList =
        status === "done"
          ? completedStocks
          : status && status !== "canceled"
            ? activeStocks
            : null;
      if (!targetList) return;
      (entry.stocks || []).forEach((stock, stockIndex) => {
        const symbol = String(stock.symbol || "").trim().toUpperCase();
        const purchasedAtKey = normalizeDateKey(stock.purchasedAt || "");
        const quantity = safeNumber(stock.quantity);
        if (!symbol || !purchasedAtKey || quantity <= 0) return;
        targetList.push({
          matchKey: `${emailKey}|${entryIndex}|${stockIndex}`,
          symbol,
          quantity,
          purchasedAtKey,
        });
      });
    });
    if (completedStocks.length > 0) {
      completedRefundStocksByEmail.set(emailKey, completedStocks);
    }
    if (activeStocks.length > 0) {
      activeRefundStocksByEmail.set(emailKey, activeStocks);
    }
  });
  const usedCompletedRefundStockKeys = new Set<string>();
  const findCompletedRefundStockMatch = (soldRow: SoldStockRow): string | null => {
    const emailKey = normalizeEmailKey(soldRow.email || "");
    const purchasedAtKey = normalizeDateKey(String(soldRow.date || ""));
    const symbol = String(soldRow.assetName || "").trim().toUpperCase();
    const quantity = safeNumber(soldRow.quantity);
    if (!emailKey || !purchasedAtKey || !symbol || quantity <= 0) return null;

    const completedStocks = completedRefundStocksByEmail.get(emailKey) || [];
    const activeStocks = activeRefundStocksByEmail.get(emailKey) || [];
    const hasActiveMatch = activeStocks.some((entry) =>
      entry.symbol === symbol &&
      entry.purchasedAtKey === purchasedAtKey &&
      Math.abs(entry.quantity - quantity) <= 0.0005,
    );
    if (hasActiveMatch) return null;

    const matched = completedStocks.find((entry) =>
      !usedCompletedRefundStockKeys.has(entry.matchKey) &&
      entry.symbol === symbol &&
      entry.purchasedAtKey === purchasedAtKey &&
      Math.abs(entry.quantity - quantity) <= 0.0005,
    );
    return matched?.matchKey || null;
  };
  const soldRenderableRows: SoldRenderableRow[] = [];
  let soldSourceIndex = 0;

  accumulatedSoldStockSheets.forEach(sheet => {
    sheet.data
      .filter((s) => selectedEmails.has(normalizeEmailKey(s.email || "")))
      .forEach((s) => {
        const completedRefundMatchKey = findCompletedRefundStockMatch(s);
        if (completedRefundMatchKey) {
          usedCompletedRefundStockKeys.add(completedRefundMatchKey);
          return;
        }
        const fmtNum = (v: number) => v ? floorTo2Decimals(v) : "";
        const soldDate = String(s.date || "");
        soldRenderableRows.push({
          email: normalizeEmailKey(s.email || ""),
          soldAmount: Math.max(0, safeNumber(s.transferAmount)),
          soldDate,
          assetName: String(s.assetName || "").trim(),
          rowValues: [
            sanitizeEmail(s.email || ""), s.phone, s.lastName, s.firstName, s.regNumber, s.ttd,
            s.assetType, s.assetName, s.quantity, fmtNum(s.price), fmtNum(s.usdMnt), fmtNum(s.totalPrice),
            fmtNum(s.uckFee), fmtNum(s.avtamataFee), fmtNum(s.transferAmount),
            soldDate, ""
          ],
          sourceIndex: soldSourceIndex++,
        });
      });
  });
  soldRenderableRows.sort((a, b) => {
    const aOrder = customerOrder.get(a.email) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = customerOrder.get(b.email) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.sourceIndex - b.sourceIndex;
  });
  const getBlockLookupDate = (block: SoldRenderableRow[]): string => {
    let maxSoldDateKey = "";
    let lookupDate = block[block.length - 1]?.soldDate || "";
    block.forEach((item) => {
      const key = normalizeDateKey(item.soldDate);
      if (key && key >= maxSoldDateKey) {
        maxSoldDateKey = key;
        lookupDate = item.soldDate;
      }
    });
    return lookupDate;
  };
  const finalizeSoldBlock = (block: SoldRenderableRow[], forcedPurchaseDate = "") => {
    if (block.length === 0) return;
    const rawTotalSoldAmount = roundTo2(block.reduce((sum, item) => sum + item.soldAmount, 0));
    if (rawTotalSoldAmount <= 0) return;

    const lookupDate = getBlockLookupDate(block);
    const nextPurchaseDate = forcedPurchaseDate || findMatchedPurchaseDateForSold(block[0].email, lookupDate, rawTotalSoldAmount);
    const purchaseAmountForComment = nextPurchaseDate
      ? findPurchaseAmountForSoldDate(block[0].email, nextPurchaseDate, rawTotalSoldAmount)
      : 0;

    const renderedRows = [...block]
      .map((item) => ({
        ...item,
        soldAmount: roundTo2(item.soldAmount),
        rowValues: [...item.rowValues],
      }));

    let totalSoldAmount = rawTotalSoldAmount;
    if (nextPurchaseDate && purchaseAmountForComment > 0 && purchaseAmountForComment < rawTotalSoldAmount - 0.01) {
      const difference = roundTo2(rawTotalSoldAmount - purchaseAmountForComment);
      const targetRow = renderedRows
        .filter((item) => item.soldAmount > 0)
        .sort((a, b) => b.soldAmount - a.soldAmount || b.sourceIndex - a.sourceIndex)[0];
      if (targetRow) {
        const currentAvtaFee = Number(targetRow.rowValues[13] || 0) || 0;
        const currentTransferAmount = Number(targetRow.rowValues[14] || 0) || 0;
        targetRow.rowValues[13] = floorTo2Decimals(currentAvtaFee + difference);
        targetRow.rowValues[14] = floorTo2Decimals(Math.max(0, currentTransferAmount - difference));
        targetRow.soldAmount = roundTo2(Math.max(0, targetRow.soldAmount - difference));
        totalSoldAmount = roundTo2(renderedRows.reduce((sum, item) => sum + item.soldAmount, 0));
      }
    }

    if (nextPurchaseDate) {
      const soldKey = `${block[0].email}|${normalizeDateKey(nextPurchaseDate)}`;
      soldCarryByEmailDate[soldKey] = (soldCarryByEmailDate[soldKey] || 0) + totalSoldAmount;
    }
    const totalFormatted = totalSoldAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const comment = nextPurchaseDate
      ? `${totalFormatted} нь ${formatNarrativeDate(nextPurchaseDate)} худалдан авалтад орсон.`
      : `${totalFormatted} нь бэлэн мөнгө хэлбэрээр байршиж байсан тул буцаав.`;

    const symbolOrder = new Map<string, number>();
    renderedRows.forEach((item, index) => {
      const key = item.assetName || "";
      if (!symbolOrder.has(key)) symbolOrder.set(key, index);
    });
    renderedRows.sort((a, b) => {
      const aOrder = symbolOrder.get(a.assetName || "") ?? Number.MAX_SAFE_INTEGER;
      const bOrder = symbolOrder.get(b.assetName || "") ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      const aDate = normalizeDateKey(a.soldDate);
      const bDate = normalizeDateKey(b.soldDate);
      if (aDate !== bDate) return aDate.localeCompare(bDate);
      return a.sourceIndex - b.sourceIndex;
    });

    const startRow = sheet6.rowCount + 1;
    renderedRows.forEach((item, index) => {
      const addedRow = sheet6.addRow([
        ...item.rowValues.slice(0, 16),
        index === 0 ? comment : "",
      ]);
      addBorderToRow(addedRow);
    });
    const endRow = sheet6.rowCount;
    if (endRow > startRow) {
      sheet6.mergeCells(startRow, 17, endRow, 17);
    }
  };

  let activeBlock: SoldRenderableRow[] = [];
  let activeExactMatchDate = "";
  soldRenderableRows.forEach((item, index) => {
    if (activeBlock.length === 0) {
      activeBlock = [item];
    } else if (item.email !== activeBlock[0].email) {
      finalizeSoldBlock(activeBlock, activeExactMatchDate);
      activeBlock = [item];
      activeExactMatchDate = "";
    } else {
      activeBlock.push(item);
    }

    const activeSum = activeBlock.reduce((sum, row) => sum + row.soldAmount, 0);
    const lookupDate = getBlockLookupDate(activeBlock);
    activeExactMatchDate = findExactMatchedDestinationDateForSold(activeBlock[0].email, lookupDate, activeSum);

    const nextItem = soldRenderableRows[index + 1];
    if (!nextItem) {
      finalizeSoldBlock(activeBlock, activeExactMatchDate);
      activeBlock = [];
      activeExactMatchDate = "";
      return;
    }

    const nextItemIsSameEmail = nextItem.email === activeBlock[0].email;
    const nextItemHasPositiveTransfer = safeNumber(nextItem.soldAmount) > 0;

    // Once the running transfer sum exactly matches a future purchase,
    // lock that block before the next positive transfer can distort the sum.
    // Keep trailing zero-transfer rows in the same block so paired symbol rows
    // like "BABA" + zero follow-up still render together.
    if (activeExactMatchDate && (!nextItemIsSameEmail || nextItemHasPositiveTransfer)) {
      finalizeSoldBlock(activeBlock, activeExactMatchDate);
      activeBlock = [];
      activeExactMatchDate = "";
    }
  });

  // Sheet 6: Bond Repurchase (Бонд дахин ХА)
  const sheet7 = workbook.addWorksheet("6.Бонд дахин ХА");
  // Set column widths for Sheet 6 (23 columns)
  setColumnWidths(sheet7, [28, 12, 15, 15, 14, 18, 18, 15, 12, 20, 25, 15, 10, 14, 14, 10, 15, 15, 15, 15, 10, 15, 35]);

  const brTitle = sheet7.addRow(["Хавсралт 6", "", "", "", "", "", "", "БОНДЫН ДАХИН ХУДАЛДАН АВАЛТЫН ЗАДАРГАА"]);
  brTitle.font = { bold: true, size: 12 };
  const brHeader = sheet7.addRow([
    "Email", "Утасны дугаар", "Овог", "Нэр", "Регистрийн дугаар", "Данс", "Хүлээн авах банк",
    "Гүйлгээний код", "ТТД", "Хөрөнгийн нэр", "Тайлбар", "Мөнгөн дүн", "Бонд хүү",
    "Худалдан авсан огноо", "Хүү тооцоолсон огноо", "Хүү тооцох хоног", "Тооцсон хүү",
    "Нийт", "Автамата шимтгэл", "Арилжааны шимтгэл", "ОАТ", "Нийт дүн", "Тайлбар"
  ]);
  styleHeaderRow(brHeader);

  // Debug: Log bondMetaMap contents
  console.log("Sheet 7 - bondMetaMap contents:");
  if (bondMetaMap) {
    Object.entries(bondMetaMap).forEach(([name, meta]) => {
      console.log(`  "${name}": rate=${meta.rate}, paymentDates=${JSON.stringify(meta.paymentDates)}`);
    });
  } else {
    console.log("  bondMetaMap is undefined!");
  }
  console.log("Sheet 6 - DEFAULT_BOND_METADATA with paymentDates:");
  Object.entries(DEFAULT_BOND_METADATA).filter(([_, meta]) => meta.paymentDates.length > 0).forEach(([name, meta]) => {
    console.log(`  "${name}": paymentDates=${JSON.stringify(meta.paymentDates)}`);
  });

  // Filter bondRetakeRows to only include purchases on or after October 25, 2024
  // Use revisedBondRetakeRows if available (from Part 3), otherwise use bondRetakeRows
  const rowsToFilter = revisedBondRetakeRows || bondRetakeRows;
  const filteredBondRetakeRows = rowsToFilter.filter(b => {
    const rawPurchaseDate =
      b.sourcePurchaseDateTime ||
      b.sourcePurchaseDate ||
      b.purchaseDate ||
      "";
    if (!rawPurchaseDate) return false;
    const purchaseDate =
      parseFlexibleDateTime(rawPurchaseDate) ||
      parsePurchaseDate(rawPurchaseDate);
    if (!purchaseDate) return false;
    return purchaseDate >= cutoffDate;
  });

  // Sheet 6 uses filtered bondRetakeRows (only "all zeros" from balanceHistory, after Oct 25, 2024)
  // Helper function to find nearest FUTURE payment date for P column (Хүү дуусах)
  // Logic: Find the first payment date that comes AFTER the purchase date
  const calculateInterestEndDate = (purchaseDateStr: string, bondNameToLookup: string): string => {
    const normalizedBondName = normalizeBondName(bondNameToLookup);
    const paymentDates = bondMetaMap?.[bondNameToLookup]?.paymentDates ||
                         bondMetaMap?.[normalizedBondName]?.paymentDates ||
                         DEFAULT_BOND_METADATA[bondNameToLookup]?.paymentDates ||
                         DEFAULT_BOND_METADATA[normalizedBondName]?.paymentDates || [];

    console.log(`calculateInterestEndDate: bond="${bondNameToLookup}" -> normalized="${normalizedBondName}", paymentDates=${JSON.stringify(paymentDates)}`);

    if (paymentDates.length === 0) {
      console.log(`  No payment dates found, using refundDate: ${refundDate}`);
      return refundDate;
    }

    const purchaseDate = parseFlexibleDate(purchaseDateStr);
    if (!purchaseDate) {
      console.log(`  Invalid purchase date format: ${purchaseDateStr}`);
      return refundDate;
    }

    const purchaseYear = purchaseDate.getFullYear();
    let nearestFutureDate: Date | null = null;

    paymentDates.forEach((rawDateStr) => {
      const dateStr = String(rawDateStr || "").trim();
      if (!dateStr) return;

      const fullySpecified = parseFlexibleDate(dateStr);
      if (fullySpecified && /\d{4}/.test(dateStr)) {
        if (fullySpecified > purchaseDate && (!nearestFutureDate || fullySpecified < nearestFutureDate)) {
          nearestFutureDate = fullySpecified;
        }
        return;
      }

      const parts = dateStr.split("/");
      if (parts.length < 2) return;
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      if (!Number.isFinite(month) || !Number.isFinite(day)) return;

      [0, 1, 2].forEach((yearOffset) => {
        const candidate = new Date(purchaseYear + yearOffset, month - 1, day);
        if (candidate > purchaseDate && (!nearestFutureDate || candidate < nearestFutureDate)) {
          nearestFutureDate = candidate;
        }
      });
    });

    if (nearestFutureDate) {
      const matchedDate = nearestFutureDate as Date;
      const year = matchedDate.getFullYear();
      const month = String(matchedDate.getMonth() + 1).padStart(2, "0");
      const day = String(matchedDate.getDate()).padStart(2, "0");
      const result = `${year}/${month}/${day}`;
      console.log(`  purchaseDate=${purchaseDateStr}, nearestFuture=${result}`);
      return result;
    }

    console.log(`  No nearest future date found, using refundDate: ${refundDate}`);
    return refundDate;
  };

  const formatCommentDate = (dateStr: string): string => {
    const d = parseFlexibleDate(dateStr);
    if (!d) return dateStr || "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const purchasesByEmail = new Map<string, { date: string; sortKey: string }[]>();
  purchaseHistoryData.forEach((ph) => {
    const email = (ph.email || "").toLowerCase().trim();
    if (!email) return;
    const parsed = parseFlexibleDate(ph.haDate || "");
    const sortKey = parsed ? toYMD(parsed) : (ph.haDate || "");
    if (!purchasesByEmail.has(email)) purchasesByEmail.set(email, []);
    purchasesByEmail.get(email)!.push({ date: ph.haDate || "", sortKey });
  });
  purchasesByEmail.forEach((arr) => arr.sort((a, b) => a.sortKey.localeCompare(b.sortKey)));
  const findNextPurchaseDate = (email: string, fromDate: string): string => {
    const parsedFrom = parseFlexibleDate(fromDate);
    const fromKey = parsedFrom ? toYMD(parsedFrom) : fromDate;
    const list = purchasesByEmail.get(email.toLowerCase().trim()) || [];
    for (const item of list) {
      if (item.sortKey > fromKey) return item.date;
    }
    return "";
  };
  const toCarryKey = (email: string, dateStr: string): string => {
    const d = parseFlexibleDate(dateStr);
    const normalizedDate = d ? toYMD(d) : (dateStr || "");
    return `${(email || "").toLowerCase().trim()}|${normalizedDate}`;
  };

  const formatSheet6Date = (value: string): string => {
    const parsed = parseFlexibleDate(value);
    if (!parsed) return value || "";
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
  };
  const formatSheet6CommentDate = (value: string): string => {
    const parsed = parseFlexibleDate(value);
    if (!parsed) return value || "";
    return `${parsed.getMonth() + 1}/${parsed.getDate()}/${parsed.getFullYear()}`;
  };
  const formatSheet5CarrySourceName = (value: string): string => {
    const normalized = normalizeBondName(value || "");
    if (!normalized) return "";
    if (normalized.startsWith("Нео")) return "Нео";
    if (normalized.startsWith("Нөмөр")) return "Нөмөр";
    if (normalized.startsWith("Шунхлай")) return "Шунхлай";
    if (normalized.startsWith("Лэнд")) return "Лэнд";
    if (normalized === "GSB-ABS ХБҮЦ" || normalized === "ХБҮЦ" || normalized === "GSB-ABS") return "ХБҮЦ";
    return normalized;
  };
  const buildSheet5CarrySourceLabel = (groupRows: ExpandedBondRepurchaseRow[]): string => {
    const sourceNames = Array.from(new Set(
      groupRows
        .map((row) => String(row.displayBondName || "").trim())
        .filter(Boolean)
        .map((name) =>
          name
            .replace(/^Нео бонд\s+/i, "Нео ")
            .replace(/^Шунхлай\s+/i, "Шунхлай")
        ),
    ));
    if (sourceNames.length === 0) return "Бондын хүү төлөлт";
    return `${sourceNames.join(" / ")} хүү төлөлт`;
  };
  const buildSheet5CarryComment = (groupRows: ExpandedBondRepurchaseRow[], carryAmount: number): string => {
    const sourceNames = Array.from(new Set(
      groupRows
        .map((row) => formatSheet5CarrySourceName(row.displayBondName))
        .filter(Boolean),
    ));
    if (sourceNames.length === 0) {
      return `${formatSheet6Money(carryAmount)} нь бондын дүн дээр нэмэгдэн бондын худалдан авалтад орсон.`;
    }
    return `${formatSheet6Money(carryAmount)} нь ${sourceNames.join(", ")} хүүгийн төлөлт болж, бондын дүн дээр нэмэгдэн бондын худалдан авалтад орсон.`;
  };
  const toDateKey = (value: Date): string => toYMD(value);
  const toDateTimeKey = (value: Date): string => {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    const hh = String(value.getHours()).padStart(2, "0");
    const mm = String(value.getMinutes()).padStart(2, "0");
    const ss = String(value.getSeconds()).padStart(2, "0");
    return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
  };
  const addDays = (value: Date, days: number): Date => {
    const next = new Date(value);
    next.setDate(next.getDate() + days);
    return next;
  };
  const diffDays = (start: Date, end: Date): number => {
    const normalizedStart = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate()
    );
    const normalizedEnd = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate()
    );
    const diff = Math.floor(
      (normalizedEnd.getTime() - normalizedStart.getTime()) /
        (1000 * 60 * 60 * 24)
    );
    return Math.max(0, diff);
  };
  const sameDate = (a: Date, b: Date): boolean => toDateKey(a) === toDateKey(b);
  const formatSheet6Money = (value: number): string =>
    safeNumber(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const targetNameForSheet6Comment = (bondName: string): string => {
    const normalized = normalizeBondName(bondName || "");
    if (normalized === "GSB-ABS ХБҮЦ" || normalized === "ХБҮЦ") return "ХБҮЦ";
    if (normalized.startsWith("Нео ")) return "Нео бонд";
    if (normalized.startsWith("Нео бонд ")) return "Нео бонд";
    if (normalized.startsWith("Шунхлай")) return "Шунхлай бонд";
    if (normalized === "Сэндли") return "Сэндли бонд";
    if (normalized === "Лэнд МН") return "Лэнд МН бонд";
    if (normalized === "Лэнд 2.4") return "Лэнд бонд";
    if (normalized === "Emerald") return "Эмералд бонд";
    if (normalized.startsWith("Нөмөр")) return "Нөмөр бонд";
    return normalized || bondName || "";
  };
  const formatDistributionDate = (value: string): string => {
    const parsed = parseFlexibleDate(value);
    if (!parsed) return value || "";
    return `${parsed.getFullYear()}/${String(parsed.getMonth() + 1).padStart(2, "0")}/${String(parsed.getDate()).padStart(2, "0")}`;
  };
  const SHEET6_DISTRIBUTION_AMOUNT_TOLERANCE = 10000;
  const isWithinSheet6DistributionTolerance = (
    leftAmount: number,
    rightAmount: number,
  ): boolean =>
    Math.abs(roundTo2(leftAmount) - roundTo2(rightAmount)) <=
    SHEET6_DISTRIBUTION_AMOUNT_TOLERANCE;
  const isFirstExpandedQuarterForSheet6Row = (
    row: ExpandedBondRepurchaseRow,
  ): boolean => {
    const adjustedSourcePurchaseDate = adjustSheet6PurchaseDateForBond(
      row.displayBondName,
      row.sourcePurchaseDateObj,
    );
    return toDateKey(adjustedSourcePurchaseDate) === toDateKey(row.purchaseDateObj);
  };
  const distributionRowsByBondQuarter = new Map<string, BondDistributionRow[]>();
  const distributionRowsByQuarter = new Map<string, BondDistributionRow[]>();
  bondDistributionFiles.forEach((file) => {
    file.data.forEach((row) => {
      const normalizedBondName = normalizeBondName(file.bondName || row.bondName || "");
      const normalizedInterestDate = normalizeDateKey(row.interestEndDate || "");
      const normalizedRow = {
        ...row,
        bondName: normalizedBondName,
        uploadSlotId: file.uploadSlotId || row.uploadSlotId,
      };
      if (normalizedBondName && normalizedInterestDate) {
        const key = `${normalizedBondName}|${normalizedInterestDate}`;
        if (!distributionRowsByBondQuarter.has(key)) {
          distributionRowsByBondQuarter.set(key, []);
        }
        distributionRowsByBondQuarter.get(key)!.push(normalizedRow);
      }
      if (normalizedInterestDate) {
        if (!distributionRowsByQuarter.has(normalizedInterestDate)) {
          distributionRowsByQuarter.set(normalizedInterestDate, []);
        }
        distributionRowsByQuarter.get(normalizedInterestDate)!.push(normalizedRow);
      }
    });
  });
  const getDistributionFallbackScheduleForSheet6 = (
    candidateBondNames: string[],
    regNumber: string,
    purchaseDate: Date,
    refundDateObj: Date,
  ): Date[] => {
    const regNumberKey = String(regNumber || "").trim().toUpperCase();
    if (!regNumberKey || candidateBondNames.length === 0) return [];
    const seen = new Set<string>();
    const schedule: Date[] = [];

    candidateBondNames.forEach((bondName) => {
      distributionRowsByBondQuarter.forEach((quarterRows, key) => {
        if (!key.startsWith(`${bondName}|`)) return;
        quarterRows.forEach((row) => {
          if (
            String(row.regNumber || "").trim().toUpperCase() !==
            regNumberKey
          ) {
            return;
          }
          const interestEndDate = parseFlexibleDate(
            row.interestEndDate || ""
          );
          if (!interestEndDate) return;
          if (
            interestEndDate.getTime() <= purchaseDate.getTime() ||
            interestEndDate.getTime() > refundDateObj.getTime()
          ) {
            return;
          }
          const dateKey = toDateKey(interestEndDate);
          if (seen.has(dateKey)) return;
          seen.add(dateKey);
          schedule.push(interestEndDate);
        });
      });
    });

    return schedule.sort((a, b) => a.getTime() - b.getTime());
  };
  const shouldPreferDistributionScheduleForSheet6 = (
    originalBondName: string,
    displayBondName: string,
  ): boolean => {
    const originalNormalized = normalizeBondName(originalBondName || "");
    const displayNormalized = normalizeBondName(displayBondName || "");
    const compactDisplayName = String(displayBondName || "").replace("Нео бонд ", "Нео ");
    const displayLower = String(displayBondName || "").toLowerCase().trim();
    const compactLower = compactDisplayName.toLowerCase().trim();
    const originalLower = String(originalBondName || "").toLowerCase().trim();
    const canonicalUploadedMeta =
      bondMetaMap?.[compactLower] ||
      bondMetaMap?.[displayLower] ||
      bondMetaMap?.[compactDisplayName] ||
      bondMetaMap?.[displayBondName] ||
      bondMetaMap?.[displayNormalized];
    const originalUploadedMeta =
      bondMetaMap?.[originalLower] ||
      bondMetaMap?.[originalBondName] ||
      bondMetaMap?.[originalNormalized];
    const originalDefaultMeta = DEFAULT_BOND_METADATA[originalNormalized];
    const displayDefaultMeta = DEFAULT_BOND_METADATA[displayNormalized];
    const originalHasTrustedSchedule =
      Array.isArray(originalDefaultMeta?.paymentDates) &&
      originalDefaultMeta.paymentDates.length > 0;
    const displayHasTrustedSchedule =
      Array.isArray(displayDefaultMeta?.paymentDates) &&
      displayDefaultMeta.paymentDates.length > 0;
    const hasUploadedSchedule =
      Array.isArray(canonicalUploadedMeta?.paymentDates) &&
      canonicalUploadedMeta.paymentDates.length > 0 ||
      Array.isArray(originalUploadedMeta?.paymentDates) &&
      originalUploadedMeta.paymentDates.length > 0;
    const isNeoSeries =
      compactDisplayName.startsWith("Нео ") ||
      displayNormalized.startsWith("Нео");
    if (isNeoSeries && hasUploadedSchedule) {
      return false;
    }
    return !originalHasTrustedSchedule && !displayHasTrustedSchedule;
  };
  const getSheet6DistributionCandidateNames = (
    displayBondName: string,
    sourceNormalizedBondName: string,
  ): string[] => {
    const candidates = new Set<string>();
    const normalizedDisplayName = normalizeBondName(displayBondName || "");
    if (normalizedDisplayName) candidates.add(normalizedDisplayName);
    if (sourceNormalizedBondName) candidates.add(sourceNormalizedBondName);
    if (displayBondName === "Гялс төгрөг" || sourceNormalizedBondName === "Сэндли") {
      candidates.add("Сэндли");
    }
    if (displayBondName === "GSB-ABS ХБҮЦ") {
      candidates.add("ХБҮЦ");
      candidates.add("GSB-ABS ХБҮЦ");
    }
    return Array.from(candidates);
  };
  const findBestSheet6DistributionMatch = (
    quarterRows: BondDistributionRow[],
    row: {
      regNumber: string;
      purchaseDate: string;
      interestEndDate: string;
      amount: number;
    },
  ): BondDistributionRow | undefined => {
    const regNumberKey = String(row.regNumber || "")
      .trim()
      .toUpperCase();
    const purchaseDateKey = normalizeDateKey(
      row.purchaseDate || ""
    );
    const sameRegRows = quarterRows.filter(
      (distributionRow) =>
        String(distributionRow.regNumber || "")
          .trim()
          .toUpperCase() === regNumberKey
    );
    if (sameRegRows.length === 0) return undefined;

    const currentAmount = roundTo2(row.amount);
    const rankedRows = [...sameRegRows]
      .map((distributionRow) => ({
        row: distributionRow,
        amountGap: Math.abs(roundTo2(safeNumber(distributionRow.amount)) - currentAmount),
        exactPurchaseDate:
          normalizeDateKey(distributionRow.purchaseDate || "") === purchaseDateKey,
      }))
      .filter((entry) =>
        isWithinSheet6DistributionTolerance(
          safeNumber(entry.row.amount),
          currentAmount,
        )
      )
      .sort((a, b) => {
        if (a.amountGap !== b.amountGap) return a.amountGap - b.amountGap;
        if (a.exactPurchaseDate !== b.exactPurchaseDate) {
          return a.exactPurchaseDate ? -1 : 1;
        }
        const dateA = parseFlexibleDate(a.row.purchaseDate || "")?.getTime() || 0;
        const dateB = parseFlexibleDate(b.row.purchaseDate || "")?.getTime() || 0;
        return dateA - dateB;
      });
    return rankedRows[0]?.row;
  };
  const findClosestSheet6DistributionMatchIgnoringTolerance = (
    quarterRows: BondDistributionRow[],
    row: {
      regNumber: string;
      purchaseDate: string;
      amount: number;
    },
  ): BondDistributionRow | undefined => {
    const regNumberKey = String(row.regNumber || "")
      .trim()
      .toUpperCase();
    const purchaseDateKey = normalizeDateKey(row.purchaseDate || "");
    const sameRegRows = quarterRows.filter(
      (distributionRow) =>
        String(distributionRow.regNumber || "")
          .trim()
          .toUpperCase() === regNumberKey
    );
    if (sameRegRows.length === 0) return undefined;

    const currentAmount = roundTo2(row.amount);
    return [...sameRegRows]
      .map((distributionRow) => ({
        row: distributionRow,
        amountGap: Math.abs(roundTo2(safeNumber(distributionRow.amount)) - currentAmount),
        exactPurchaseDate:
          normalizeDateKey(distributionRow.purchaseDate || "") === purchaseDateKey,
      }))
      .sort((a, b) => {
        if (a.amountGap !== b.amountGap) return a.amountGap - b.amountGap;
        if (a.exactPurchaseDate !== b.exactPurchaseDate) {
          return a.exactPurchaseDate ? -1 : 1;
        }
        const dateA = parseFlexibleDate(a.row.purchaseDate || "")?.getTime() || 0;
        const dateB = parseFlexibleDate(b.row.purchaseDate || "")?.getTime() || 0;
        return dateA - dateB;
      })[0]?.row;
  };
  const regNumberMatchesSheet6DistributionRow = (
    distributionRow: BondDistributionRow,
    regNumber: string,
  ): boolean =>
    String(distributionRow.regNumber || "").trim().toUpperCase() ===
    String(regNumber || "").trim().toUpperCase();
  const displayBondNameForSheet6 = (originalBondName: string, startDate?: Date): string => {
    const lowerOriginalBond = String(originalBondName || "").toLowerCase();
    let bondName = normalizeBondName(originalBondName || "");
    if (lowerOriginalBond.includes("гялс")) bondName = "Гялс төгрөг";
    if (lowerOriginalBond.includes("gsb-abs") || lowerOriginalBond.includes("хбүц")) bondName = "GSB-ABS ХБҮЦ";
    if (
      (lowerOriginalBond.includes("шунхлай") || lowerOriginalBond.includes("shunkhlai") || lowerOriginalBond.includes("shunhlai")) &&
      (lowerOriginalBond.includes("bond 2") || lowerOriginalBond.includes("бонд 2"))
    ) bondName = "Шунхлай 3.3";
    if ((lowerOriginalBond.includes("шунхлай") || lowerOriginalBond.includes("shunkhlai") || lowerOriginalBond.includes("shunhlai")) && lowerOriginalBond.includes("3.3")) bondName = "Шунхлай 3.3";
    if ((lowerOriginalBond.includes("шунхлай") || lowerOriginalBond.includes("shunkhlai") || lowerOriginalBond.includes("shunhlai")) && lowerOriginalBond.includes("3.1")) bondName = "Шунхлай 3.1";
    if (bondName === "Сэндли" && startDate) {
      const gyalsCutoffDate = new Date(2024, 11, 30);
      if (startDate.getTime() < gyalsCutoffDate.getTime()) {
        return "Гялс төгрөг";
      }
    }
    if (bondName.startsWith("Нео ")) {
      return `Нео бонд ${bondName.replace("Нео ", "")}`;
    }
    return bondName;
  };
  const getSheet6ChargeBondCandidates = (description: string): string[] => {
    const lowerDescription = String(description || "").toLowerCase().trim();
    if (!lowerDescription) return [];

    const candidates: string[] = [];
    if (lowerDescription.includes("гялс") || lowerDescription.includes("gyals")) {
      candidates.push("Гялс төгрөг");
    }
    if (lowerDescription.includes("сэндли") || lowerDescription.includes("sendly")) {
      candidates.push("Гялс төгрөг", "Сэндли");
    }
    if (lowerDescription.includes("gsb-abs") || lowerDescription.includes("хбүц")) {
      candidates.push("GSB-ABS ХБҮЦ");
    }
    if (lowerDescription.includes("лэнд мн") || lowerDescription.includes("lend mn")) {
      candidates.push("Лэнд МН");
    }
    if (
      lowerDescription.includes("лэнд 2.4") ||
      lowerDescription.includes("lend 2.4") ||
      lowerDescription.includes("bond 2.4")
    ) {
      candidates.push("Лэнд 2.4");
    }
    if (lowerDescription.includes("аму") || lowerDescription.includes("amu")) {
      if (lowerDescription.includes("2")) candidates.push("АМУ 2");
      else candidates.push("АМУ 1");
    }
    if (lowerDescription.includes("нөмөр") || lowerDescription.includes("numur") || lowerDescription.includes("nomur")) {
      if (lowerDescription.includes("8.5")) candidates.push("Нөмөр 8.5");
      else if (lowerDescription.includes("8.3")) candidates.push("Нөмөр 8.3");
      else candidates.push("Нөмөр");
    }
    if (lowerDescription.includes("нео") || lowerDescription.includes("neo")) {
      const neoVersion = lowerDescription.match(/(?:нео|neo)(?:\s+bond|\s+бонд)?\s*(1\.\d)/);
      if (neoVersion?.[1]) candidates.push(`Нео бонд ${neoVersion[1]}`);
      else candidates.push("Нео бонд");
    }
    if (lowerDescription.includes("шунхлай") || lowerDescription.includes("shunkhlai") || lowerDescription.includes("shunhlai")) {
      if (lowerDescription.includes("3.3") || lowerDescription.includes("bond 2") || lowerDescription.includes("бонд 2")) {
        candidates.push("Шунхлай 3.3");
      } else if (lowerDescription.includes("3.1")) {
        candidates.push("Шунхлай 3.1");
      } else {
        candidates.push("Шунхлай");
      }
    }
    if (lowerDescription.includes("emerald")) candidates.push("Emerald");
    if (lowerDescription.includes("national investment")) candidates.push("National Investment");
    if (lowerDescription.includes("цогц") || lowerDescription.includes("tsogts")) candidates.push("Цогц зээл");

    return Array.from(new Set(candidates));
  };
  const isCompatibleSheet6ChargeRow = (
    row: {
      displayBondName: string;
    },
    description: string,
  ): boolean => {
    const candidates = getSheet6ChargeBondCandidates(description);
    if (candidates.length === 0) return false;
    const displayName = row.displayBondName;
    return candidates.some((candidate) =>
      displayName === candidate ||
      (candidate === "Сэндли" && displayName === "Гялс төгрөг") ||
      (candidate === "Шунхлай" && displayName.startsWith("Шунхлай")) ||
      (candidate === "Нөмөр" && displayName.startsWith("Нөмөр")) ||
      (candidate === "Нео бонд" && displayName.startsWith("Нео бонд ")) ||
      (candidate === "Лэнд МН" && displayName === "Лэнд МН") ||
      (candidate === "GSB-ABS ХБҮЦ" && displayName === "GSB-ABS ХБҮЦ")
    );
  };
  const getBondMetaForSheet6 = (
    originalBondName: string,
    displayBondName: string,
  ): { rate: number; paymentDates: string[]; maturityDate?: string } | undefined => {
    const normalizedBondName = normalizeBondName(originalBondName);
    const compactDisplayName = displayBondName.replace("Нео бонд ", "Нео ");
    const displayLower = displayBondName.toLowerCase().trim();
    const compactLower = compactDisplayName.toLowerCase().trim();
    const originalLower = String(originalBondName || "").toLowerCase().trim();
    const canonicalMeta =
      bondMetaMap?.[compactLower] ||
      bondMetaMap?.[displayLower] ||
      bondMetaMap?.[compactDisplayName] ||
      bondMetaMap?.[displayBondName] ||
      bondMetaMap?.[normalizedBondName];
    const originalMeta =
      bondMetaMap?.[originalLower] ||
      bondMetaMap?.[originalBondName];
    const shunkhlaiBond2Meta =
      (originalLower.includes("shunkhlai") || originalLower.includes("shunhlai") || originalLower.includes("шунхлай")) &&
      (originalLower.includes("bond 2") || originalLower.includes("бонд 2"))
        ? DEFAULT_BOND_METADATA["Шунхлай 2"]
        : undefined;
    const defaultMeta =
      shunkhlaiBond2Meta ||
      DEFAULT_BOND_METADATA[compactDisplayName] ||
      DEFAULT_BOND_METADATA[displayBondName] ||
      DEFAULT_BOND_METADATA[normalizedBondName];

    // For a few canonical series such as Сэндли/АМУ/АБТС, prefer the hardcoded
    // recurring schedule to avoid alias collisions from uploaded metadata
    // (for example "Sendly bond 2" normalizing into "Сэндли").
    const shouldPreferDefaultSchedule = [
      "Гялс төгрөг",
      "Сэндли",
      "АМУ 1",
      "АМУ 2",
      "АБТС",
      "Лэнд МН",
      "Лэнд 2.4",
      "ХБҮЦ",
      "GSB-ABS ХБҮЦ",
      "GSB-ABS",
    ].includes(compactDisplayName);
    if (shouldPreferDefaultSchedule && defaultMeta?.paymentDates?.length) {
      return {
        rate: safeNumber(defaultMeta.rate) || safeNumber(canonicalMeta?.rate) || safeNumber(originalMeta?.rate),
        paymentDates: defaultMeta.paymentDates,
        // When we intentionally prefer the hardcoded recurring schedule for
        // canonical series such as Сэндли/АМУ/АБТС, also prefer the exact
        // original-name maturity date over the normalized fallback entry.
        // Otherwise an alias like "Sendly bond 2" can normalize into "Сэндли"
        // and incorrectly mark an early coupon date as the final maturity row.
        maturityDate: originalMeta?.maturityDate || canonicalMeta?.maturityDate || "",
      };
    }

    if (canonicalMeta || originalMeta) {
      return canonicalMeta || originalMeta;
    }
    if (defaultMeta) {
      return {
        rate: defaultMeta.rate,
        paymentDates: defaultMeta.paymentDates,
        maturityDate: "",
      };
    }
    return undefined;
  };
  const parseMonthDayPaymentDate = (raw: string): { month: number; day: number } | null => {
    const match = String(raw || "").trim().match(/^(\d{1,2})[/-](\d{1,2})$/);
    if (!match) return null;
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    if (!Number.isFinite(month) || !Number.isFinite(day)) return null;
    return { month, day };
  };
  const buildPaymentScheduleForSheet6 = (
    originalBondName: string,
    purchaseDate: Date,
    paymentDates: string[],
    refundDateObj: Date,
    maturityDateObj?: Date | null,
  ): Date[] => {
    const allPaymentDates: Date[] = [];
    const gyalsCutoffDate = new Date(2024, 11, 30);
    const normalized = normalizeBondName(originalBondName);
    if (normalized === "Сэндли" && purchaseDate.getTime() < gyalsCutoffDate.getTime()) {
      allPaymentDates.push(new Date(gyalsCutoffDate));
    }

    paymentDates.forEach((rawPaymentDate) => {
      const explicit = parseFlexibleDate(rawPaymentDate);
      if (explicit && explicit.getTime() > purchaseDate.getTime() && explicit.getTime() <= refundDateObj.getTime()) {
        if (!allPaymentDates.some((item) => item.getTime() === explicit.getTime())) {
          allPaymentDates.push(new Date(explicit));
        }
        return;
      }

      const monthDay = parseMonthDayPaymentDate(rawPaymentDate);
      if (!monthDay) return;
      for (let year = purchaseDate.getFullYear() - 1; year <= refundDateObj.getFullYear() + 1; year++) {
        const candidate = new Date(year, monthDay.month - 1, monthDay.day);
        if (candidate.getTime() <= purchaseDate.getTime()) continue;
        if (candidate.getTime() > refundDateObj.getTime()) continue;
        if (!allPaymentDates.some((item) => item.getTime() === candidate.getTime())) {
          allPaymentDates.push(candidate);
        }
      }
    });

    // Some uploaded metadata puts the final maturity only in "Maturity date"
    // and not in the recurring "Payment date" rows. Sheet 6 still needs that
    // last coupon/principal row, so include maturity as a final schedule point.
    if (
      maturityDateObj &&
      maturityDateObj.getTime() > purchaseDate.getTime() &&
      maturityDateObj.getTime() <= refundDateObj.getTime() &&
      !allPaymentDates.some((item) => item.getTime() === maturityDateObj.getTime())
    ) {
      allPaymentDates.push(new Date(maturityDateObj));
    }

    return allPaymentDates.sort((a, b) => a.getTime() - b.getTime());
  };
  const SHEET6_GSB_ABS_START_DATE = new Date(2025, 4, 1);
  const adjustSheet6PurchaseDateForBond = (
    displayBondName: string,
    purchaseDateObj: Date,
  ): Date => {
    if (
      displayBondName === "GSB-ABS ХБҮЦ" &&
      purchaseDateObj.getTime() < SHEET6_GSB_ABS_START_DATE.getTime()
    ) {
      return new Date(SHEET6_GSB_ABS_START_DATE);
    }
    return new Date(purchaseDateObj);
  };
  const purchaseHistoryAmountByEmailDate = new Map<string, number>();
  const purchaseHistoryBondByEmailDate = new Map<string, number>();
  const baseBondRatioByPortfolioIndex = new Map<number, number>();
  const portfolioRatioCounts = new Map<number, Map<number, number>>();
  purchaseHistoryData.forEach((row) => {
    const email = String(row.email || "").toLowerCase().trim();
    const parsedDate = parseFlexibleDate(row.haDate || "");
    if (!email.includes("@") || !parsedDate) return;
    const key = `${email}|${toDateKey(parsedDate)}`;
    purchaseHistoryAmountByEmailDate.set(key, safeNumber(row.amount));
    purchaseHistoryBondByEmailDate.set(key, safeNumber(row.bond));

    const amount = safeNumber(row.amount);
    const bond = safeNumber(row.bond);
    const portfolioIndex = Number(row.portfolioIndex) || 0;
    if (portfolioIndex > 0 && amount > 0 && bond > 0) {
      const ratio = roundTo2(bond / amount);
      if (ratio > 0 && ratio <= 1) {
        if (!portfolioRatioCounts.has(portfolioIndex)) {
          portfolioRatioCounts.set(portfolioIndex, new Map<number, number>());
        }
        const counts = portfolioRatioCounts.get(portfolioIndex)!;
        counts.set(ratio, (counts.get(ratio) || 0) + 1);
      }
    }
  });
  portfolioRatioCounts.forEach((counts, portfolioIndex) => {
    let bestRatio = 0;
    let bestCount = -1;
    counts.forEach((count, ratio) => {
      if (count > bestCount) {
        bestCount = count;
        bestRatio = ratio;
      }
    });
    if (bestRatio > 0) {
      baseBondRatioByPortfolioIndex.set(portfolioIndex, bestRatio);
    }
  });
  const starterPurchaseDateOverridesByEmail = new Map<string, { bond: number; date: string; parsedDate: Date }[]>();
  balanceHistoryRows.forEach((row) => {
    const email = String(row.email || "").toLowerCase().trim();
    const parsedDate = parseFlexibleDateTime(row.createdAt || row.purchaseDate || "") || parseFlexibleDate(row.purchaseDate || row.createdAt || "");
    const investedAmount = safeNumber(row.amount);
    const bondAmount = safeNumber(row.bondAmount);
    if (!email.includes("@") || !parsedDate || investedAmount <= 0 || bondAmount <= 0) return;
    if (!starterPurchaseDateOverridesByEmail.has(email)) starterPurchaseDateOverridesByEmail.set(email, []);
    starterPurchaseDateOverridesByEmail.get(email)!.push({
      bond: bondAmount,
      date: row.createdAt || row.purchaseDate || "",
      parsedDate,
    });
  });
  starterPurchaseDateOverridesByEmail.forEach((rows) => {
    rows.sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
  });
  const resolveStarterPurchaseDate = (email: string, amount: number, fallbackDate: string): string => {
    const fallbackParsed = parseFlexibleDate(fallbackDate);
    const candidates = starterPurchaseDateOverridesByEmail.get(email.toLowerCase().trim()) || [];
    const exactCandidates = candidates.filter((candidate) => Math.abs(candidate.bond - safeNumber(amount)) <= 1);
    if (exactCandidates.length === 0) return "";
    const matched = exactCandidates.reduce((best, candidate) => {
      if (!best) return candidate;
      const bestDateGap = fallbackParsed ? Math.abs(best.parsedDate.getTime() - fallbackParsed.getTime()) : 0;
      const candidateDateGap = fallbackParsed ? Math.abs(candidate.parsedDate.getTime() - fallbackParsed.getTime()) : 0;
      return candidateDateGap < bestDateGap ? candidate : best;
    }, exactCandidates[0]);
    return matched?.date || "";
  };

  const isAvtamataFeeCharge = (description: string): boolean => {
    const normalizedDescription = String(description || "").toLowerCase();
    return normalizedDescription.includes("автамата") ||
      normalizedDescription.includes("avta") ||
      normalizedDescription.includes("шимтгэл") ||
      normalizedDescription.includes("fee");
  };

  const chargeByEmailDateForSheet6: Record<string, number> = {};
  accumulatedChargeSheets.forEach((sheet) => {
    sheet.data.forEach((charge) => {
      const email = String(charge.email || "").toLowerCase().trim();
      if (!email.includes("@")) return;
      if (!isAvtamataFeeCharge(charge.description || "")) return;
      const chargeDate = parseFlexibleDate(charge.haDate || "");
      if (!chargeDate) return;
      const key = `${email}|${toDateKey(chargeDate)}`;
      chargeByEmailDateForSheet6[key] = (chargeByEmailDateForSheet6[key] || 0) + safeNumber(charge.amount);
    });
  });

  const sourcePurchaseDateOverridesByEmail = new Map<string, { bond: number; date: string; parsedDate: Date; used: boolean }[]>();
  purchaseHistoryData.forEach((row) => {
    const email = String(row.email || "").toLowerCase().trim();
    const parsedDate = parseFlexibleDateTime(row.haDate || "") || parseFlexibleDate(row.haDate || "");
    const bondAmount = safeNumber(row.bond);
    if (!email.includes("@") || !parsedDate || bondAmount <= 0) return;
    if (!sourcePurchaseDateOverridesByEmail.has(email)) {
      sourcePurchaseDateOverridesByEmail.set(email, []);
    }
    sourcePurchaseDateOverridesByEmail.get(email)!.push({
      bond: bondAmount,
      date: row.haDate || "",
      parsedDate,
      used: false,
    });
  });
  sourcePurchaseDateOverridesByEmail.forEach((rows) => {
    rows.sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
  });
  const resolveSourcePurchaseDate = (email: string, amount: number, fallbackDate: string): string => {
    const fallbackParsed = parseFlexibleDateTime(fallbackDate) || parseFlexibleDate(fallbackDate);
    const candidates: { bond: number; date: string; parsedDate: Date; used: boolean }[] =
      sourcePurchaseDateOverridesByEmail.get(email.toLowerCase().trim()) || [];
    if (candidates.length === 0) return fallbackDate;

    const exactCandidates = candidates.filter((candidate) => Math.abs(candidate.bond - safeNumber(amount)) <= 1);
    if (exactCandidates.length > 0) {
      const matchedExact = exactCandidates.reduce((best, candidate) => {
        if (!best) return candidate;
        const bestDateGap = fallbackParsed ? Math.abs(best.parsedDate.getTime() - fallbackParsed.getTime()) : 0;
        const candidateDateGap = fallbackParsed ? Math.abs(candidate.parsedDate.getTime() - fallbackParsed.getTime()) : 0;
        return candidateDateGap < bestDateGap ? candidate : best;
      }, exactCandidates[0]);
      matchedExact.used = true;
      return matchedExact.date || fallbackDate;
    }

    const available = candidates.filter((candidate) => !candidate.used);
    const pool = available.length > 0 ? available : candidates;
    let bestIndex = -1;
    let bestScore = Number.POSITIVE_INFINITY;
    pool.forEach((candidate, index) => {
      const amountGap = Math.abs(candidate.bond - safeNumber(amount));
      const dateGap = fallbackParsed ? Math.abs(candidate.parsedDate.getTime() - fallbackParsed.getTime()) : 0;
      const score = amountGap * 1000 + dateGap / (1000 * 60 * 60 * 24);
      if (score < bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    });
    if (bestIndex >= 0) {
      const matchedCandidate = pool[bestIndex];
      matchedCandidate.used = true;
      return matchedCandidate.date || fallbackDate;
    }
    return fallbackDate;
  };

  const destinationRowsByEmail = new Map<string, { dateObj: Date; dateStr: string; amount: number; bondName: string; isTopUpPurchase: boolean }[]>();
  const findDestinationBondName = (email: string, purchaseDate: Date): string => {
    let bestName = "";
    let bestDiff = Number.POSITIVE_INFINITY;
    rowsToFilter.forEach((row) => {
      const rowEmail = String(row.email || "").toLowerCase().trim();
      if (rowEmail !== email.toLowerCase().trim()) return;
      const rowPurchaseDate =
        parseFlexibleDateTime(row.sourcePurchaseDateTime || row.sourcePurchaseDate || row.purchaseDate || "") ||
        parseFlexibleDate(row.sourcePurchaseDate || row.purchaseDate || "");
      if (!rowPurchaseDate) return;
      const diff = Math.abs(rowPurchaseDate.getTime() - purchaseDate.getTime());
      if (diff < bestDiff) {
        bestDiff = diff;
        bestName = displayBondNameForSheet6(row.name || "", rowPurchaseDate);
      }
    });
    return bestName;
  };
  const isSheet6DestinationTag = (tag: string): boolean => {
    const normalizedTag = String(tag || "").trim();
    return normalizedTag === "Бондын дахин худалдан авалт" ||
      normalizedTag === "Бондын зөрүүтэй";
  };
  balanceHistoryRows.forEach((row) => {
    const email = String(row.email || "").toLowerCase().trim();
    const parsedDate =
      parseFlexibleDateTime(row.createdAt || row.purchaseDate || "") ||
      parseFlexibleDate(row.purchaseDate || row.createdAt || "");
    const bondAmount = safeNumber(row.bondAmount);
    if (!email.includes("@") || !parsedDate || bondAmount <= 0) return;
    const portfolioIndex = Number(row.portfolioIndex) || 0;
    const baseBondRatio = baseBondRatioByPortfolioIndex.get(portfolioIndex) || 0;
    const investedAmount = safeNumber(row.amount);
    const holdAmount = safeNumber(row.holdAmount);
    const carryAmount = investedAmount > 0
      ? roundTo2(Math.max(0, bondAmount - (investedAmount * baseBondRatio)))
      : roundTo2(Math.max(0, bondAmount - holdAmount));
    if (carryAmount <= 0) return;
    if (!destinationRowsByEmail.has(email)) destinationRowsByEmail.set(email, []);
    destinationRowsByEmail.get(email)!.push({
      dateObj: parsedDate,
      dateStr: row.createdAt || row.purchaseDate || "",
      amount: carryAmount,
      bondName: findDestinationBondName(email, parsedDate),
      isTopUpPurchase: investedAmount > 0,
    });
  });
  destinationRowsByEmail.forEach((rows) => {
    rows.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  });
  const requestedBondSourcesByEmail = new Map<string, { purchaseDateObj: Date; normalizedBondName: string }[]>();
  bondRows.forEach((row) => {
    const email = String(row.email || "").toLowerCase().trim();
    const purchaseDateObj =
      parseFlexibleDateTime(row.sourcePurchaseDateTime || row.sourcePurchaseDate || row.purchaseDate || "") ||
      parseFlexibleDate(row.sourcePurchaseDateTime || row.sourcePurchaseDate || row.purchaseDate || "");
    const normalizedBondName = normalizeBondName(row.name || "");
    if (!email.includes("@") || !purchaseDateObj || !normalizedBondName) return;
    if (!requestedBondSourcesByEmail.has(email)) requestedBondSourcesByEmail.set(email, []);
    requestedBondSourcesByEmail.get(email)!.push({ purchaseDateObj, normalizedBondName });
  });
  const isExplicitlyRequestedSource = (email: string, purchaseDateObj: Date, normalizedBondName: string): boolean => {
    const requested = requestedBondSourcesByEmail.get(email.toLowerCase().trim()) || [];
    return requested.some((item) =>
      item.normalizedBondName === normalizedBondName &&
      Math.abs(item.purchaseDateObj.getTime() - purchaseDateObj.getTime()) <= 24 * 60 * 60 * 1000
    );
  };
  const findRequestedSourceHint = (email: string, normalizedBondName: string, fallbackDate?: Date | null): Date | null => {
    const requested = requestedBondSourcesByEmail.get(email.toLowerCase().trim()) || [];
    const sameBond = requested.filter((item) => item.normalizedBondName === normalizedBondName);
    if (sameBond.length === 0) return null;
    if (!fallbackDate) return sameBond[0].purchaseDateObj;
    return sameBond.reduce((best, item) => {
      const bestGap = Math.abs(best.purchaseDateObj.getTime() - fallbackDate.getTime());
      const itemGap = Math.abs(item.purchaseDateObj.getTime() - fallbackDate.getTime());
      return itemGap < bestGap ? item : best;
    }, sameBond[0]).purchaseDateObj;
  };
  const buildSourceRowKey = (email: string, purchaseDateObj: Date, bondName: string): string =>
    `${email.toLowerCase().trim()}|${toDateTimeKey(purchaseDateObj)}|${normalizeBondName(bondName.replace("Нео бонд ", "Нео "))}`;
  type CompletedRefundBondMatch = {
    matchKey: string;
    normalizedBondName: string;
    amount: number;
    purchasedAtKey: string;
  };
  const completedRefundBondsByEmail = new Map<string, CompletedRefundBondMatch[]>();
  const activeRefundBondsByEmail = new Map<string, CompletedRefundBondMatch[]>();
  Object.entries(refundHistoryMap).forEach(([email, entries]) => {
    const emailKey = normalizeEmailKey(email);
    const completedBonds: CompletedRefundBondMatch[] = [];
    const activeBonds: CompletedRefundBondMatch[] = [];
    entries.forEach((entry, entryIndex) => {
      const status = (entry.status || "").toLowerCase();
      const targetList =
        status === "done"
          ? completedBonds
          : status && status !== "canceled"
            ? activeBonds
            : null;
      if (!targetList) return;
      (entry.bonds || []).forEach((bond, bondIndex) => {
        const normalizedBondName = normalizeBondName(String(bond.name || ""));
        const purchasedAtKey = normalizeDateKey(String(bond.purchasedAt || ""));
        const amount = safeNumber(bond.amount);
        if (!normalizedBondName || !purchasedAtKey || amount <= 0) return;
        targetList.push({
          matchKey: `${emailKey}|${entryIndex}|${bondIndex}`,
          normalizedBondName,
          amount,
          purchasedAtKey,
        });
      });
    });
    if (completedBonds.length > 0) {
      completedRefundBondsByEmail.set(emailKey, completedBonds);
    }
    if (activeBonds.length > 0) {
      activeRefundBondsByEmail.set(emailKey, activeBonds);
    }
  });
  const usedCompletedRefundBondKeys = new Set<string>();
  const findCompletedRefundBondMatch = (row: BondResultRow): string | null => {
    const emailKey = normalizeEmailKey(row.email || "");
    const purchasedAtKey = normalizeDateKey(String(row.sourcePurchaseDate || row.purchaseDate || ""));
    const normalizedBondName = normalizeBondName(String(row.name || ""));
    const amount = safeNumber(row.amount);
    if (!emailKey || !purchasedAtKey || !normalizedBondName || amount <= 0) return null;

    const activeBonds = activeRefundBondsByEmail.get(emailKey) || [];
    const hasActiveMatch = activeBonds.some((entry) =>
      entry.normalizedBondName === normalizedBondName &&
      entry.purchasedAtKey === purchasedAtKey &&
      Math.abs(entry.amount - amount) <= 1,
    );
    if (hasActiveMatch) return null;

    const completedBonds = completedRefundBondsByEmail.get(emailKey) || [];
    const matched = completedBonds.find((entry) =>
      !usedCompletedRefundBondKeys.has(entry.matchKey) &&
      entry.normalizedBondName === normalizedBondName &&
      entry.purchasedAtKey === purchasedAtKey &&
      Math.abs(entry.amount - amount) <= 1,
    );
    return matched?.matchKey || null;
  };

  type ExpandedBondRepurchaseRow = {
    email: string;
    phone: string;
    lastName: string;
    firstName: string;
    regNumber: string;
    bankAccount: string;
    bank: string;
    txCode: string;
    ttd: string;
    displayBondName: string;
    tailbarText: string;
    amount: number;
    bondRate: number;
    purchaseDate: string;
    interestEndDate: string;
    purchaseDateObj: Date;
    interestEndDateObj: Date;
    sourcePurchaseDateObj: Date;
    days: number;
    calculatedInterest: number;
    totalPrice: number;
    baseAvtamataFee: number;
    avtamataFee: number;
    ariljaaFee: number;
    oat: number | string;
    transferAmount: number;
    sourcePurchaseKey: string;
    sourceRowKey: string;
    sourceNormalizedBondName: string;
    isExplicitlyRequested: boolean;
    isMaturityRow: boolean;
    isGsbAbsHbuc: boolean;
    revisedTailbar?: string;
  };
  const buildSheet6Amounts = (
    principalAmount: number,
    rate: number,
    days: number,
    baseAvtamataFee: number,
    isMaturityRow: boolean,
    isGsbAbsHbuc: boolean,
  ) => {
    const calculatedInterest = roundTo2((principalAmount * rate * days) / 365);
    let totalPrice = calculatedInterest;
    let tradingFee = 0;
    let oat: number | string = roundTo2(calculatedInterest * 0.10);
    let effectiveAvtamataFee = baseAvtamataFee;

    if (isMaturityRow) {
      totalPrice = roundTo2(principalAmount + calculatedInterest);
      if (isGsbAbsHbuc) {
        tradingFee = roundTo2(totalPrice * 0.002);
        oat = "";
        effectiveAvtamataFee = 0;
      }
    }

    return {
      calculatedInterest,
      totalPrice,
      avtamataFee: effectiveAvtamataFee,
      ariljaaFee: tradingFee,
      oat,
      transferAmount: roundTo2(totalPrice - effectiveAvtamataFee - tradingFee - (typeof oat === "number" ? oat : 0)),
    };
  };

  const expandedRowsByEmail = new Map<string, ExpandedBondRepurchaseRow[]>();

  // Revert to the older stable Sheet 6 source ordering:
  // selected customer order first, then purchase date, then bond name.
  const userOrder = new Map<string, number>();
  filteredBondRetakeRows.forEach((row) => {
    const email = (row.email || "").toLowerCase().trim();
    if (!userOrder.has(email)) {
      userOrder.set(email, userOrder.size);
    }
  });

  const sortedBondRetakeRows = [...filteredBondRetakeRows].sort((a, b) => {
    const emailA = (a.email || "").toLowerCase().trim();
    const emailB = (b.email || "").toLowerCase().trim();
    const userIdxA = userOrder.get(emailA) ?? Number.MAX_SAFE_INTEGER;
    const userIdxB = userOrder.get(emailB) ?? Number.MAX_SAFE_INTEGER;
    if (userIdxA !== userIdxB) return userIdxA - userIdxB;

    const purchaseA =
      parseFlexibleDateTime(a.sourcePurchaseDateTime || a.sourcePurchaseDate || a.purchaseDate || "") ||
      parseFlexibleDate(a.sourcePurchaseDateTime || a.sourcePurchaseDate || a.purchaseDate || "");
    const purchaseB =
      parseFlexibleDateTime(b.sourcePurchaseDateTime || b.sourcePurchaseDate || b.purchaseDate || "") ||
      parseFlexibleDate(b.sourcePurchaseDateTime || b.sourcePurchaseDate || b.purchaseDate || "");
    if (purchaseA && purchaseB && purchaseA.getTime() !== purchaseB.getTime()) {
      return purchaseA.getTime() - purchaseB.getTime();
    }
    if (purchaseA && !purchaseB) return -1;
    if (!purchaseA && purchaseB) return 1;

    return (a.name || "").localeCompare(b.name || "");
  });

  const refundDateObj = parseFlexibleDate(refundDate) || cutoffDate;

  sortedBondRetakeRows.forEach((b) => {
    const revisedRow = b as BondRetakeRowWithTailbar;
    const email = String(b.email || "").toLowerCase().trim();
    if (!email.includes("@")) return;

    const sourceNormalizedBondName = normalizeBondName(b.name || "");
    const fallbackRawPurchaseDate = b.sourcePurchaseDate || b.purchaseDate || "";
    const fallbackPurchaseDateObj =
      parseFlexibleDateTime(b.sourcePurchaseDateTime || fallbackRawPurchaseDate) ||
      parseFlexibleDate(fallbackRawPurchaseDate);
    const requestedSourceHint = findRequestedSourceHint(email, sourceNormalizedBondName, fallbackPurchaseDateObj);
    const starterSourceDate = resolveStarterPurchaseDate(
      email,
      safeNumber(b.amount),
      requestedSourceHint ? toDateKey(requestedSourceHint) : fallbackRawPurchaseDate,
    );
    const rawPurchaseDate = starterSourceDate || resolveSourcePurchaseDate(
      email,
      safeNumber(b.amount),
      requestedSourceHint ? toDateKey(requestedSourceHint) : fallbackRawPurchaseDate,
    );
    const purchaseDateObj =
      parseFlexibleDateTime(b.sourcePurchaseDateTime || rawPurchaseDate) ||
      parseFlexibleDate(rawPurchaseDate);
    if (!purchaseDateObj) return;

    const initialDisplayName = displayBondNameForSheet6(b.name || "", purchaseDateObj);
    const effectivePurchaseDateObj = adjustSheet6PurchaseDateForBond(
      initialDisplayName,
      purchaseDateObj,
    );
    const bondMeta = getBondMetaForSheet6(b.name || "", initialDisplayName);
    const paymentDates = bondMeta?.paymentDates || [];
    const maturityDateObj = bondMeta?.maturityDate ? parseFlexibleDate(bondMeta.maturityDate) : null;
    const distributionFallbackSchedule = getDistributionFallbackScheduleForSheet6(
      getSheet6DistributionCandidateNames(
        initialDisplayName,
        sourceNormalizedBondName,
      ),
      b.regNumber || "",
      effectivePurchaseDateObj,
      refundDateObj,
    );
    let schedule = buildPaymentScheduleForSheet6(
      b.name || "",
      effectivePurchaseDateObj,
      paymentDates,
      refundDateObj,
      maturityDateObj,
    );
    if (
      distributionFallbackSchedule.length > 0 &&
      shouldPreferDistributionScheduleForSheet6(
        b.name || "",
        initialDisplayName,
      )
    ) {
      schedule = distributionFallbackSchedule;
    } else if (schedule.length === 0) {
      schedule = distributionFallbackSchedule;
    }
    if (schedule.length === 0) return;

    const sourcePurchaseKey = `${email}|${toDateKey(purchaseDateObj)}`;
    const sourceRowKey = buildSourceRowKey(email, purchaseDateObj, sourceNormalizedBondName);
    const isExplicitlyRequested = isExplicitlyRequestedSource(email, purchaseDateObj, sourceNormalizedBondName);
    let currentStartDate = new Date(effectivePurchaseDateObj);
    schedule.forEach((paymentDate, index) => {
      const days = diffDays(currentStartDate, paymentDate);
      if (days <= 0) {
        currentStartDate = new Date(paymentDate);
        return;
      }

      const displayBondName = displayBondNameForSheet6(b.name || "", currentStartDate);
      const periodMeta = getBondMetaForSheet6(b.name || "", displayBondName);
      const rate = displayBondName === "Гялс төгрөг"
        ? 0.18
        : (safeNumber(periodMeta?.rate) || safeNumber(b.bondRate) || 0);

      let principalAmount = safeNumber(b.amount);
      const isGsbAbsHbuc = displayBondName === "GSB-ABS ХБҮЦ";
      if (isGsbAbsHbuc) {
        principalAmount = roundTo2(principalAmount * 0.998);
      }

      const isMaturityRow = displayBondName !== "Гялс төгрөг" && (
        (maturityDateObj ? sameDate(paymentDate, maturityDateObj) : false) ||
        (isGsbAbsHbuc && index === schedule.length - 1)
      );

      const chargeKey = `${email}|${toDateKey(paymentDate)}`;
      const baseAvtamataFee = roundTo2(chargeByEmailDateForSheet6[chargeKey] || 0);
      const computedAmounts = buildSheet6Amounts(
        principalAmount,
        rate,
        days,
        baseAvtamataFee,
        isMaturityRow,
        isGsbAbsHbuc,
      );
      const tailbarText = isGsbAbsHbuc
        ? "GSB-ABS-н үндсэн дүн, хүү төлөлт"
        : isMaturityRow
          ? `${displayBondName}-н үндсэн дүн, хүү төлөлт`
        : `${displayBondName}-н хүү төлөлт`;

      const expandedRow: ExpandedBondRepurchaseRow = {
        email: b.email || "",
        phone: b.phone || "",
        lastName: b.lastName || "",
        firstName: b.firstName || "",
        regNumber: b.regNumber || "",
        bankAccount: b.bankAccount || "",
        bank: b.bank || "",
        txCode: b.txCode || "",
        ttd: b.ttd || "",
        displayBondName,
        tailbarText,
        amount: roundTo2(principalAmount),
        bondRate: rate,
        purchaseDate: formatSheet6Date(toDateKey(currentStartDate)),
        interestEndDate: formatSheet6Date(toDateKey(paymentDate)),
        purchaseDateObj: new Date(currentStartDate),
        interestEndDateObj: new Date(paymentDate),
        sourcePurchaseDateObj: new Date(purchaseDateObj),
        days,
        calculatedInterest: computedAmounts.calculatedInterest,
        totalPrice: computedAmounts.totalPrice,
        baseAvtamataFee,
        avtamataFee: computedAmounts.avtamataFee,
        ariljaaFee: computedAmounts.ariljaaFee,
        oat: computedAmounts.oat,
        transferAmount: computedAmounts.transferAmount,
        sourcePurchaseKey,
        sourceRowKey,
        sourceNormalizedBondName,
        isExplicitlyRequested,
        isMaturityRow,
        isGsbAbsHbuc,
        revisedTailbar: revisedRow.tailbar,
      };

      if (!expandedRowsByEmail.has(email)) expandedRowsByEmail.set(email, []);
      expandedRowsByEmail.get(email)!.push(expandedRow);

      currentStartDate = new Date(paymentDate);
    });
  });

  const areSameSheet6BondFamily = (leftBondName: string, rightBondName: string): boolean => {
    const left = normalizeBondName(leftBondName || "");
    const right = normalizeBondName(rightBondName || "");
    if (!left || !right) return false;
    if (left === right) return true;
    if (left.startsWith("Нөмөр") && right.startsWith("Нөмөр")) return true;
    if (left.startsWith("Шунхлай") && right.startsWith("Шунхлай")) return true;
    if (left.startsWith("Нео ") && right.startsWith("Нео ")) return true;
    return false;
  };
  const getExistingExpandedRowsForReg = (regNumber: string): ExpandedBondRepurchaseRow[] => {
    const regKey = String(regNumber || "").trim().toUpperCase();
    if (!regKey) return [];
    return Array.from(expandedRowsByEmail.values()).flat().filter(
      (row) => String(row.regNumber || "").trim().toUpperCase() === regKey
    );
  };
  const findSupplementalSourceRowForDistribution = (
    distributionRow: BondDistributionRow,
  ): BondResultRow | null => {
    const regKey = String(distributionRow.regNumber || "").trim().toUpperCase();
    const distributionPurchaseDate =
      parseFlexibleDateTime(distributionRow.purchaseDate || "") ||
      parseFlexibleDate(distributionRow.purchaseDate || "");
    if (!regKey || !distributionPurchaseDate) return null;

    const candidates = rowsToFilter.filter((sourceRow) => {
      const sourceReg = String(sourceRow.regNumber || "").trim().toUpperCase();
      if (sourceReg !== regKey) return false;
      const sourcePurchaseDate =
        parseFlexibleDateTime(
          sourceRow.sourcePurchaseDateTime ||
            sourceRow.sourcePurchaseDate ||
            sourceRow.purchaseDate ||
            ""
        ) ||
        parseFlexibleDate(
          sourceRow.sourcePurchaseDateTime ||
            sourceRow.sourcePurchaseDate ||
            sourceRow.purchaseDate ||
            ""
        );
      if (!sourcePurchaseDate) return false;
      const dateGapDays = Math.abs(
        diffDays(
          new Date(Math.min(sourcePurchaseDate.getTime(), distributionPurchaseDate.getTime())),
          new Date(Math.max(sourcePurchaseDate.getTime(), distributionPurchaseDate.getTime()))
        )
      );
      if (dateGapDays > 1) return false;
      if (
        !isWithinSheet6DistributionTolerance(
          safeNumber(sourceRow.amount),
          safeNumber(distributionRow.amount),
        )
      ) {
        return false;
      }
      return true;
    });
    if (candidates.length === 0) return null;

    const targetBondName = normalizeBondName(distributionRow.bondName || "");
    const ranked = [...candidates].sort((a, b) => {
      const familyA = areSameSheet6BondFamily(a.name || "", targetBondName) ? 0 : 1;
      const familyB = areSameSheet6BondFamily(b.name || "", targetBondName) ? 0 : 1;
      if (familyA !== familyB) return familyA - familyB;
      const amountGapA = Math.abs(safeNumber(a.amount) - safeNumber(distributionRow.amount));
      const amountGapB = Math.abs(safeNumber(b.amount) - safeNumber(distributionRow.amount));
      if (amountGapA !== amountGapB) return amountGapA - amountGapB;
      const dateA =
        parseFlexibleDateTime(a.sourcePurchaseDateTime || a.sourcePurchaseDate || a.purchaseDate || "") ||
        parseFlexibleDate(a.sourcePurchaseDate || a.purchaseDate || "");
      const dateB =
        parseFlexibleDateTime(b.sourcePurchaseDateTime || b.sourcePurchaseDate || b.purchaseDate || "") ||
        parseFlexibleDate(b.sourcePurchaseDate || b.purchaseDate || "");
      if (dateA && dateB) {
        return (
          Math.abs(dateA.getTime() - distributionPurchaseDate.getTime()) -
          Math.abs(dateB.getTime() - distributionPurchaseDate.getTime())
        );
      }
      return 0;
    });
    return ranked[0] || null;
  };
  bondDistributionFiles.forEach((file) => {
    file.data.forEach((distributionRow) => {
      const regKey = String(distributionRow.regNumber || "").trim().toUpperCase();
      const purchaseDateObj =
        parseFlexibleDateTime(distributionRow.purchaseDate || "") ||
        parseFlexibleDate(distributionRow.purchaseDate || "");
      const interestEndDateObj =
        parseFlexibleDateTime(distributionRow.interestEndDate || "") ||
        parseFlexibleDate(distributionRow.interestEndDate || "");
      if (!regKey || !purchaseDateObj || !interestEndDateObj) return;

      const displayBondName = displayBondNameForSheet6(
        distributionRow.bondName || file.bondName || "",
        purchaseDateObj,
      );
      const existingRows = getExistingExpandedRowsForReg(regKey);
      const alreadyExists = existingRows.some((row) =>
        areSameSheet6BondFamily(row.displayBondName, displayBondName) &&
        normalizeDateKey(row.purchaseDate || "") === toDateKey(purchaseDateObj) &&
        normalizeDateKey(row.interestEndDate || "") === toDateKey(interestEndDateObj)
      );
      if (alreadyExists) return;

      const sourceRow = findSupplementalSourceRowForDistribution(distributionRow);
      if (!sourceRow) return;

      const email = String(sourceRow.email || "").toLowerCase().trim();
      if (!email.includes("@")) return;
      const sourcePurchaseDateObj =
        parseFlexibleDateTime(
          sourceRow.sourcePurchaseDateTime ||
            sourceRow.sourcePurchaseDate ||
            sourceRow.purchaseDate ||
            ""
        ) ||
        parseFlexibleDate(
          sourceRow.sourcePurchaseDateTime ||
            sourceRow.sourcePurchaseDate ||
            sourceRow.purchaseDate ||
            ""
        );
      if (!sourcePurchaseDateObj) return;

      const effectivePurchaseDateObj = adjustSheet6PurchaseDateForBond(
        displayBondName,
        purchaseDateObj,
      );
      const days = diffDays(effectivePurchaseDateObj, interestEndDateObj);
      if (days <= 0) return;
      const periodMeta = getBondMetaForSheet6(sourceRow.name || "", displayBondName);
      const rate =
        safeNumber(distributionRow.rate) ||
        safeNumber(periodMeta?.rate) ||
        safeNumber(sourceRow.bondRate) ||
        0;
      const principalAmount = roundTo2(Math.min(
        safeNumber(sourceRow.amount) || safeNumber(distributionRow.amount),
        Math.max(safeNumber(sourceRow.amount), 0) || safeNumber(distributionRow.amount),
      ));
      const baseAvtamataFee = 0;
      const isGsbAbsHbuc = displayBondName === "GSB-ABS ХБҮЦ";
      const isMaturityRow = false;
      const computedAmounts = buildSheet6Amounts(
        principalAmount,
        rate,
        days,
        baseAvtamataFee,
        isMaturityRow,
        isGsbAbsHbuc,
      );
      const tailbarText = `${displayBondName}-н хүү төлөлт`;
      const sourceNormalizedBondName = normalizeBondName(sourceRow.name || "");
      const sourceRowKey = buildSourceRowKey(email, sourcePurchaseDateObj, sourceNormalizedBondName);
      const sourcePurchaseKey = `${email}|${toDateKey(sourcePurchaseDateObj)}`;

      const supplementalRow: ExpandedBondRepurchaseRow = {
        email: sourceRow.email || "",
        phone: sourceRow.phone || "",
        lastName: sourceRow.lastName || "",
        firstName: sourceRow.firstName || "",
        regNumber: sourceRow.regNumber || "",
        bankAccount: sourceRow.bankAccount || "",
        bank: sourceRow.bank || "",
        txCode: sourceRow.txCode || "",
        ttd: sourceRow.ttd || "",
        displayBondName,
        tailbarText,
        amount: principalAmount,
        bondRate: rate,
        purchaseDate: formatSheet6Date(toDateKey(effectivePurchaseDateObj)),
        interestEndDate: formatSheet6Date(toDateKey(interestEndDateObj)),
        purchaseDateObj: new Date(effectivePurchaseDateObj),
        interestEndDateObj: new Date(interestEndDateObj),
        sourcePurchaseDateObj: new Date(sourcePurchaseDateObj),
        days,
        calculatedInterest: computedAmounts.calculatedInterest,
        totalPrice: computedAmounts.totalPrice,
        baseAvtamataFee,
        avtamataFee: computedAmounts.avtamataFee,
        ariljaaFee: computedAmounts.ariljaaFee,
        oat: computedAmounts.oat,
        transferAmount: computedAmounts.transferAmount,
        sourcePurchaseKey,
        sourceRowKey,
        sourceNormalizedBondName,
        isExplicitlyRequested: false,
        isMaturityRow,
        isGsbAbsHbuc,
      };
      if (!expandedRowsByEmail.has(email)) expandedRowsByEmail.set(email, []);
      expandedRowsByEmail.get(email)!.push(supplementalRow);
    });
  });

  type SuppressedSheet6Row = {
    email: string;
    lastName: string;
    firstName: string;
    regNumber: string;
    bondName: string;
    purchaseDate: string;
    interestEndDate: string;
    amount: number;
    sourcePurchaseKey: string;
    sourceRowKey: string;
    sheet5Comment: string;
    workerComment: string;
  };
  type CappedSheet6Row = {
    email: string;
    sourcePurchaseKey: string;
    sourceRowKey: string;
    originalAmount: number;
    cappedAmount: number;
  };
  const suppressedSheet6Rows: SuppressedSheet6Row[] = [];
  const cappedSheet6Rows: CappedSheet6Row[] = [];
  const cappedSheet6RowKeys = new Set<string>();
  const buildExpandedSheet6RowId = (row: ExpandedBondRepurchaseRow): string =>
    [
      row.email,
      row.sourceRowKey,
      normalizeBondName(row.displayBondName),
      normalizeDateKey(row.purchaseDate || ""),
      normalizeDateKey(row.interestEndDate || ""),
    ].join("|");
  const recordCappedSheet6Row = (
    row: ExpandedBondRepurchaseRow,
    originalAmount: number,
    cappedAmount: number,
  ) => {
    const key = [
      row.email,
      row.sourcePurchaseKey,
      row.sourceRowKey,
      roundTo2(originalAmount),
      roundTo2(cappedAmount),
    ].join("|");
    if (cappedSheet6RowKeys.has(key)) return;
    cappedSheet6RowKeys.add(key);
    cappedSheet6Rows.push({
      email: row.email,
      sourcePurchaseKey: row.sourcePurchaseKey,
      sourceRowKey: row.sourceRowKey,
      originalAmount: roundTo2(originalAmount),
      cappedAmount: roundTo2(cappedAmount),
    });
  };
  const applyDistributionCapToSheet6Row = (
    row: ExpandedBondRepurchaseRow,
    distributionAmount: number,
  ) => {
    const cappedAmount = roundTo2(distributionAmount);
    const currentAmount = roundTo2(row.amount);
    if (cappedAmount <= 0 || cappedAmount >= currentAmount) return;
    recordCappedSheet6Row(row, currentAmount, cappedAmount);
    const recomputed = buildSheet6Amounts(
      cappedAmount,
      row.bondRate,
      row.days,
      row.baseAvtamataFee,
      row.isMaturityRow,
      row.isGsbAbsHbuc,
    );
    row.amount = cappedAmount;
    row.calculatedInterest = recomputed.calculatedInterest;
    row.totalPrice = recomputed.totalPrice;
    row.avtamataFee = recomputed.avtamataFee;
    row.ariljaaFee = recomputed.ariljaaFee;
    row.oat = recomputed.oat;
    row.transferAmount = recomputed.transferAmount;
  };
  type AssignedSheet6DistributionMatch = {
    amount: number;
    shouldCap: boolean;
  };
  const assignedDistributionAmountBySheet6RowId = new Map<
    string,
    AssignedSheet6DistributionMatch
  >();
  const quarterRowExistsBySheet6RowId = new Map<string, boolean>();
  const regOnlyDistributionWarningBySheet6RowId = new Set<string>();
  const buildSheet6DistributionRegGroupKey = (
    row: ExpandedBondRepurchaseRow,
    candidateQuarterKeys?: string[],
  ): string => {
    const interestEndDateKey = normalizeDateKey(row.interestEndDate || "");
    const regNumberKey = String(row.regNumber || "").trim().toUpperCase();
    const quarterKeys =
      candidateQuarterKeys ||
      getSheet6DistributionCandidateNames(
        row.displayBondName,
        row.sourceNormalizedBondName,
      )
        .map((bondName) => `${bondName}|${interestEndDateKey}`)
        .sort();
    return `${interestEndDateKey}@@${regNumberKey}@@${quarterKeys.join("||")}`;
  };
  const sheet6ExpandedRowCountBySourceRowKey = new Map<string, number>();
  expandedRowsByEmail.forEach((rows) => {
    rows.forEach((row) => {
      sheet6ExpandedRowCountBySourceRowKey.set(
        row.sourceRowKey,
        (sheet6ExpandedRowCountBySourceRowKey.get(row.sourceRowKey) || 0) + 1,
      );
    });
  });
  const findAmountSubsetIndicesForSheet6 = (
    rows: ExpandedBondRepurchaseRow[],
    candidateIndices: number[],
    targetAmount: number,
  ): number[] | null => {
    const toAmountCents = (value: number): number =>
      Math.round(roundTo2(value) * 100);
    const target = toAmountCents(targetAmount);
    const tolerance = toAmountCents(
      SHEET6_DISTRIBUTION_AMOUNT_TOLERANCE
    );
    if (target <= 0) return null;
    const items = candidateIndices
      .map((index) => ({
        index,
        cents: toAmountCents(rows[index].amount),
      }))
      .filter((item) => item.cents > 0)
      .sort((a, b) => {
        const rowA = rows[a.index];
        const rowB = rows[b.index];
        if (
          rowA.sourcePurchaseDateObj.getTime() !==
          rowB.sourcePurchaseDateObj.getTime()
        ) {
          return (
            rowA.sourcePurchaseDateObj.getTime() -
            rowB.sourcePurchaseDateObj.getTime()
          );
        }
        return (
          rowA.interestEndDateObj.getTime() -
          rowB.interestEndDateObj.getTime()
        );
      });
    if (items.length === 0) return null;

    let bestIndices: number[] | null = null;
    let bestDiff = Number.MAX_SAFE_INTEGER;
    const currentIndices: number[] = [];

    const dfs = (index: number, currentSum: number) => {
      const currentDiff = Math.abs(currentSum - target);
      if (
        currentIndices.length >= 2 &&
        (currentDiff < bestDiff ||
          (currentDiff === bestDiff &&
            (bestIndices?.length || 0) <
              currentIndices.length))
      ) {
        bestDiff = currentDiff;
        bestIndices = [...currentIndices];
        if (bestDiff === 0) return;
      }
      if (
        index >= items.length ||
        currentSum > target + tolerance
      ) {
        return;
      }

      currentIndices.push(items[index].index);
      dfs(index + 1, currentSum + items[index].cents);
      currentIndices.pop();
      dfs(index + 1, currentSum);
    };

    dfs(0, 0);
    if (!bestIndices || bestDiff > tolerance) return null;
    return bestIndices;
  };
  expandedRowsByEmail.forEach((rows) => {
    const availableRowsByKey = new Map<
      string,
      Array<{ id: string; row: BondDistributionRow }>
    >();
    const availableRowsByQuarterKey = new Map<
      string,
      Array<{ id: string; row: BondDistributionRow }>
    >();
    const sheet6RowCountByDistributionRegGroupKey = new Map<string, number>();

    rows.forEach((row) => {
      quarterRowExistsBySheet6RowId.set(buildExpandedSheet6RowId(row), false);
      const groupKey = buildSheet6DistributionRegGroupKey(row);
      sheet6RowCountByDistributionRegGroupKey.set(
        groupKey,
        (sheet6RowCountByDistributionRegGroupKey.get(groupKey) || 0) + 1,
      );
    });

    distributionRowsByBondQuarter.forEach((quarterRows, key) => {
      availableRowsByKey.set(
        key,
        quarterRows.map((row, index) => ({
          id: `${key}|${index}|${normalizeDateKey(row.purchaseDate || "")}|${roundTo2(
            safeNumber(row.amount)
          )}`,
          row,
        }))
      );
    });
    distributionRowsByQuarter.forEach((quarterRows, key) => {
      availableRowsByQuarterKey.set(
        key,
        quarterRows.map((row, index) => ({
          id: `${key}|${index}|${normalizeBondName(row.bondName || "")}|${normalizeDateKey(
            row.purchaseDate || ""
          )}|${roundTo2(safeNumber(row.amount))}`,
          row,
        }))
      );
    });

    const sortedRows = [...rows].sort((a, b) => {
      if (a.interestEndDateObj.getTime() !== b.interestEndDateObj.getTime()) {
        return a.interestEndDateObj.getTime() - b.interestEndDateObj.getTime();
      }
      if (a.sourcePurchaseDateObj.getTime() !== b.sourcePurchaseDateObj.getTime()) {
        return a.sourcePurchaseDateObj.getTime() - b.sourcePurchaseDateObj.getTime();
      }
      return roundTo2(b.amount) - roundTo2(a.amount);
    });

    sortedRows.forEach((row) => {
      const rowId = buildExpandedSheet6RowId(row);
      const interestEndDateKey = normalizeDateKey(row.interestEndDate || "");
      if (!interestEndDateKey) return;

      const candidateNames = getSheet6DistributionCandidateNames(
        row.displayBondName,
        row.sourceNormalizedBondName,
      );
      const candidateQuarterKeys = candidateNames.map(
        (bondName) => `${bondName}|${interestEndDateKey}`
      );
      const staticCandidateRows = candidateQuarterKeys.flatMap((key) =>
        (distributionRowsByBondQuarter.get(key) || []).map((candidateRow, index) => ({
          id: `${key}|static|${index}|${normalizeDateKey(candidateRow.purchaseDate || "")}|${roundTo2(
            safeNumber(candidateRow.amount)
          )}`,
          row: candidateRow,
        }))
      );
      const candidateRows = candidateQuarterKeys.flatMap(
        (key) => availableRowsByKey.get(key) || []
      );
      if (staticCandidateRows.length > 0) {
        quarterRowExistsBySheet6RowId.set(rowId, true);
      }

      let matchedEntry:
        | { id: string; row: BondDistributionRow; poolKey: string; fallback: false }
        | { id: string; row: BondDistributionRow; poolKey: string; fallback: true }
        | undefined;

      if (staticCandidateRows.length > 0) {
        const staticSameRegRows = staticCandidateRows.filter((entry) =>
          regNumberMatchesSheet6DistributionRow(entry.row, row.regNumber)
        );
        const match = findBestSheet6DistributionMatch(
          candidateRows.map((entry) => entry.row),
          row,
        );
        if (match) {
          const matchedFromPool = candidateRows.find((entry) => entry.row === match);
          if (matchedFromPool) {
            const poolKey = candidateQuarterKeys.find((key) =>
              (availableRowsByKey.get(key) || []).some((entry) => entry.id === matchedFromPool.id)
            );
            if (poolKey) {
              matchedEntry = { ...matchedFromPool, poolKey, fallback: false };
            }
          }
        }
        if (
          !matchedEntry &&
          isFirstExpandedQuarterForSheet6Row(row) &&
          staticSameRegRows.length >= 2
        ) {
          const fallbackMatch =
            findClosestSheet6DistributionMatchIgnoringTolerance(
              candidateRows.map((entry) => entry.row),
              row,
            );
          if (fallbackMatch) {
            const matchedFromPool = candidateRows.find(
              (entry) => entry.row === fallbackMatch
            );
            if (matchedFromPool) {
              const poolKey = candidateQuarterKeys.find((key) =>
                (availableRowsByKey.get(key) || []).some(
                  (entry) => entry.id === matchedFromPool.id
                )
              );
              if (poolKey) {
                matchedEntry = {
                  ...matchedFromPool,
                  poolKey,
                  fallback: false,
                };
              }
            }
          }
        }
      }

      if (!matchedEntry && staticCandidateRows.length === 0) {
        const fallbackQuarterRows = availableRowsByQuarterKey.get(interestEndDateKey) || [];
        if (fallbackQuarterRows.length > 0) {
          const fallbackMatch = findBestSheet6DistributionMatch(
            fallbackQuarterRows.map((entry) => entry.row),
            row,
          );
          if (fallbackMatch) {
            const matchedFromFallback = fallbackQuarterRows.find(
              (entry) => entry.row === fallbackMatch
            );
            if (matchedFromFallback) {
              matchedEntry = {
                ...matchedFromFallback,
                poolKey: interestEndDateKey,
                fallback: true,
              };
            }
          }
        }
      }

      if (!matchedEntry) return;

      assignedDistributionAmountBySheet6RowId.set(
        rowId,
        {
          amount: safeNumber(matchedEntry.row.amount),
          shouldCap:
            isFirstExpandedQuarterForSheet6Row(row) &&
            safeNumber(matchedEntry.row.amount) <
              roundTo2(row.amount),
        }
      );

      const removeFromPool = (
        entries: Array<{ id: string; row: BondDistributionRow }>
      ): Array<{ id: string; row: BondDistributionRow }> =>
        entries.filter((entry) => entry.id !== matchedEntry!.id);

      if (matchedEntry.fallback) {
        availableRowsByQuarterKey.set(
          matchedEntry.poolKey,
          removeFromPool(availableRowsByQuarterKey.get(matchedEntry.poolKey) || [])
        );
        availableRowsByKey.forEach((entries, key) => {
          if (entries.some((entry) => entry.id === matchedEntry!.id)) {
            availableRowsByKey.set(key, removeFromPool(entries));
          }
        });
      } else {
        availableRowsByKey.set(
          matchedEntry.poolKey,
          removeFromPool(availableRowsByKey.get(matchedEntry.poolKey) || [])
        );
        availableRowsByQuarterKey.set(
          interestEndDateKey,
          removeFromPool(availableRowsByQuarterKey.get(interestEndDateKey) || [])
        );
      }
    });

    const unmatchedGroupIndices = new Map<string, number[]>();
    sortedRows.forEach((row, index) => {
      const rowId = buildExpandedSheet6RowId(row);
      if (assignedDistributionAmountBySheet6RowId.has(rowId)) return;
      const interestEndDateKey = normalizeDateKey(row.interestEndDate || "");
      if (!interestEndDateKey) return;
      const regNumberKey = String(row.regNumber || "").trim().toUpperCase();
      if (!regNumberKey) return;
      const candidateQuarterKeys = getSheet6DistributionCandidateNames(
        row.displayBondName,
        row.sourceNormalizedBondName,
      ).map((bondName) => `${bondName}|${interestEndDateKey}`);
      if (candidateQuarterKeys.length === 0) return;
      const groupKey = `${interestEndDateKey}@@${regNumberKey}@@${candidateQuarterKeys
        .slice()
        .sort()
        .join("||")}`;
      if (!unmatchedGroupIndices.has(groupKey)) {
        unmatchedGroupIndices.set(groupKey, []);
      }
      unmatchedGroupIndices.get(groupKey)!.push(index);
    });

    unmatchedGroupIndices.forEach((groupIndices, groupKey) => {
      if (groupIndices.length < 2) return;
      const [interestEndDateKey, regNumberKey, quarterKeysText] =
        groupKey.split("@@");
      const candidateQuarterKeys = quarterKeysText
        .split("||")
        .filter(Boolean);
      const availableGroupedCandidates = candidateQuarterKeys.flatMap((key) =>
        (availableRowsByKey.get(key) || [])
          .filter((entry) =>
            String(entry.row.regNumber || "")
              .trim()
              .toUpperCase() === regNumberKey
          )
          .map((entry) => ({ ...entry, poolKey: key }))
      );
      if (availableGroupedCandidates.length === 0) return;

      availableGroupedCandidates
        .sort(
          (a, b) =>
            Math.abs(
              roundTo2(safeNumber(a.row.amount)) -
              roundTo2(
                groupIndices.reduce(
                  (sum, index) => sum + safeNumber(sortedRows[index].amount),
                  0
                )
              )
            ) -
            Math.abs(
              roundTo2(safeNumber(b.row.amount)) -
              roundTo2(
                groupIndices.reduce(
                  (sum, index) => sum + safeNumber(sortedRows[index].amount),
                  0
                )
              )
            )
        )
        .some((candidateEntry) => {
          const remainingIndices = groupIndices.filter((index) => {
            const rowId = buildExpandedSheet6RowId(sortedRows[index]);
            return !assignedDistributionAmountBySheet6RowId.has(rowId);
          });
          if (remainingIndices.length < 2) return false;
          const matchedSubset = findAmountSubsetIndicesForSheet6(
            sortedRows,
            remainingIndices,
            safeNumber(candidateEntry.row.amount)
          );
          if (!matchedSubset || matchedSubset.length < 2) return false;

          matchedSubset.forEach((index) => {
            assignedDistributionAmountBySheet6RowId.set(
              buildExpandedSheet6RowId(sortedRows[index]),
              {
                amount: safeNumber(sortedRows[index].amount),
                shouldCap: false,
              }
            );
          });

          const removeFromPool = (
            entries: Array<{ id: string; row: BondDistributionRow }>
          ): Array<{ id: string; row: BondDistributionRow }> =>
            entries.filter((entry) => entry.id !== candidateEntry.id);

          availableRowsByKey.set(
            candidateEntry.poolKey,
            removeFromPool(
              availableRowsByKey.get(candidateEntry.poolKey) || []
            )
          );
          availableRowsByQuarterKey.set(
            interestEndDateKey,
            removeFromPool(
              availableRowsByQuarterKey.get(interestEndDateKey) || []
            )
          );
          return true;
        });
    });
  });
  const findMatchingDistributionAmountForSheet6Row = (
    row: ExpandedBondRepurchaseRow,
  ): { amount: number; shouldCap: boolean } | undefined => {
    const match = assignedDistributionAmountBySheet6RowId.get(
      buildExpandedSheet6RowId(row)
    );
    if (!match) return undefined;
    return {
      amount: safeNumber(match.amount),
      shouldCap: match.shouldCap,
    };
  };
  const sourceRowKeyHasAnyDistributionRegMatch = new Map<string, boolean>();
  expandedRowsByEmail.forEach((rows) => {
    rows.forEach((row) => {
      if (sourceRowKeyHasAnyDistributionRegMatch.has(row.sourceRowKey)) return;
      const candidateNames = getSheet6DistributionCandidateNames(
        row.displayBondName,
        row.sourceNormalizedBondName,
      );
      const hasAnyRegMatch = candidateNames.some((bondName) =>
        Array.from(distributionRowsByBondQuarter.entries()).some(
          ([key, quarterRows]) =>
            key.startsWith(`${bondName}|`) &&
            quarterRows.some((candidateRow) =>
              regNumberMatchesSheet6DistributionRow(candidateRow, row.regNumber)
            )
        )
      );
      sourceRowKeyHasAnyDistributionRegMatch.set(
        row.sourceRowKey,
        hasAnyRegMatch,
      );
    });
  });
  expandedRowsByEmail.forEach((rows, email) => {
    const keptRows = rows.filter((row) => {
      const interestEndDateKey = normalizeDateKey(row.interestEndDate || "");
      if (!interestEndDateKey) return true;

      const candidateNames = getSheet6DistributionCandidateNames(
        row.displayBondName,
        row.sourceNormalizedBondName,
      );
      const quarterRows = candidateNames.flatMap((bondName) =>
        distributionRowsByBondQuarter.get(`${bondName}|${interestEndDateKey}`) || []
      );
      const sameRegQuarterRows = quarterRows.filter((candidateRow) =>
        regNumberMatchesSheet6DistributionRow(candidateRow, row.regNumber)
      );
      const rowId = buildExpandedSheet6RowId(row);
      const hasQuarterRows =
        quarterRows.length > 0 || quarterRowExistsBySheet6RowId.get(rowId) === true;
      const distributionMatch = findMatchingDistributionAmountForSheet6Row(row);
      if (!hasQuarterRows) {
        if (distributionMatch?.shouldCap) {
          applyDistributionCapToSheet6Row(row, distributionMatch.amount);
        }
        return true;
      }

      if (distributionMatch) {
        if (distributionMatch.shouldCap) {
          applyDistributionCapToSheet6Row(row, distributionMatch.amount);
        }
        return true;
      }

      const repeatedSourcePurchaseCount =
        sheet6ExpandedRowCountBySourceRowKey.get(row.sourceRowKey) || 0;
      const hasAnyDistributionRegMatchForSource =
        sourceRowKeyHasAnyDistributionRegMatch.get(row.sourceRowKey) === true;
      if (
        repeatedSourcePurchaseCount > 1 &&
        (sameRegQuarterRows.length > 0 || hasAnyDistributionRegMatchForSource)
      ) {
        regOnlyDistributionWarningBySheet6RowId.add(rowId);
        return true;
      }

      const narrativeBondName = targetNameForSheet6Comment(row.displayBondName);
      const distributionDateText = formatDistributionDate(row.interestEndDate);
      suppressedSheet6Rows.push({
        email: row.email,
        lastName: row.lastName,
        firstName: row.firstName,
        regNumber: row.regNumber,
        bondName: row.displayBondName,
        purchaseDate: row.purchaseDate,
        interestEndDate: row.interestEndDate,
        amount: row.amount,
        sourcePurchaseKey: row.sourcePurchaseKey,
        sourceRowKey: row.sourceRowKey,
        sheet5Comment: `${distributionDateText} нд худалдан авалтад орсон.`,
        workerComment: `${narrativeBondName}ын ${distributionDateText}-ны тархалтад байгаагүй тул ХУЗ дээр ${distributionDateText} нд худалдан авалтад орсон. гэж бичив.`,
      });
      return false;
    });
    expandedRowsByEmail.set(email, keptRows);
  });

  const toCents = (value: number): number => Math.round(roundTo2(value) * 100);
  const findSubsetIndices = (
    rows: ExpandedBondRepurchaseRow[],
    candidateIndices: number[],
    targetAmount: number,
  ): number[] | null => {
    const target = toCents(targetAmount);
    if (target <= 0) return null;
    const items = candidateIndices
      .map((index) => ({ index, cents: toCents(rows[index].transferAmount) }))
      .filter((item) => item.cents > 0)
      .sort((a, b) => {
        const rowA = rows[a.index];
        const rowB = rows[b.index];
        if (rowA.sourcePurchaseDateObj.getTime() !== rowB.sourcePurchaseDateObj.getTime()) {
          return rowA.sourcePurchaseDateObj.getTime() - rowB.sourcePurchaseDateObj.getTime();
        }
        return rowA.interestEndDateObj.getTime() - rowB.interestEndDateObj.getTime();
      });
    if (items.length === 0) return null;

    const tolerance = 5;
    const suffixSums = new Array(items.length + 1).fill(0);
    for (let i = items.length - 1; i >= 0; i--) {
      suffixSums[i] = suffixSums[i + 1] + items[i].cents;
    }

    let bestSum = 0;
    let bestIndices: number[] | null = null;
    const currentIndices: number[] = [];
    const visited = new Set<string>();

    const dfs = (index: number, currentSum: number) => {
      if (currentSum > target + tolerance) return;
      if (currentSum > bestSum) {
        bestSum = currentSum;
        bestIndices = [...currentIndices];
        if (target - currentSum <= tolerance) return;
      }
      if (index >= items.length) return;
      if (currentSum + suffixSums[index] <= bestSum) return;

      const memoKey = `${index}|${currentSum}`;
      if (visited.has(memoKey)) return;

      currentIndices.push(items[index].index);
      dfs(index + 1, currentSum + items[index].cents);
      currentIndices.pop();
      dfs(index + 1, currentSum);

      visited.add(memoKey);
    };

    dfs(0, 0);
    return bestIndices && bestSum > 0 ? bestIndices : null;
  };

  type Sheet6Group = {
    rows: ExpandedBondRepurchaseRow[];
    comment: string;
    sortDate: Date;
    carryKey?: string;
    carryAmount?: number;
  };
  const isSheet6CashReturnComment = (comment: string): boolean =>
    String(comment || "")
      .trim()
      .endsWith("нь бэлэн мөнгө хэлбэрээр байршиж байсан тул буцаав.");

  const orderedEmails = Array.from(expandedRowsByEmail.keys()).sort((a, b) => {
    const aOrder = userOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = userOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  });

  orderedEmails.forEach((email) => {
    const rows = expandedRowsByEmail.get(email) || [];
    if (rows.length === 0) return;
    rows.forEach((row) => {
      const distributionAmount = findMatchingDistributionAmountForSheet6Row(row);
      if (!distributionAmount) return;
      if (distributionAmount.shouldCap) {
        applyDistributionCapToSheet6Row(row, distributionAmount.amount);
      }
    });
    const destinations = destinationRowsByEmail.get(email) || [];
    const usedIndices = new Set<number>();
    const usedDestinationIndices = new Set<number>();
    const groups: Sheet6Group[] = [];
    const isStarterSourceRow = (row: ExpandedBondRepurchaseRow): boolean =>
      (purchaseHistoryAmountByEmailDate.get(row.sourcePurchaseKey) || 0) > 0;
    const sourceRowsByKey = new Map<string, ExpandedBondRepurchaseRow[]>();
    const sourceRowsByDate = new Map<string, ExpandedBondRepurchaseRow[]>();
    rows.forEach((row) => {
      if (!sourceRowsByKey.has(row.sourceRowKey)) sourceRowsByKey.set(row.sourceRowKey, []);
      sourceRowsByKey.get(row.sourceRowKey)!.push(row);
      const dateKey = toDateKey(row.sourcePurchaseDateObj);
      if (!sourceRowsByDate.has(dateKey)) sourceRowsByDate.set(dateKey, []);
      sourceRowsByDate.get(dateKey)!.push(row);
    });
    const overriddenSourceKeys = new Set<string>();
    const findDestinationSourceRows = (
      destination: { dateObj: Date; bondName: string },
    ): ExpandedBondRepurchaseRow[] => {
      const exactKey = buildSourceRowKey(email, destination.dateObj, destination.bondName);
      const exactRows = sourceRowsByKey.get(exactKey) || [];
      if (exactRows.length > 0) return exactRows;

      const sameDateRows = sourceRowsByDate.get(toDateKey(destination.dateObj)) || [];
      if (sameDateRows.length === 0) return [];
      const candidateNames = new Set(
        getSheet6DistributionCandidateNames(destination.bondName, normalizeBondName(destination.bondName)),
      );
      const matchedRows = sameDateRows.filter((row) => {
        const displayName = normalizeBondName(row.displayBondName);
        const sourceName = normalizeBondName(row.sourceNormalizedBondName);
        return candidateNames.has(displayName) || candidateNames.has(sourceName);
      });
      return matchedRows.length > 0 ? matchedRows : sameDateRows;
    };
    const syncDestinationAmountToCommentAmount = (
      destination: { dateObj: Date; dateStr: string; amount: number; bondName: string; isTopUpPurchase: boolean },
      commentAmount: number,
    ) => {
      if (destination.isTopUpPurchase) return;
      const sourceRows = findDestinationSourceRows(destination);
      if (sourceRows.length === 0) return;
      const overrideKey = `${toDateKey(destination.dateObj)}|${normalizeBondName(destination.bondName)}`;
      if (overriddenSourceKeys.has(overrideKey)) return;
      const principalAmount = sourceRows[0].isGsbAbsHbuc
        ? roundTo2(commentAmount * 0.998)
        : roundTo2(commentAmount);

      sourceRows.forEach((row) => {
        const updatedAmounts = buildSheet6Amounts(
          principalAmount,
          row.bondRate,
          row.days,
          row.baseAvtamataFee,
          row.isMaturityRow,
          row.isGsbAbsHbuc,
        );
        row.amount = principalAmount;
        row.calculatedInterest = updatedAmounts.calculatedInterest;
        row.totalPrice = updatedAmounts.totalPrice;
        row.avtamataFee = updatedAmounts.avtamataFee;
        row.ariljaaFee = updatedAmounts.ariljaaFee;
        row.oat = updatedAmounts.oat;
        row.transferAmount = updatedAmounts.transferAmount;
        const distributionAmount =
          findMatchingDistributionAmountForSheet6Row(row);
        if (distributionAmount?.shouldCap) {
          applyDistributionCapToSheet6Row(
            row,
            distributionAmount.amount,
          );
        }
      });

      overriddenSourceKeys.add(overrideKey);
    };
    const addSheet6DestinationGroup = (
      groupRows: ExpandedBondRepurchaseRow[],
      destination: { dateObj: Date; dateStr: string; amount: number; bondName: string; isTopUpPurchase: boolean },
    ) => {
      const groupAmount = roundTo2(groupRows.reduce((sum, row) => sum + row.transferAmount, 0));
      const targetName = targetNameForSheet6Comment(destination.bondName);
      const comment = destination.isTopUpPurchase
        ? `${formatSheet6Money(groupAmount)} нь ${formatSheet6CommentDate(destination.dateStr)} бондын дүн дээр нэмэгдэн${targetName ? ` ${targetName}` : ""} худалдан авалтад орсон.`
        : `${formatSheet6Money(groupAmount)} нь ${formatSheet6CommentDate(destination.dateStr)}${targetName ? ` ${targetName}` : ""} худалдан авалтад орсон.`;
      const carryKey = toCarryKey(email, destination.dateStr);
      carryForwardByEmailDate[carryKey] = (carryForwardByEmailDate[carryKey] || 0) + groupAmount;
      const sheet5CarryComment = buildSheet5CarryComment(groupRows, groupAmount);
      carryForwardCommentByEmailDate[carryKey] = carryForwardCommentByEmailDate[carryKey]
        ? `${carryForwardCommentByEmailDate[carryKey]}\n${sheet5CarryComment}`
        : sheet5CarryComment;
      carryForwardSourceLabelByEmailDate[carryKey] = buildSheet5CarrySourceLabel(groupRows);
      carryForwardSourceAmountByEmailDate[carryKey] = roundTo2(
        (carryForwardSourceAmountByEmailDate[carryKey] || 0) + groupAmount
      );
      syncDestinationAmountToCommentAmount(destination, groupAmount);
      groups.push({
        rows: groupRows,
        comment,
        sortDate: destination.dateObj,
        carryKey,
        carryAmount: groupAmount,
      });
    };
    const chargeRows = accumulatedChargeSheets
      .flatMap((sheet, sheetIndex) =>
        sheet.data.map((charge) => ({
          charge,
          sheetIndex,
          parsedDate: parseFlexibleDateTime(charge.haDate || "") || parseFlexibleDate(charge.haDate || ""),
        })),
      )
      .filter(({ charge }) => String(charge.email || "").toLowerCase().trim() === email)
      .filter(({ charge }) => getSheet6ChargeBondCandidates(charge.description || "").length > 0)
      .sort((a, b) => {
        const aTime = a.parsedDate?.getTime() || Number.MAX_SAFE_INTEGER;
        const bTime = b.parsedDate?.getTime() || Number.MAX_SAFE_INTEGER;
        if (aTime !== bTime) return aTime - bTime;
        if (a.sheetIndex !== b.sheetIndex) return a.sheetIndex - b.sheetIndex;
        return a.charge.index - b.charge.index;
      })
      .map((item) => item.charge);
    const pickBestChargeCandidateIndex = (
      charge: ChargeRow,
      destinationDateObj: Date | null,
      provisionalUsedIndices: Set<number>,
    ): number | undefined =>
      rows
        .map((row, index) => ({ row, index }))
        .filter(({ row, index }) =>
          !usedIndices.has(index) &&
          !provisionalUsedIndices.has(index) &&
          isCompatibleSheet6ChargeRow(row, charge.description || "") &&
          (!destinationDateObj || row.sourcePurchaseDateObj.getTime() < destinationDateObj.getTime())
        )
        .sort((a, b) => {
          if (a.row.sourcePurchaseDateObj.getTime() !== b.row.sourcePurchaseDateObj.getTime()) {
            return a.row.sourcePurchaseDateObj.getTime() - b.row.sourcePurchaseDateObj.getTime();
          }
          if (a.row.interestEndDateObj.getTime() !== b.row.interestEndDateObj.getTime()) {
            return a.row.interestEndDateObj.getTime() - b.row.interestEndDateObj.getTime();
          }
          const aAmountGap = Math.abs(roundTo2(a.row.transferAmount) - roundTo2(safeNumber(charge.amount)));
          const bAmountGap = Math.abs(roundTo2(b.row.transferAmount) - roundTo2(safeNumber(charge.amount)));
          return aAmountGap - bAmountGap;
        })[0]?.index;

    const provisionalUsedIndices = new Set<number>();
    const chargeMatchedIndicesByDestination = new Map<number, { indices: number[]; maxChargeDate: Date | null }>();
    const orphanChargeMatchedIndices: number[] = [];
    let orphanMaxChargeDate: Date | null = null;

    chargeRows.forEach((charge) => {
      const chargeDate = parseFlexibleDateTime(charge.haDate || "") || parseFlexibleDate(charge.haDate || "");
      if (!chargeDate) return;

      const destinationIndex = destinations.findIndex((destination, index) =>
        !usedDestinationIndices.has(index) &&
        chargeDate.getTime() <= destination.dateObj.getTime()
      );
      const destinationDateObj = destinationIndex >= 0 ? destinations[destinationIndex].dateObj : null;
      const candidateIndex = pickBestChargeCandidateIndex(
        charge,
        destinationDateObj,
        provisionalUsedIndices,
      );
      if (candidateIndex === undefined) return;

      provisionalUsedIndices.add(candidateIndex);

      if (destinationIndex >= 0) {
        const existing = chargeMatchedIndicesByDestination.get(destinationIndex) || {
          indices: [],
          maxChargeDate: null as Date | null,
        };
        existing.indices.push(candidateIndex);
        if (!existing.maxChargeDate || chargeDate.getTime() > existing.maxChargeDate.getTime()) {
          existing.maxChargeDate = chargeDate;
        }
        chargeMatchedIndicesByDestination.set(destinationIndex, existing);
      } else {
        orphanChargeMatchedIndices.push(candidateIndex);
        if (!orphanMaxChargeDate || chargeDate.getTime() > orphanMaxChargeDate.getTime()) {
          orphanMaxChargeDate = chargeDate;
        }
      }
    });

    destinations.forEach((destination, destinationIndex) => {
      const matched = chargeMatchedIndicesByDestination.get(destinationIndex);
      if (!matched || matched.indices.length === 0) return;

      const uniqueMatchedIndices = Array.from(new Set(matched.indices));
      uniqueMatchedIndices.forEach((index) => usedIndices.add(index));
      usedDestinationIndices.add(destinationIndex);
      const groupRows = uniqueMatchedIndices
        .map((index) => rows[index])
        .sort((a, b) => {
          if (a.sourcePurchaseDateObj.getTime() !== b.sourcePurchaseDateObj.getTime()) {
            return a.sourcePurchaseDateObj.getTime() - b.sourcePurchaseDateObj.getTime();
          }
          return a.interestEndDateObj.getTime() - b.interestEndDateObj.getTime();
        });
      addSheet6DestinationGroup(groupRows, destination);
    });

    if (orphanChargeMatchedIndices.length > 0) {
      const uniqueMatchedIndices = Array.from(new Set(orphanChargeMatchedIndices));
      uniqueMatchedIndices.forEach((index) => usedIndices.add(index));
      const groupRows = uniqueMatchedIndices
        .map((index) => rows[index])
        .sort((a, b) => {
          if (a.sourcePurchaseDateObj.getTime() !== b.sourcePurchaseDateObj.getTime()) {
            return a.sourcePurchaseDateObj.getTime() - b.sourcePurchaseDateObj.getTime();
          }
          return a.interestEndDateObj.getTime() - b.interestEndDateObj.getTime();
        });
      const groupAmount = roundTo2(groupRows.reduce((sum, row) => sum + row.transferAmount, 0));
      groups.push({
        rows: groupRows,
        comment: `${formatSheet6Money(groupAmount)} нь бэлэн мөнгө хэлбэрээр байршиж байсан тул буцаав.`,
        sortDate: orphanMaxChargeDate || groupRows[groupRows.length - 1].interestEndDateObj,
      });
    }

    destinations.forEach((destination, destinationIndex) => {
      if (usedDestinationIndices.has(destinationIndex)) return;
      const candidateIndices = rows
        .map((row, index) => ({ row, index }))
        .filter(({ row, index }) =>
          !usedIndices.has(index) &&
          toCents(row.transferAmount) > 0 &&
          row.interestEndDateObj.getTime() < destination.dateObj.getTime()
        )
        .map(({ index }) => index);
      const matchedIndices = findSubsetIndices(rows, candidateIndices, destination.amount);
      if (!matchedIndices || matchedIndices.length === 0) return;

      matchedIndices.forEach((index) => usedIndices.add(index));
      usedDestinationIndices.add(destinationIndex);
      const groupRows = matchedIndices
        .map((index) => rows[index])
        .sort((a, b) => {
          if (a.sourcePurchaseDateObj.getTime() !== b.sourcePurchaseDateObj.getTime()) {
            return a.sourcePurchaseDateObj.getTime() - b.sourcePurchaseDateObj.getTime();
          }
          return a.interestEndDateObj.getTime() - b.interestEndDateObj.getTime();
        });
      addSheet6DestinationGroup(groupRows, destination);
    });

    const remainingItems = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row, index }) =>
        !usedIndices.has(index) &&
        toCents(row.transferAmount) > 0
      )
      .sort((a, b) => a.index - b.index);

    const remainingBlocks: ExpandedBondRepurchaseRow[][] = [];
    let currentBlock: ExpandedBondRepurchaseRow[] = [];
    let previousIndex = -2;
    remainingItems.forEach(({ row, index }) => {
      if (currentBlock.length === 0 || index === previousIndex + 1) {
        currentBlock.push(row);
      } else {
        remainingBlocks.push(currentBlock);
        currentBlock = [row];
      }
      previousIndex = index;
    });
    if (currentBlock.length > 0) remainingBlocks.push(currentBlock);

    remainingBlocks.forEach((remainingRows) => {
      const groupAmount = roundTo2(remainingRows.reduce((sum, row) => sum + row.transferAmount, 0));
      groups.push({
        rows: remainingRows,
        comment: `${formatSheet6Money(groupAmount)} нь бэлэн мөнгө хэлбэрээр байршиж байсан тул буцаав.`,
        sortDate: addDays(remainingRows[remainingRows.length - 1].interestEndDateObj, 1),
      });
    });

    const mergedGroups: Sheet6Group[] = [];
    groups.forEach((group) => {
      const previousGroup =
        mergedGroups[mergedGroups.length - 1];
      if (
        previousGroup &&
        isSheet6CashReturnComment(previousGroup.comment) &&
        isSheet6CashReturnComment(group.comment)
      ) {
        previousGroup.rows.push(...group.rows);
        const mergedAmount = roundTo2(
          previousGroup.rows.reduce(
            (sum, row) => sum + row.transferAmount,
            0
          )
        );
        previousGroup.comment = `${formatSheet6Money(
          mergedAmount
        )} нь бэлэн мөнгө хэлбэрээр байршиж байсан тул буцаав.`;
        if (
          group.sortDate.getTime() >
          previousGroup.sortDate.getTime()
        ) {
          previousGroup.sortDate = group.sortDate;
        }
        return;
      }
      mergedGroups.push({
        ...group,
        rows: [...group.rows],
      });
    });

    mergedGroups.forEach((group) => {
      const groupStartRowNumber = sheet7.rowCount + 1;
      group.rows.forEach((row, index) => {
        const addedRow = sheet7.addRow([
          row.email,
          row.phone,
          row.lastName,
          row.firstName,
          row.regNumber,
          row.bankAccount,
          row.bank,
          row.txCode,
          row.ttd,
          row.displayBondName,
          row.tailbarText,
          row.amount,
          row.bondRate,
          row.purchaseDate,
          row.interestEndDate,
          row.days,
          row.calculatedInterest,
          row.totalPrice,
          row.avtamataFee || "",
          row.ariljaaFee || "",
          row.oat,
          row.transferAmount,
          index === 0 ? group.comment : "",
        ]);
        addBorderToRow(addedRow);
        if (regOnlyDistributionWarningBySheet6RowId.has(buildExpandedSheet6RowId(row))) {
          for (let col = 1; col <= 23; col += 1) {
            addedRow.getCell(col).fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFC7CE" },
            };
          }
        }
      });
      const groupEndRowNumber = sheet7.rowCount;
      if (group.comment && groupEndRowNumber > groupStartRowNumber) {
        sheet7.mergeCells(groupStartRowNumber, 23, groupEndRowNumber, 23);
        sheet7.getRow(groupStartRowNumber).getCell(23).alignment = { wrapText: true, vertical: "top" };
      }
    });
  });

  const latestSheet6RowBySourceRowKey = new Map<string, ExpandedBondRepurchaseRow>();
  const latestSheet6RowBySourceDateBondKey = new Map<string, ExpandedBondRepurchaseRow>();
  expandedRowsByEmail.forEach((rows) => {
    rows.forEach((row) => {
      const existing = latestSheet6RowBySourceRowKey.get(row.sourceRowKey);
      if (!existing || row.interestEndDateObj.getTime() > existing.interestEndDateObj.getTime()) {
        latestSheet6RowBySourceRowKey.set(row.sourceRowKey, row);
      }
      const sourceDateBondKey = `${(row.email || "").toLowerCase().trim()}|${normalizeDateKey(
        toDateKey(row.sourcePurchaseDateObj),
      )}|${normalizeBondName(row.sourceNormalizedBondName || row.displayBondName || "")}`;
      const existingByDateBond = latestSheet6RowBySourceDateBondKey.get(sourceDateBondKey);
      if (
        !existingByDateBond ||
        row.interestEndDateObj.getTime() > existingByDateBond.interestEndDateObj.getTime()
      ) {
        latestSheet6RowBySourceDateBondKey.set(sourceDateBondKey, row);
      }
    });
  });

  // Apply Sheet 6 carry-forward map back to Sheet 2 and Sheet 5 so all sheets stay consistent.
  const parseCellDateToYmd = (value: ExcelJS.CellValue): string => {
    if (value instanceof Date) return toYMD(value);
    if (typeof value === "string") {
      const parsed = parseFlexibleDate(value);
      if (parsed) return toYMD(parsed);
      return value.trim();
    }
    if (typeof value === "number" && value > 40000 && value < 60000) {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
      return toYMD(date);
    }
    return "";
  };
  const parseCellNum = (value: ExcelJS.CellValue): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const cleaned = value.replace(/,/g, "").replace(/%/g, "").trim();
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };
  const parseRatePercent = (value: ExcelJS.CellValue): number => {
    if (typeof value === "number") return value > 1 ? value / 100 : value;
    if (typeof value === "string") {
      const cleaned = value.replace("%", "").trim();
      const parsed = Number(cleaned);
      if (!Number.isFinite(parsed)) return 0;
      return parsed > 1 ? parsed / 100 : parsed;
    }
    return 0;
  };
  const formatMoneyLine = (value: number): string =>
    value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const splitLines = (value: ExcelJS.CellValue): string[] =>
    String(value || "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
  const chargeByEmailDate: Record<string, number> = {};
  accumulatedChargeSheets.forEach((sheet) => {
    sheet.data.forEach((charge) => {
      const email = String(charge.email || "").toLowerCase().trim();
      if (!email.includes("@")) return;
      if (!isAvtamataFeeCharge(charge.description || "")) return;
      const chargeDate = parseFlexibleDate(charge.haDate || "");
      if (!chargeDate) return;
      const key = `${email}|${toYMD(chargeDate)}`;
      chargeByEmailDate[key] = (chargeByEmailDate[key] || 0) + safeNumber(charge.amount);
    });
  });

  const cashPurchaseByEmailDate: Record<string, number> = {};
  const cashBlocks = new Map<string, CashBalanceRow[]>();
  cashBalanceRows.forEach((row) => {
    const key = [
      (row.email || "").toLowerCase().trim(),
      row.purchaseDate || "",
      row.portfolioIndex || "",
      safeNumber(row.investedAmount),
    ].join("|");
    if (!cashBlocks.has(key)) cashBlocks.set(key, []);
    cashBlocks.get(key)!.push(row);
  });
  cashBlocks.forEach((rows) => {
    if (rows.length === 0) return;
    const email = (rows[0].email || "").toLowerCase().trim();
    const purchaseDate = rows[0].purchaseDate || "";
    const totalCashBalance = rows.reduce((sum, row) => sum + safeNumber(row.cashBalance), 0);
    if (!email || totalCashBalance <= 0) return;
    const nextPurchaseDate = findNextPurchaseDate(email, purchaseDate);
    if (!nextPurchaseDate) return;
    const purchaseKey = toCarryKey(email, nextPurchaseDate);
    cashPurchaseByEmailDate[purchaseKey] = (cashPurchaseByEmailDate[purchaseKey] || 0) + totalCashBalance;
  });

  // Sheet 2 amount must stay sourced from the uploaded withdraw-request file
  // (I column / "Тоо ширхэг"). Keep the carry-forward map for Sheet 5/6 linkage,
  // but do not push carry-forward amounts back into Sheet 2 when an uploaded
  // request amount already exists.
  const sheet2RowsByKey: Record<string, number[]> = {};
  Object.entries(sheet2RowsBySourceKey).forEach(([key, rows]) => {
    sheet2RowsByKey[key] = [...rows];
  });
  // Fallback map by displayed purchase date (in case sourcePurchaseDate is missing).
  for (let r = 3; r <= sheet2.rowCount; r++) {
    const email = String(sheet2.getRow(r).getCell(1).value || "").toLowerCase().trim();
    if (!email.includes("@")) continue;
    const purchaseYmd = parseCellDateToYmd(sheet2.getRow(r).getCell(13).value);
    const key = `${email}|${purchaseYmd}`;
    if (!sheet2RowsByKey[key]) sheet2RowsByKey[key] = [];
    if (!sheet2RowsByKey[key].includes(r)) sheet2RowsByKey[key].push(r);
  }
  Object.entries(sheet2RowsByKey).forEach(([key, rowNumbers]) => {
    const linkedAmount = carryForwardByEmailDate[key];
    if (!linkedAmount) return;
    let targetRowNumber = rowNumbers[0];
    if (rowNumbers.length > 1) {
      targetRowNumber = rowNumbers.reduce((bestRow, currentRow) => {
        const bestAmount = parseCellNum(sheet2.getRow(bestRow).getCell(11).value);
        const currentAmount = parseCellNum(sheet2.getRow(currentRow).getCell(11).value);
        return Math.abs(currentAmount - linkedAmount) < Math.abs(bestAmount - linkedAmount) ? currentRow : bestRow;
      }, rowNumbers[0]);
    }

    const row = sheet2.getRow(targetRowNumber);
    const currentAmount = parseCellNum(row.getCell(11).value);
    if (currentAmount > 0) return;

    const amount = ceilTo2Decimals(linkedAmount);
    const rate = parseRatePercent(row.getCell(12).value);
    const days = parseCellNum(row.getCell(15).value);
    const avtamataFee = parseCellNum(row.getCell(19).value);
    const interest = amount * rate * days / 365;
    const totalPrice = amount + interest;
    const oat = interest * 0.1;
    const transfer = totalPrice - avtamataFee - oat;

    row.getCell(11).value = amount;
    row.getCell(16).value = ceilTo2Decimals(interest);
    row.getCell(17).value = ceilTo2Decimals(interest);
    row.getCell(18).value = ceilTo2Decimals(totalPrice);
    row.getCell(21).value = ceilTo2Decimals(oat);
    row.getCell(22).value = ceilTo2Decimals(transfer);
  });
  Object.entries(sheet2RowsBySourceRowKey).forEach(([sourceRowKey, rowNumbers]) => {
    const latestSheet6Row =
      latestSheet6RowBySourceRowKey.get(sourceRowKey) ||
      latestSheet6RowBySourceDateBondKey.get(sourceRowKey);
    if (!latestSheet6Row || latestSheet6Row.isMaturityRow) return;

    const continuationStartDate = parseFlexibleDate(latestSheet6Row.interestEndDate);
    const refundDateForSheet2 = parseFlexibleDate(refundDate);
    if (!continuationStartDate || !refundDateForSheet2) return;
    const days = diffDays(continuationStartDate, refundDateForSheet2);
    if (days <= 0) return;

    let targetRowNumber = rowNumbers[0];
    if (rowNumbers.length > 1) {
      targetRowNumber = rowNumbers.reduce((bestRow, currentRow) => {
        const bestAmount = parseCellNum(sheet2.getRow(bestRow).getCell(11).value);
        const currentAmount = parseCellNum(sheet2.getRow(currentRow).getCell(11).value);
        return Math.abs(currentAmount - latestSheet6Row.amount) < Math.abs(bestAmount - latestSheet6Row.amount)
          ? currentRow
          : bestRow;
      }, rowNumbers[0]);
    }

    const row = sheet2.getRow(targetRowNumber);
    const amount = roundTo2(latestSheet6Row.amount);
    const rate = safeNumber(latestSheet6Row.bondRate);
    const avtamataFee = parseCellNum(row.getCell(19).value);
    const interest = amount * rate * days / 365;
    const totalPrice = amount + interest;
    const oat = interest * 0.1;
    const transfer = totalPrice - avtamataFee - oat;

    row.getCell(11).value = ceilTo2Decimals(amount);
    row.getCell(12).value = `${(rate * 100).toFixed(2)}%`;
    row.getCell(13).value = formatSheet6Date(normalizeDateKey(latestSheet6Row.interestEndDate));
    row.getCell(14).value = formatSheet6Date(normalizeDateKey(refundDate));
    row.getCell(15).value = days;
    row.getCell(16).value = ceilTo2Decimals(interest);
    row.getCell(17).value = ceilTo2Decimals(interest);
    row.getCell(18).value = ceilTo2Decimals(totalPrice);
    row.getCell(21).value = ceilTo2Decimals(oat);
    row.getCell(22).value = ceilTo2Decimals(transfer);
  });

  // Sheet 5 overrides:
  // Rebuild rows from the uploaded purchase history so dates, stock allocations,
  // and bank-source splits stay aligned with the user's source file.
  type Sheet5PurchaseEntry = PurchaseHistoryRow & {
    rowKey: string;
    dateKey: string;
    normalizedDate: string;
    parsedDate: Date;
    order: number;
  };
  const SHEET5_COMMENTLESS_RATIOS = [0.5, 0.55, 0.6, 0.65, 0.8, 1];
  const isSheet5CommentlessRatio = (ratio: number | ""): boolean => {
    if (ratio === "") return true;
    return SHEET5_COMMENTLESS_RATIOS.some((allowedRatio) => Math.abs(ratio - allowedRatio) < 0.000001);
  };
  const buildSheet5Tag = (bondAmount: number, totalAmount: number): string => {
    if (totalAmount === 0) return "";
    const ratio = bondAmount / totalAmount;
    return isSheet5CommentlessRatio(ratio) ? "" : "Бондын зөрүүтэй";
  };
  const combineSheet5Comments = (parts: string[]): string => {
    const filtered = parts.map((part) => part.trim()).filter(Boolean);
    if (filtered.length <= 1) return filtered.join("");
    return filtered.map((part, index) => `${index + 1}. ${part}`).join(" ");
  };
  const pickClosestEntryRowKey = (
    entries: Sheet5PurchaseEntry[],
    selector: (entry: Sheet5PurchaseEntry) => number,
    target: number,
  ): string | undefined => {
    if (entries.length === 0) return undefined;
    return entries.reduce((best, entry) => {
      if (!best) return entry;
      const bestScore = Math.abs(selector(best) - target);
      const entryScore = Math.abs(selector(entry) - target);
      return entryScore < bestScore ? entry : best;
    }, entries[0]).rowKey;
  };
  const pickClosestCarryTargetRowKey = (
    entries: Sheet5PurchaseEntry[],
    target: number,
  ): string | undefined => {
    if (entries.length === 0) return undefined;
    const positiveEntries = entries.filter((entry) => safeNumber(entry.amount) > 0);
    if (positiveEntries.length > 0) {
      return pickClosestEntryRowKey(
        positiveEntries,
        (entry) => safeNumber(entry.amount),
        target,
      );
    }
    return pickClosestEntryRowKey(
      entries,
      (entry) => safeNumber(entry.bond),
      target,
    );
  };
  const sheet5Entries = purchaseHistoryData
    .map((row, order) => {
      const email = resolvePurchaseHistoryEmail(row);
      const parsedDate = parseFlexibleDate(row.haDate || "");
      if (!email.includes("@") || !parsedDate || !selectedEmails.has(email)) return null;
      const normalizedDate = toYMD(parsedDate);
      return {
        ...row,
        email,
        rowKey: `${email}|${normalizedDate}|${order}`,
        dateKey: `${email}|${normalizedDate}`,
        normalizedDate,
        parsedDate,
        order,
      } as Sheet5PurchaseEntry;
    })
    .filter((row): row is Sheet5PurchaseEntry => row !== null);
  const balanceHistorySheet5GuardsByEmail = new Map<
    string,
    { dateKey: string; totalAmount: number; used: boolean }[]
  >();
  balanceHistoryRows.forEach((row) => {
    const email = normalizeEmailKey(row.email);
    const dateKey = normalizeDateKey(row.purchaseDate || "");
    if (!email || !dateKey) return;
    if (!balanceHistorySheet5GuardsByEmail.has(email)) {
      balanceHistorySheet5GuardsByEmail.set(email, []);
    }
    balanceHistorySheet5GuardsByEmail.get(email)!.push({
      dateKey,
      totalAmount: roundTo2(safeNumber(row.totalAmount)),
      used: false,
    });
  });
  const filteredSheet5Entries = sheet5Entries.filter((entry) => {
    const email = normalizeEmailKey(entry.email);
    const guards = balanceHistorySheet5GuardsByEmail.get(email);
    if (!guards || guards.length === 0) return true;

    const sameDateGuards = guards.filter(
      (guard) => !guard.used && guard.dateKey === entry.normalizedDate
    );
    if (sameDateGuards.length === 0) {
      return false;
    }

    const targetAmount = roundTo2(safeNumber(entry.amount));
    const matchedGuard = sameDateGuards.find(
      (guard) => Math.abs(guard.totalAmount - targetAmount) <= 1
    );
    if (!matchedGuard) {
      return false;
    }

    matchedGuard.used = true;
    return true;
  });

  const entriesByEmail = new Map<string, Sheet5PurchaseEntry[]>();
  const entriesByDateKey = new Map<string, Sheet5PurchaseEntry[]>();
  filteredSheet5Entries.forEach((entry) => {
    const email = String(entry.email || "").toLowerCase().trim();
    if (!entriesByEmail.has(email)) entriesByEmail.set(email, []);
    entriesByEmail.get(email)!.push(entry);
    if (!entriesByDateKey.has(entry.dateKey)) entriesByDateKey.set(entry.dateKey, []);
    entriesByDateKey.get(entry.dateKey)!.push(entry);
  });
  entriesByEmail.forEach((entries) => {
    entries.sort((a, b) => {
      if (a.parsedDate.getTime() !== b.parsedDate.getTime()) {
        return a.parsedDate.getTime() - b.parsedDate.getTime();
      }
      return a.order - b.order;
    });
  });
  const distributionSuppressionCommentByRowKey = new Map<string, string[]>();
  suppressedSheet6Rows.forEach((suppressedRow) => {
    const matchedEntries = entriesByDateKey.get(suppressedRow.sourcePurchaseKey) || [];
    if (matchedEntries.length === 0) return;
    const matchedRowKey = pickClosestEntryRowKey(
      matchedEntries,
      (entry) => safeNumber(entry.bond),
      suppressedRow.amount,
    );
    if (!matchedRowKey) return;
    if (!distributionSuppressionCommentByRowKey.has(matchedRowKey)) {
      distributionSuppressionCommentByRowKey.set(matchedRowKey, []);
    }
    distributionSuppressionCommentByRowKey.get(matchedRowKey)!.push(suppressedRow.sheet5Comment);
  });
  const distributionCappedCommentByRowKey = new Map<string, string[]>();
  cappedSheet6Rows.forEach((cappedRow) => {
    const matchedEntries = entriesByDateKey.get(cappedRow.sourcePurchaseKey) || [];
    if (matchedEntries.length === 0) return;
    const matchedRowKey = pickClosestEntryRowKey(
      matchedEntries,
      (entry) => safeNumber(entry.bond),
      cappedRow.originalAmount,
    );
    if (!matchedRowKey) return;
    const matchedEntry = matchedEntries.find((entry) => entry.rowKey === matchedRowKey);
    if (!matchedEntry) return;
    const cappedAmount = roundTo2(cappedRow.cappedAmount);
    const heldAmount = roundTo2(Math.max(0, safeNumber(matchedEntry.bond) - cappedAmount));
    const comment = heldAmount > 0
      ? `${formatSheet6Money(cappedAmount)} нь худалдан авалтад орсон. ${formatSheet6Money(heldAmount)} нь битүүмжлэгдэв.`
      : `${formatSheet6Money(cappedAmount)} нь худалдан авалтад орсон.`;
    if (!distributionCappedCommentByRowKey.has(matchedRowKey)) {
      distributionCappedCommentByRowKey.set(matchedRowKey, []);
    }
    const existing = distributionCappedCommentByRowKey.get(matchedRowKey)!;
    if (!existing.includes(comment)) {
      existing.push(comment);
    }
  });
  const directDistributionCappedCommentByRowKey = new Map<string, string[]>();

  const bankSourcesByRowKey = new Map<string, { id: string; date: string; income: number; account: string }[]>();
  const bankEntriesByEmail = new Map<string, { id: string; date: string; income: number; account: string; parsedDate: Date }[]>();
  entriesByEmail.forEach((entries, email) => {
    const txCode = String(entries.find((entry) => entry.txCode)?.txCode || customerMap[email]?.depositCode || "").trim();
    const userBankEntries = findBankEntriesForUser(txCode);
    bankEntriesByEmail.set(email, userBankEntries);

    let bankCursor = 0;
    entries.forEach((entry) => {
      if (safeNumber(entry.amount) <= 0) return;
      const available = userBankEntries
        .slice(bankCursor)
        .filter((candidate) => candidate.parsedDate.getTime() <= entry.parsedDate.getTime());
      if (available.length === 0) return;

      let bestCount = 0;
      let bestGap = Number.POSITIVE_INFINITY;
      let running = 0;
      available.forEach((candidate, index) => {
        running += safeNumber(candidate.income);
        const gap = Math.abs(running - safeNumber(entry.amount));
        if (gap < bestGap - 0.01) {
          bestGap = gap;
          bestCount = index + 1;
        }
      });

      if (bestCount <= 0) return;
      const assigned = available.slice(0, bestCount).map((candidate) => ({
        id: candidate.id,
        date: candidate.date,
        income: candidate.income,
        account: candidate.account,
      }));
      bankCursor += bestCount;
      if (assigned.length > 0) {
        bankSourcesByRowKey.set(entry.rowKey, assigned);
      }
    });
  });

  const cashTargetRowKeyByDateKey: Record<string, string> = {};
  Object.entries(cashPurchaseByEmailDate).forEach(([dateKey, cashAmount]) => {
    const entries = entriesByDateKey.get(dateKey) || [];
    const targetRowKey = pickClosestEntryRowKey(entries, (entry) => safeNumber(entry.amount), cashAmount);
    if (targetRowKey) cashTargetRowKeyByDateKey[dateKey] = targetRowKey;
  });
  const soldTargetRowKeyByDateKey: Record<string, string> = {};
  Object.entries(soldCarryByEmailDate).forEach(([dateKey, soldAmount]) => {
    const entries = entriesByDateKey.get(dateKey) || [];
    const positiveEntries = entries.filter((entry) => safeNumber(entry.amount) > 0);
    const pool = positiveEntries.length > 0 ? positiveEntries : entries;
    const targetRowKey = pickClosestEntryRowKey(pool, (entry) => safeNumber(entry.amount), soldAmount);
    if (targetRowKey) soldTargetRowKeyByDateKey[dateKey] = targetRowKey;
  });
  const carryTargetRowKeyByDateKey: Record<string, string> = {};
  Object.entries(carryForwardByEmailDate).forEach(([dateKey, carryAmount]) => {
    const entries = entriesByDateKey.get(dateKey) || [];
    const targetRowKey = pickClosestCarryTargetRowKey(entries, carryAmount);
    if (targetRowKey) carryTargetRowKeyByDateKey[dateKey] = targetRowKey;
  });
  const chargeTargetRowKeyByDateKey: Record<string, string> = {};
  Object.entries(chargeByEmailDate).forEach(([dateKey, chargeAmount]) => {
    const entries = entriesByDateKey.get(dateKey) || [];
    const targetRowKey = pickClosestEntryRowKey(entries, (entry) => safeNumber(entry.amount), chargeAmount);
    if (targetRowKey) chargeTargetRowKeyByDateKey[dateKey] = targetRowKey;
  });

  const inferredSheet5TxCodeByEmail = new Map<string, string>();
  const missingTxCodeEntriesByEmail = new Map<string, Sheet5PurchaseEntry[]>();
  filteredSheet5Entries.forEach((entry) => {
    const email = normalizeEmailKey(entry.email);
    const explicitTxCode = String(entry.txCode || customerMap[email]?.depositCode || "").trim();
    if (explicitTxCode || safeNumber(entry.amount) <= 0) return;
    if (!missingTxCodeEntriesByEmail.has(email)) missingTxCodeEntriesByEmail.set(email, []);
    missingTxCodeEntriesByEmail.get(email)!.push(entry);
  });
  missingTxCodeEntriesByEmail.forEach((entries, email) => {
    let bestCode = "";
    let bestScore = -1;
    let bestGap = Number.POSITIVE_INFINITY;
    bankEntriesByAt1Code.forEach((candidateEntries, at1Code) => {
      const usedIds = new Set<string>();
      let score = 0;
      let gapScore = 0;
      const sortedEntries = [...entries].sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
      sortedEntries.forEach((entry) => {
        const available = candidateEntries.filter(
          (candidate) =>
            !usedIds.has(candidate.id) &&
            candidate.parsedDate.getTime() <= entry.parsedDate.getTime()
        );
        if (available.length === 0) return;
        const targets = Array.from(new Set([roundTo2(safeNumber(entry.amount)), roundTo2(safeNumber(entry.amount) + 5000)])).filter((target) => target > 0);
        for (const target of targets) {
          const exactSingle = available
            .filter((candidate) => Math.abs(roundTo2(candidate.income) - target) <= 1)
            .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime())[0];
          if (exactSingle) {
            score += 1;
            gapScore += Math.abs(roundTo2(exactSingle.income) - target);
            usedIds.add(exactSingle.id);
            return;
          }
          const subset = findBestRecentBankSubset(available, target);
          if (subset.length > 0 && Math.abs(sumBankLines(subset) - target) <= 1) {
            score += 1;
            gapScore += Math.abs(sumBankLines(subset) - target);
            subset.forEach((line) => usedIds.add(line.id));
            return;
          }
        }
      });
      if (
        score > bestScore ||
        (score === bestScore && gapScore < bestGap)
      ) {
        bestScore = score;
        bestGap = gapScore;
        bestCode = at1Code;
      }
    });
    if (bestCode && bestScore > 0) {
      inferredSheet5TxCodeByEmail.set(email, bestCode);
    }
  });
  const getEffectiveSheet5TxCode = (entry: Sheet5PurchaseEntry): string => {
    const email = normalizeEmailKey(entry.email);
    return String(
      entry.txCode ||
      customerMap[email]?.depositCode ||
      inferredSheet5TxCodeByEmail.get(email) ||
      ""
    ).trim();
  };
  const usedBankEntryIdsByEmail = new Map<string, Set<string>>();
  const markUsedBankEntry = (email: string, bankEntryId: string) => {
    const emailKey = String(email || "").toLowerCase().trim();
    if (!usedBankEntryIdsByEmail.has(emailKey)) usedBankEntryIdsByEmail.set(emailKey, new Set<string>());
    usedBankEntryIdsByEmail.get(emailKey)!.add(bankEntryId);
  };
  const findRecentExactBankEntries = (
    email: string,
    purchaseDate: Date,
    targetAmount: number,
  ): { id: string; date: string; income: number; account: string }[] => {
    const normalizedEmail = String(email || "").toLowerCase().trim();
    const usedIds = usedBankEntryIdsByEmail.get(normalizedEmail) || new Set<string>();
    const candidates = (bankEntriesByEmail.get(normalizedEmail) || [])
      .filter((candidate) => !usedIds.has(candidate.id) && candidate.parsedDate.getTime() <= purchaseDate.getTime());
    if (candidates.length === 0 || targetAmount <= 0) return [];

    const recentCandidates = candidates.slice(-8);
    let bestMatch: { gap: number; itemCount: number; recencyScore: number; items: typeof recentCandidates } | null = null;
    const subsetCount = 1 << recentCandidates.length;
    for (let mask = 1; mask < subsetCount; mask++) {
      const selected = recentCandidates.filter((_, index) => (mask & (1 << index)) !== 0);
      const total = roundTo2(selected.reduce((sum, item) => sum + safeNumber(item.income), 0));
      const gap = Math.abs(total - roundTo2(targetAmount));
      if (gap > 1) continue;
      const recencyScore = selected.reduce((sum, item) => sum + recentCandidates.indexOf(item), 0);
      const candidateMatch = {
        gap,
        itemCount: selected.length,
        recencyScore,
        items: selected,
      };
      if (
        !bestMatch ||
        candidateMatch.gap < bestMatch.gap - 0.0001 ||
        (Math.abs(candidateMatch.gap - bestMatch.gap) <= 0.0001 && candidateMatch.itemCount < bestMatch.itemCount) ||
        (
          Math.abs(candidateMatch.gap - bestMatch.gap) <= 0.0001 &&
          candidateMatch.itemCount === bestMatch.itemCount &&
          candidateMatch.recencyScore > bestMatch.recencyScore
        )
      ) {
        bestMatch = candidateMatch;
      }
    }

    if (!bestMatch) return [];
    return bestMatch.items
      .slice()
      .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime())
      .map((item) => ({
        id: item.id,
        date: item.date,
        income: item.income,
        account: item.account,
      }));
  };
  const findLatestExactSingleBankEntry = (
    email: string,
    purchaseDate: Date,
    targetAmount: number,
  ): { id: string; date: string; income: number; account: string }[] => {
    const normalizedEmail = String(email || "").toLowerCase().trim();
    const usedIds = usedBankEntryIdsByEmail.get(normalizedEmail) || new Set<string>();
    const target = roundTo2(targetAmount);
    if (target <= 0) return [];

    const matched = (bankEntriesByEmail.get(normalizedEmail) || [])
      .filter((candidate) =>
        !usedIds.has(candidate.id) &&
        candidate.parsedDate.getTime() <= purchaseDate.getTime() &&
        Math.abs(roundTo2(candidate.income) - target) <= 1,
      )
      .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime());

    if (matched.length === 0) return [];
    return [{
      id: matched[0].id,
      date: matched[0].date,
      income: matched[0].income,
      account: matched[0].account,
    }];
  };
  const findLatestDirectTxCodeExactBankEntry = (
    txCode: string,
    email: string,
    purchaseDate: Date,
    targetAmount: number,
  ): { id: string; date: string; income: number; account: string }[] => {
    const at1Match = String(txCode || "").trim().toUpperCase().match(/AT1-[A-Z0-9]{4}-[A-Z0-9]{4}/i);
    if (!at1Match) return [];
    const at1Code = at1Match[0].toUpperCase();
    const usedIds = usedBankEntryIdsByEmail.get(String(email || "").toLowerCase().trim()) || new Set<string>();
    const target = roundTo2(targetAmount);
    if (target <= 0) return [];

    const matched = allBankStatementEntries
      .flatMap((candidate, index) => {
        if (!candidate.txCode.toUpperCase().includes(at1Code)) return [];
        const parsedDate = parseBankDate(candidate.date);
        if (!parsedDate) return [];
        return [{
          id: `${at1Code}|${index}|${candidate.date}|${safeNumber(candidate.income)}|${candidate.account || ""}`,
          date: candidate.date,
          income: candidate.income,
          account: candidate.account || "",
          parsedDate,
        }];
      })
      .filter((candidate) =>
        !usedIds.has(candidate.id) &&
        candidate.parsedDate.getTime() <= purchaseDate.getTime() &&
        Math.abs(roundTo2(candidate.income) - target) <= 1,
      )
      .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime());

    if (matched.length === 0) return [];
    return [{
      id: matched[0].id,
      date: matched[0].date,
      income: matched[0].income,
      account: matched[0].account,
    }];
  };
  const findNearestPriorExactBankEntryIgnoringUsage = (
    txCode: string,
    purchaseDate: Date,
    targetAmount: number,
  ): { id: string; date: string; income: number; account: string }[] => {
    const at1Match = String(txCode || "").trim().toUpperCase().match(/AT1-[A-Z0-9]{4}-[A-Z0-9]{4}/i);
    if (!at1Match) return [];
    const at1Code = at1Match[0].toUpperCase();
    const target = roundTo2(targetAmount);
    if (target <= 0) return [];

    const matched = allBankStatementEntries
      .flatMap((candidate, index) => {
        if (!candidate.txCode.toUpperCase().includes(at1Code)) return [];
        const parsedDate = parseBankDate(candidate.date);
        if (!parsedDate) return [];
        return [{
          id: `${at1Code}|direct|${index}|${candidate.date}|${safeNumber(candidate.income)}|${candidate.account || ""}`,
          date: candidate.date,
          income: candidate.income,
          account: candidate.account || "",
          parsedDate,
        }];
      })
      .filter((candidate) =>
        candidate.parsedDate.getTime() <= purchaseDate.getTime() &&
        Math.abs(roundTo2(candidate.income) - target) <= 1,
      )
      .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime());

    if (matched.length === 0) return [];
    return [{
      id: matched[0].id,
      date: matched[0].date,
      income: matched[0].income,
      account: matched[0].account,
    }];
  };
  const findLatestPriorBankEntryByTxCode = (
    txCode: string,
    email: string,
    purchaseDate: Date,
  ): { id: string; date: string; income: number; account: string }[] => {
    const at1Match = String(txCode || "").trim().toUpperCase().match(/AT1-[A-Z0-9]{4}-[A-Z0-9]{4}/i);
    if (!at1Match) return [];
    const at1Code = at1Match[0].toUpperCase();
    const usedIds = usedBankEntryIdsByEmail.get(String(email || "").toLowerCase().trim()) || new Set<string>();

    const matched = allBankStatementEntries
      .flatMap((candidate, index) => {
        if (!candidate.txCode.toUpperCase().includes(at1Code)) return [];
        const parsedDate = parseBankDate(candidate.date);
        if (!parsedDate) return [];
        return [{
          id: `${at1Code}|${index}|${candidate.date}|${safeNumber(candidate.income)}|${candidate.account || ""}`,
          date: candidate.date,
          income: candidate.income,
          account: candidate.account || "",
          parsedDate,
        }];
      })
      .filter((candidate) =>
        !usedIds.has(candidate.id) &&
        candidate.parsedDate.getTime() <= purchaseDate.getTime()
      )
      .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime());

    if (matched.length === 0) return [];
    return [{
      id: matched[0].id,
      date: matched[0].date,
      income: matched[0].income,
      account: matched[0].account,
    }];
  };
  const directExactPriorBankLinesByRowKey = new Map<string, { id: string; date: string; income: number; account: string }[]>();
  filteredSheet5Entries.forEach((entry) => {
    const effectiveTxCode = getEffectiveSheet5TxCode(entry);
    if (safeNumber(entry.amount) <= 0) return;
    const directExactBankLines = findNearestPriorExactBankEntryIgnoringUsage(
      effectiveTxCode,
      entry.parsedDate,
      roundTo2(safeNumber(entry.amount)),
    );
    if (directExactBankLines.length > 0) {
      directExactPriorBankLinesByRowKey.set(entry.rowKey, directExactBankLines);
    }
  });
  if (sheet5.rowCount > 3) {
    sheet5.spliceRows(4, sheet5.rowCount - 3);
  }

  const orderedSheet5Entries = [...filteredSheet5Entries].sort((a, b) => {
    const aOrder = customerOrder.get(String(a.email || "").toLowerCase().trim()) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = customerOrder.get(String(b.email || "").toLowerCase().trim()) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    if (a.parsedDate.getTime() !== b.parsedDate.getTime()) return a.parsedDate.getTime() - b.parsedDate.getTime();
    return a.order - b.order;
  });
  const finalSheet5BankLinesByRowKey = new Map<string, { id: string; date: string; income: number; account: string }[]>();
  const usedFinalSheet5BankIds = new Set<string>();
  const getSheet5BankTargetAmounts = (baseAmount: number): number[] => {
    const normalized = roundTo2(baseAmount);
    if (normalized <= 0) return [];
    return Array.from(new Set([normalized, roundTo2(normalized + 5000)]));
  };
  orderedSheet5Entries.forEach((entry) => {
    const txCode = getEffectiveSheet5TxCode(entry);
    if (!txCode) return;

    const soldAmountForEntry = soldTargetRowKeyByDateKey[entry.dateKey] === entry.rowKey
      ? roundTo2(safeNumber(soldCarryByEmailDate[entry.dateKey] || 0))
      : 0;
    const targetBankAmount = roundTo2(Math.max(0, safeNumber(entry.amount) - soldAmountForEntry));
    const targetBankAmounts = getSheet5BankTargetAmounts(targetBankAmount);
    if (targetBankAmounts.length === 0) return;

    const candidates = findBankEntriesForUser(txCode)
      .filter((candidate) =>
        !usedFinalSheet5BankIds.has(candidate.id) &&
        candidate.parsedDate.getTime() <= entry.parsedDate.getTime(),
      );
    if (candidates.length === 0) return;

    const exactSingle = targetBankAmounts
      .flatMap((target) =>
        candidates
          .filter((candidate) => Math.abs(roundTo2(candidate.income) - target) <= 1)
          .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime())
          .slice(0, 1)
      )[0];

    if (exactSingle) {
      finalSheet5BankLinesByRowKey.set(entry.rowKey, [{
        id: exactSingle.id,
        date: exactSingle.date,
        income: exactSingle.income,
        account: exactSingle.account,
      }]);
      usedFinalSheet5BankIds.add(exactSingle.id);
      return;
    }

    let exactSubset: PreliminaryBankLine[] = [];
    for (const target of targetBankAmounts) {
      const candidateSubset = findBestRecentBankSubset(candidates as PreliminaryBankLine[], target);
      const candidateSubsetTotal = roundTo2(candidateSubset.reduce((sum, item) => sum + safeNumber(item.income), 0));
      if (candidateSubset.length > 0 && Math.abs(candidateSubsetTotal - target) <= 1) {
        exactSubset = candidateSubset;
        break;
      }
    }
    if (exactSubset.length === 0) return;

    finalSheet5BankLinesByRowKey.set(
      entry.rowKey,
      exactSubset.map((item) => ({
        id: item.id,
        date: item.date,
        income: item.income,
        account: item.account,
      })),
    );
    exactSubset.forEach((item) => usedFinalSheet5BankIds.add(item.id));
  });
  const strictSheet5BankLinesByRowKey = new Map<string, { id: string; date: string; income: number; account: string }[]>();
  const usedStrictSheet5BankIds = new Set<string>();
  orderedSheet5Entries.forEach((entry) => {
    const entryAmount = roundTo2(safeNumber(entry.amount));
    if (entryAmount <= 0) return;
    const targetBankAmounts = getSheet5BankTargetAmounts(entryAmount);
    const txCode = getEffectiveSheet5TxCode(entry);
    if (!txCode) return;

    const candidates = findBankEntriesForUser(txCode)
      .filter((candidate) =>
        !usedStrictSheet5BankIds.has(candidate.id) &&
        candidate.parsedDate.getTime() <= entry.parsedDate.getTime(),
      );
    if (candidates.length === 0) return;

    const exactSingle = targetBankAmounts
      .flatMap((target) =>
        candidates
          .filter((candidate) => Math.abs(roundTo2(candidate.income) - target) <= 1)
          .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime())
          .slice(0, 1)
      )[0];

    if (exactSingle) {
      strictSheet5BankLinesByRowKey.set(entry.rowKey, [{
        id: exactSingle.id,
        date: exactSingle.date,
        income: exactSingle.income,
        account: exactSingle.account,
      }]);
      usedStrictSheet5BankIds.add(exactSingle.id);
      return;
    }

    let matchedSubset: PreliminaryBankLine[] = [];
    for (const target of targetBankAmounts) {
      const candidateSubset = findBestRecentBankSubset(candidates as PreliminaryBankLine[], target);
      const matchedTotal = roundTo2(candidateSubset.reduce((sum, item) => sum + safeNumber(item.income), 0));
      if (candidateSubset.length > 0 && Math.abs(matchedTotal - target) <= 1) {
        matchedSubset = candidateSubset;
        break;
      }
    }
    if (matchedSubset.length === 0) return;

    strictSheet5BankLinesByRowKey.set(
      entry.rowKey,
      matchedSubset.map((item) => ({
        id: item.id,
        date: item.date,
        income: item.income,
        account: item.account,
      })),
    );
    matchedSubset.forEach((item) => usedStrictSheet5BankIds.add(item.id));
  });
  const deterministicBankLinesByRowKey = new Map<string, { id: string; date: string; income: number; account: string }[]>();
  const usedDeterministicBankIds = new Set<string>();
  orderedSheet5Entries.forEach((entry) => {
    if (safeNumber(entry.amount) <= 0) return;
    const txCode = getEffectiveSheet5TxCode(entry);
    if (!txCode) return;
    const txCodeBankEntries = findBankEntriesForUser(txCode)
      .filter((candidate) =>
        !usedDeterministicBankIds.has(candidate.id) &&
        candidate.parsedDate.getTime() <= entry.parsedDate.getTime(),
      );
    const matched = findBestRecentBankSubset(txCodeBankEntries as PreliminaryBankLine[], roundTo2(safeNumber(entry.amount)));
    const matchedTotal = sumBankLines(matched);
    if (matched.length === 0 || Math.abs(matchedTotal - roundTo2(safeNumber(entry.amount))) > 1) return;
    deterministicBankLinesByRowKey.set(
      entry.rowKey,
      matched.map((item) => ({
        id: item.id,
        date: item.date,
        income: item.income,
        account: item.account,
      })),
    );
    matched.forEach((item) => usedDeterministicBankIds.add(item.id));
  });

  orderedSheet5Entries.forEach((entry) => {
    if ((bankSourcesByRowKey.get(entry.rowKey) || []).length > 0) return;
    if (safeNumber(entry.amount) <= 0) return;
    const soldAmount = soldTargetRowKeyByDateKey[entry.dateKey] === entry.rowKey
      ? safeNumber(soldCarryByEmailDate[entry.dateKey] || 0)
      : 0;
    const targetBankAmount = roundTo2(safeNumber(entry.amount));
    const targetBankAmounts = getSheet5BankTargetAmounts(targetBankAmount);
    if (targetBankAmounts.length === 0) return;
    let recoveredBankLines: { id: string; date: string; income: number; account: string }[] = [];
    for (const target of targetBankAmounts) {
      recoveredBankLines = findRecentExactBankEntries(entry.email || "", entry.parsedDate, target);
      if (recoveredBankLines.length > 0) break;
    }
    if (recoveredBankLines.length === 0) return;
    bankSourcesByRowKey.set(entry.rowKey, recoveredBankLines);
  });

  orderedSheet5Entries.forEach((entry) => {
    const email = String(entry.email || "").toLowerCase().trim();
    const customer = customerMap[email];
    const effectiveTxCode = getEffectiveSheet5TxCode(entry);
    const dateKey = entry.dateKey;
    const soldAmount = soldTargetRowKeyByDateKey[dateKey] === entry.rowKey ? safeNumber(soldCarryByEmailDate[dateKey] || 0) : 0;
    const cashAmount = cashTargetRowKeyByDateKey[dateKey] === entry.rowKey ? safeNumber(cashPurchaseByEmailDate[dateKey] || 0) : 0;
    const chargeAmount = chargeTargetRowKeyByDateKey[dateKey] === entry.rowKey ? safeNumber(chargeByEmailDate[dateKey] || 0) : 0;
    const carryComment = carryTargetRowKeyByDateKey[dateKey] === entry.rowKey ? String(carryForwardCommentByEmailDate[dateKey] || "") : "";
    const carrySourceLabel = carryTargetRowKeyByDateKey[dateKey] === entry.rowKey ? String(carryForwardSourceLabelByEmailDate[dateKey] || "") : "";
    const carrySourceAmount = carryTargetRowKeyByDateKey[dateKey] === entry.rowKey ? roundTo2(safeNumber(carryForwardSourceAmountByEmailDate[dateKey] || 0)) : 0;
    const distributionSuppressionComments = distributionSuppressionCommentByRowKey.get(entry.rowKey) || [];
    const distributionCappedComments = Array.from(
      new Set([
        ...(distributionCappedCommentByRowKey.get(entry.rowKey) || []),
        ...(directDistributionCappedCommentByRowKey.get(entry.rowKey) || []),
      ])
    );
    const targetBankAmount = roundTo2(Math.max(0, safeNumber(entry.amount) - soldAmount));
    const targetBankAmounts = getSheet5BankTargetAmounts(targetBankAmount);
    const finalMatchedBankLines = finalSheet5BankLinesByRowKey.get(entry.rowKey) || [];
    const preliminaryBankLines = preliminaryBankSourcesByRowKey.get(entry.rowKey) || [];
    const strictBankLines = strictSheet5BankLinesByRowKey.get(entry.rowKey) || [];
    const deterministicBankLines = deterministicBankLinesByRowKey.get(entry.rowKey) || [];
    const directExactBankLines = directExactPriorBankLinesByRowKey.get(entry.rowKey) || [];
    const candidateBankLineGroups = [
      finalMatchedBankLines,
      strictBankLines,
      bankSourcesByRowKey.get(entry.rowKey) || [],
      deterministicBankLines,
      preliminaryBankLines,
      directExactBankLines,
    ].map((lines) => lines.filter((line) => !(usedBankEntryIdsByEmail.get(email) || new Set<string>()).has(line.id)));
    let bankLines = targetBankAmount > 0
      ? (candidateBankLineGroups.find((lines) =>
          lines.length > 0 && targetBankAmounts.some((target) => Math.abs(sumBankLines(lines) - target) <= 1),
        ) || [])
      : [];

    if (bankLines.length === 0 && safeNumber(entry.amount) > 0) {
      if (targetBankAmounts.length > 0) {
        let recoveredBankLines = findRecentExactBankEntries(email, entry.parsedDate, targetBankAmount);
        for (const target of targetBankAmounts) {
          recoveredBankLines = findRecentExactBankEntries(email, entry.parsedDate, target);
          if (recoveredBankLines.length > 0) break;
          recoveredBankLines = findLatestExactSingleBankEntry(email, entry.parsedDate, target);
          if (recoveredBankLines.length > 0) break;
          recoveredBankLines = findLatestDirectTxCodeExactBankEntry(
            effectiveTxCode,
            email,
            entry.parsedDate,
            target,
          );
          if (recoveredBankLines.length > 0) break;
          recoveredBankLines = findNearestPriorExactBankEntryIgnoringUsage(
            effectiveTxCode,
            entry.parsedDate,
            target,
          );
          if (recoveredBankLines.length > 0) break;
        }
        if (recoveredBankLines.length > 0) {
          recoveredBankLines = recoveredBankLines.filter(
            (bankLine) => !(usedBankEntryIdsByEmail.get(email) || new Set<string>()).has(bankLine.id)
          );
        }
        if (recoveredBankLines.length > 0) {
          bankSourcesByRowKey.set(entry.rowKey, recoveredBankLines);
          recoveredBankLines.forEach((bankLine) => markUsedBankEntry(email, bankLine.id));
          bankLines = recoveredBankLines;
        }
      }
    }

    if (bankLines.length === 0 && targetBankAmounts.length > 0) {
      const at1Match = String(effectiveTxCode || "").trim().toUpperCase().match(/AT1-[A-Z0-9]{4}-[A-Z0-9]{4}/i);
      if (at1Match) {
        const at1Code = at1Match[0].toUpperCase();
        const directFallback = allBankStatementEntries
          .flatMap((candidate, index) => {
            if (!candidate.txCode.toUpperCase().includes(at1Code)) return [];
            const parsedDate = parseBankDate(candidate.date);
            if (!parsedDate) return [];
            return [{
              id: `${at1Code}|ultimate|${index}|${candidate.date}|${safeNumber(candidate.income)}|${candidate.account || ""}`,
              date: candidate.date,
              income: candidate.income,
              account: candidate.account || "",
              parsedDate,
            }];
          })
          .filter((candidate) =>
            candidate.parsedDate.getTime() <= entry.parsedDate.getTime() &&
            targetBankAmounts.some((target) => Math.abs(roundTo2(candidate.income) - target) <= 1),
          )
          .sort((a, b) => b.parsedDate.getTime() - a.parsedDate.getTime());
        if (directFallback.length > 0) {
          const chosenBankLine = {
            id: directFallback[0].id,
            date: directFallback[0].date,
            income: directFallback[0].income,
            account: directFallback[0].account,
          };
          if (!(usedBankEntryIdsByEmail.get(email) || new Set<string>()).has(chosenBankLine.id)) {
            bankLines = [chosenBankLine];
          }
        }
      }
    }

    if (bankLines.length === 0 && targetBankAmount > 0) {
      const at1Match = String(effectiveTxCode || "")
        .trim()
        .toUpperCase()
        .match(/AT1-[A-Z0-9]{4}-[A-Z0-9]{4}/i);
      if (at1Match) {
        const latestPriorByTxCode = findLatestPriorBankEntryByTxCode(
          effectiveTxCode,
          email,
          entry.parsedDate,
        );
        if (latestPriorByTxCode.length > 0) {
          bankLines = latestPriorByTxCode.filter(
            (bankLine) => !(usedBankEntryIdsByEmail.get(email) || new Set<string>()).has(bankLine.id)
          );
          bankLines.forEach((bankLine) => markUsedBankEntry(email, bankLine.id));
        }
      }
    }

    const sourceLines: string[] = [];
    const sourceAmountLines: string[] = [];
    const sourceAccountLines: string[] = [];
    bankLines.forEach((bankLine) => {
      sourceLines.push(bankLine.date);
      sourceAmountLines.push(formatMoneyLine(bankLine.income));
      sourceAccountLines.push(bankLine.account || "");
    });
    if (bankLines.length > 0) {
      bankLines.forEach((bankLine) => markUsedBankEntry(email, bankLine.id));
    }

    if (soldAmount > 0) {
      sourceLines.push("Хувьцаа зарсан мөнгө");
      sourceAmountLines.push(formatMoneyLine(soldAmount));
      sourceAccountLines.push("");
    }
    if (cashAmount > 0) {
      sourceLines.push("Бэлэн мөнгөний үлдэгдэл");
      sourceAmountLines.push("");
      sourceAccountLines.push("");
    }
    if (sourceLines.length === 0 && carrySourceLabel) {
      sourceLines.push("Бондын дахин худалдан авалт");
      sourceAmountLines.push(carrySourceAmount > 0 ? formatMoneyLine(carrySourceAmount) : "");
      sourceAccountLines.push("");
    }

    if (sourceLines.length === 0) {
      if (safeNumber(entry.amount) <= 0 && safeNumber(entry.bond) > 0) {
        sourceLines.push("Бондын дахин худалдан авалт");
        sourceAmountLines.push("");
        sourceAccountLines.push("");
      } else if (safeNumber(entry.amount) > 0) {
        sourceLines.push("Хувьцааны үлдэгдэл");
        sourceAmountLines.push("");
        sourceAccountLines.push("");
      }
    }

    if (
      sourceLines.length === 1 &&
      sourceLines[0] === "Бондын дахин худалдан авалт"
    ) {
      sourceAmountLines[0] = "";
      sourceAccountLines[0] = "";
    }

    const commentParts: string[] = [];
    const totalBankAmount = bankLines.reduce((sum, bankLine) => sum + safeNumber(bankLine.income), 0);
    const totalVisibleSourceAmount = roundTo2(
      totalBankAmount + soldAmount
    );
    const hasVisibleSourceAmountInColumnB =
      sourceAmountLines.some((line) => String(line || "").trim() !== "");
    const bankAmountDifference = roundTo2(totalVisibleSourceAmount - safeNumber(entry.amount));
    const feeCommentTolerance = 1;
    const shouldAddFeeComment =
      hasVisibleSourceAmountInColumnB &&
      safeNumber(entry.amount) > 0;
    const hasFixedFeeDifference =
      shouldAddFeeComment &&
      Math.abs(bankAmountDifference - 5000) <= feeCommentTolerance;
    if (hasFixedFeeDifference) {
      commentParts.push("5000 шимтгэл");
    } else if (
      shouldAddFeeComment &&
      Math.abs(bankAmountDifference) > feeCommentTolerance
    ) {
      commentParts.push("Автамата шимтгэл");
    }
    if (distributionCappedComments.length > 0) {
      distributionCappedComments.forEach((comment) => {
        if (comment) commentParts.push(comment);
      });
    }
    if (carryComment && distributionCappedComments.length === 0) {
      commentParts.push(carryComment);
    }
    distributionSuppressionComments.forEach((comment) => {
      if (comment) commentParts.push(comment);
    });
    const ratio = safeNumber(entry.amount) > 0 ? safeNumber(entry.bond) / safeNumber(entry.amount) : "";
    const defaultTag = buildSheet5Tag(safeNumber(entry.bond), safeNumber(entry.amount));
    const hasForcedSheet5Comments = distributionSuppressionComments.length > 0 || distributionCappedComments.length > 0;
    const sanitizedCommentParts = shouldAddFeeComment
      ? commentParts
      : commentParts.filter(
          (part) => part !== "5000 шимтгэл" && part !== "Автамата шимтгэл"
        );
    const hasExplicitComment = sanitizedCommentParts.length > 0;
    const finalComment = hasForcedSheet5Comments
      ? combineSheet5Comments(sanitizedCommentParts)
      : isSheet5CommentlessRatio(ratio) && !hasExplicitComment
        ? ""
        : (sanitizedCommentParts.length > 0 ? combineSheet5Comments(sanitizedCommentParts) : defaultTag);

    const displayBondAmount =
      carryTargetRowKeyByDateKey[dateKey] === entry.rowKey &&
      carrySourceAmount > 0 &&
      safeNumber(entry.amount) <= 0
        ? carrySourceAmount
        : safeNumber(entry.bond);

    const rowValues = [
      sourceLines.join("\n"),
      sourceAmountLines.join("\n"),
      sourceAccountLines.some(Boolean) ? sourceAccountLines.join("\n") : "",
      entry.haDate || "",
      entry.lastName || customer?.lastName || "",
      entry.firstName || customer?.firstName || "",
      customer?.regNumber || "",
      customer?.phone || "",
      entry.email || "",
      effectiveTxCode,
      ceilTo2Decimals(safeNumber(entry.amount)),
      Number(entry.portfolioIndex) || "",
      ratio === "" ? "" : ratio,
      ceilTo2Decimals(displayBondAmount),
      ceilTo2Decimals(safeNumber(entry.holdAmount)),
      ...stockSymbols.map((symbol) => {
        const allocation = safeNumber(entry.stockAllocations?.[symbol] || 0);
        return allocation > 0 ? allocation : 0;
      }),
      finalComment,
    ];
    const addedRow = sheet5.addRow(rowValues);
    addBorderToRow(addedRow);
    [1, 2, 3].forEach((col) => {
      addedRow.getCell(col).alignment = { wrapText: true, vertical: "top" };
    });
  });
  const unresolvedSheet5Rows = orderedSheet5Entries
    .filter((entry) => safeNumber(entry.amount) > 0)
    .filter((entry) => {
      const soldAmount = soldTargetRowKeyByDateKey[entry.dateKey] === entry.rowKey ? safeNumber(soldCarryByEmailDate[entry.dateKey] || 0) : 0;
      const targetBankAmount = roundTo2(Math.max(0, safeNumber(entry.amount) - soldAmount));
      const targetBankAmounts = getSheet5BankTargetAmounts(targetBankAmount);
      if (targetBankAmount <= 0) return false;
      const finalMatchedBankLines = finalSheet5BankLinesByRowKey.get(entry.rowKey) || [];
      const preliminaryBankLines = preliminaryBankSourcesByRowKey.get(entry.rowKey) || [];
      const strictBankLines = strictSheet5BankLinesByRowKey.get(entry.rowKey) || [];
      const deterministicBankLines = deterministicBankLinesByRowKey.get(entry.rowKey) || [];
      const directExactBankLines = directExactPriorBankLinesByRowKey.get(entry.rowKey) || [];
      const bankLines = finalMatchedBankLines.length > 0
        ? finalMatchedBankLines
        : strictBankLines.length > 0
          ? strictBankLines
          : (bankSourcesByRowKey.get(entry.rowKey) || []).length > 0
            ? (bankSourcesByRowKey.get(entry.rowKey) || [])
            : deterministicBankLines.length > 0
              ? deterministicBankLines
              : preliminaryBankLines.length > 0
              ? preliminaryBankLines
                : directExactBankLines;
      return bankLines.length === 0 ||
        !targetBankAmounts.some((target) => Math.abs(sumBankLines(bankLines) - target) <= 1);
    })
    .map((entry) => ({
      email: entry.email,
      purchaseDate: entry.haDate,
      txCode: entry.txCode,
      amount: roundTo2(safeNumber(entry.amount)),
    }));
  if (unresolvedSheet5Rows.length > 0) {
    console.warn("Sheet 5 unresolved bank rows:", unresolvedSheet5Rows.slice(0, 20), `(${unresolvedSheet5Rows.length} total)`);
  }

  // Sheet 8: Cash Balance (Бэлэн мөнгөний үлдэгдэл)
  const sheet8 = workbook.addWorksheet("8. Бэлэн мөнгөний үлдэгдэл");
  // Set column widths for Sheet 8 (20 columns)
  setColumnWidths(sheet8, [15, 18, 14, 28, 18, 18, 8, 18, 10, 35, 15, 12, 18, 16, 12, 18, 12, 12, 18, 5]);

  // Title row
  const cbTitle = sheet8.addRow(["Хавсралт 7", "", "", "", "БЭЛЭН МӨНГӨНИЙ ҮЛДЭГДЛИЙН ЗАДАРГАА"]);
  cbTitle.font = { bold: true, size: 16 };

  // Two empty rows after title (expected spacing)
  sheet8.addRow([]);
  sheet8.addRow([]);

  const addCashHeaderRow = () => {
    const cbHeader = sheet8.addRow([
      "Овог", "Нэр", "Регистр", "Харицагчийн мэйл хаяг", "Худалдан авалтын дүн",
      "Огноо", "Багц", "Хөрөнгө оруулсан дүн", "Symbol", "Description",
      "Дүн", "Тоо ширхэг", "Худалдаж авсан дүн", "Ширхэгийн үнэ", "Шимтгэл",
      "Бэлэн мөнгө үлдэгдэл", "Хувьцаа ханш", "USD ханш", "Төлбөрийн даалгавар огноо", ""
    ]);
    styleHeaderRow(cbHeader);
  };

  addCashHeaderRow();

  // Group Sheet 8 by purchase event, not by customer.
  // One customer can have multiple zero-hold purchases and each purchase must
  // render as its own block with its own total row.
  const cashByPurchase = new Map<string, CashBalanceRow[]>();
  cashBalanceRows.forEach((c) => {
    const key = [
      (c.email || "").toLowerCase().trim(),
      c.purchaseDate || "",
      c.portfolioIndex || "",
      safeNumber(c.investedAmount),
    ].join("|");
    if (!cashByPurchase.has(key)) cashByPurchase.set(key, []);
    cashByPurchase.get(key)!.push(c);
  });

  Array.from(cashByPurchase.values()).forEach((purchaseRows, purchaseIndex) => {
    purchaseRows.forEach(c => {
      const cashDataRow = sheet8.addRow([
        c.lastName || "",
        c.firstName || "",
        c.regNumber || "",
        c.email || "",
        c.purchaseType || "",
        c.purchaseDate || "",
        c.portfolioIndex || "",
        safeNumber(c.investedAmount),
        c.symbol || "",
        c.description || "",
        safeNumber(c.allocatedAmount),
        c.quantity ? c.quantity : "",
        c.purchasedAmountMNT ? safeNumber(c.purchasedAmountMNT) : "",
        c.unitPriceMNT ? safeNumber(c.unitPriceMNT) : "",
        c.fee ? safeNumber(c.fee) : "",
        c.cashBalance ? safeNumber(c.cashBalance) : "",
        c.stockPrice || "",
        c.usdRate || "",
        c.paymentDate || "",
        ""
      ]);
      addBorderToRow(cashDataRow);
      if (c.custodyNeedsReview) {
        [17, 18, 19].forEach((col) => {
          cashDataRow.getCell(col).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFC7CE" },
          };
        });
      }
    });

    const totalCashBalance = purchaseRows.reduce((sum, r) => sum + safeNumber(r.cashBalance), 0);

    if (totalCashBalance > 0) {
      const totalRow = sheet8.addRow([
        "", "", "", "", "", "Нийт", "", "", "", "", "", "", "", "", "",
        safeNumber(totalCashBalance), "", "", "", ""
      ]);
      totalRow.font = { bold: true };
      addBorderToRow(totalRow);
    }

    if (purchaseIndex < cashByPurchase.size - 1) {
      sheet8.addRow([]);
    }
  });

  const sheet9 = workbook.addWorksheet("9.Тархалт шалгах");
  setColumnWidths(sheet9, [28, 18, 18, 16, 18, 16, 20, 55]);
  const distTitle = sheet9.addRow(["Хавсралт 8", "", "", "", "ТАРХАЛТ ШАЛГАХ ЖАГСААЛТ"]);
  distTitle.font = { bold: true, size: 12 };
  sheet9.addRow([]);
  const distHeader = sheet9.addRow([
    "И-мэйл",
    "Овог",
    "Нэр",
    "Регистр",
    "Бонд",
    "Худалдан авсан огноо",
    "Тархалтын огноо",
    "Тайлбар",
  ]);
  styleHeaderRow(distHeader);
  suppressedSheet6Rows
    .slice()
    .sort((a, b) => {
      const aOrder = customerOrder.get(normalizeEmailKey(a.email)) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = customerOrder.get(normalizeEmailKey(b.email)) ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return normalizeDateKey(a.purchaseDate).localeCompare(normalizeDateKey(b.purchaseDate));
    })
    .forEach((row) => {
      const addedRow = sheet9.addRow([
        row.email,
        row.lastName,
        row.firstName,
        row.regNumber,
        row.bondName,
        formatDistributionDate(row.purchaseDate),
        formatDistributionDate(row.interestEndDate),
        row.workerComment,
      ]);
      addBorderToRow(addedRow);
    });

  // Former charge output sheet is intentionally disabled.
  if (false) {
  // Sheet 9: SGC Цэнэглэлт (Charging/Top-up)
  const sheet9 = workbook.addWorksheet("9.Цэнэглэлт");
  // Set column widths for Sheet 9 (12 columns)
  setColumnWidths(sheet9, [8, 15, 18, 14, 18, 28, 8, 18, 25, 18, 18, 18]);

  // Title row
  const chTitle = sheet9.addRow(["Хавсралт 8", "", "", "", "SGC ЦЭНЭГЛЭЛТ"]);
  chTitle.font = { bold: true, size: 12 };

  // Empty row
  sheet9.addRow([]);

  // Header row (12 columns)
  const chHeader = sheet9.addRow([
    "№", "Овог", "Нэр", "Регистр", "Утас", "И-мейл", "Багц №", "Мөнгөн дүн", "Тайлбар", "Огноо", "ХА огноо", "Нийлбэр"
  ]);
  styleHeaderRow(chHeader);

  // Helper function to convert date string to Excel serial number
  const dateToExcelSerial = (dateStr: string): number => {
    // Parse date string (format: "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD")
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return 0;

    // Excel serial date: days since 1899-12-30
    const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
    const millisPerDay = 24 * 60 * 60 * 1000;
    const serial = (date.getTime() - excelEpoch.getTime()) / millisPerDay;
    return serial;
  };

  // Get all charge rows for customers in the refund list
  // Filter by register number to match the 22 people
  const selectedRegNumbers = new Set(
    Object.values(customerMap).map(c => c.regNumber.trim().toUpperCase())
  );

  // Collect all charge rows from all sheets
  const allChargeRows: ChargeRow[] = [];
  accumulatedChargeSheets.forEach(sheet => {
    console.log(`  Sheet "${sheet.name}": ${sheet.data.length} rows`);
    allChargeRows.push(...sheet.data);
  });

  // Filter to only show rows for customers in the refund list
  const filteredChargeRows = allChargeRows.filter(charge =>
    selectedRegNumbers.has(charge.regNumber.trim().toUpperCase())
  );

  // Debug: Show sample of filtered rows
  console.log(`Sheet 8: Sample filtered rows:`, filteredChargeRows.slice(0, 3).map(c => ({
    index: c.index,
    regNumber: c.regNumber,
    amount: c.amount,
    date: c.haDate
  })));

  console.log(`Sheet 8: ${filteredChargeRows.length} charge rows for ${selectedRegNumbers.size} customers`);
  console.log(`Sheet 8: Total sheets in uploaded file: ${accumulatedChargeSheets.length}`);
  console.log(`Sheet 8: All charge rows before filtering: ${allChargeRows.length}`);

  // Debug: Show counts per person BEFORE calculating totals
  const countsPerPerson: Record<string, number> = {};
  filteredChargeRows.forEach(charge => {
    const regNumber = charge.regNumber.trim().toUpperCase();
    countsPerPerson[regNumber] = (countsPerPerson[regNumber] || 0) + 1;
  });
  console.log(`Sheet 8: Rows per person:`, Object.entries(countsPerPerson).slice(0, 5).map(([reg, count]) => `${reg}: ${count}`));

  // Calculate total amount per person (by register number)
  const totalAmountPerPerson: Record<string, number> = {};
  filteredChargeRows.forEach(charge => {
    const regNumber = charge.regNumber.trim().toUpperCase();
    if (!totalAmountPerPerson[regNumber]) {
      totalAmountPerPerson[regNumber] = 0;
    }
    totalAmountPerPerson[regNumber] += charge.amount || 0;
  });

  console.log(`Sheet 8: Total amounts calculated for ${Object.keys(totalAmountPerPerson).length} unique persons`);
  // Debug: Show first few totals
  Object.entries(totalAmountPerPerson).slice(0, 3).forEach(([reg, total]) => {
    console.log(`  ${reg}: total=${total.toFixed(2)}, rows=${countsPerPerson[reg]}`);
  });

  // Add charge rows
  filteredChargeRows.forEach(charge => {
    const regNumber = charge.regNumber.trim().toUpperCase();
    const totalAmount = totalAmountPerPerson[regNumber] || 0;

    // Convert haDate to Excel serial number for Огноо column
    // haDate format might be "YYYY-MM-DD HH:mm:ss" or just date
    const excelSerial = charge.haDate ? dateToExcelSerial(charge.haDate) : "";

    // ХА огноо: Format as YYYY/MM/DD
    let haDateFormatted = "";
    if (charge.haDate) {
      const dateObj = new Date(charge.haDate);
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        haDateFormatted = `${year}/${month}/${day}`;
      }
    }

    const chargeRow = sheet9.addRow([
      charge.index || "", // № - original index from file
      charge.lastName || "",
      charge.firstName || "",
      charge.regNumber || "",
      charge.phone || "",
      charge.email || "",
      charge.portfolioIndex !== undefined && charge.portfolioIndex !== null ? charge.portfolioIndex : "", // Багц № from uploaded file (column G)
      charge.amount !== undefined && charge.amount !== null ? safeNumber(charge.amount) : "", // Мөнгөн дүн from uploaded file (column H)
      charge.description || "", // Тайлбар from uploaded file (column I)
      excelSerial || "", // Огноо - Excel serial number (J)
      haDateFormatted, // ХА огноо - YYYY/MM/DD format (K)
      totalAmount > 0 ? safeNumber(totalAmount) : "" // Нийлбэр - sum of all amounts for this person (L)
    ]);
    addBorderToRow(chargeRow);
  });
  }

    // Download the Excel file
    console.log("Writing Excel buffer...");
    const buffer = await workbook.xlsx.writeBuffer();
    console.log("Buffer size:", buffer.byteLength);

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    console.log("Blob created, size:", blob.size);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Буцаалт_${refundDate}.xlsx`;
    a.style.display = "none";
    document.body.appendChild(a);
    console.log("Triggering download:", a.download);
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      a.remove();
    }, 1000);
    console.log("Download triggered successfully");
  } catch (error) {
    console.error("Error in generate8SheetExcel:", error);
    if (error instanceof Error) {
      console.error("Error details:", error.message, error.stack);
    }
    throw error;
  }
};
